import React from 'react';

import autoBind from 'react-autobind';

// import other components within the same project here

import Localization from "../Localization.jsx"

import {
  Modal,
  Box,
  Flex,
  Group,
  Stack,
  ScrollArea,
  NavLink,
  Text,
  Button,
  CloseButton,
  ActionIcon,
  Tooltip,
  TextInput,
  Textarea,
  Select,
  Switch,
  Table,
  Loader,
  Divider,
  SimpleGrid,
  Paper,
  FileButton,
  Badge,
  Checkbox,
  Alert,
  SegmentedControl,
  Pill,
  UnstyledButton,
  Popover,
} from '@mantine/core';
import {
  IconX,
  IconRefresh,
  IconEdit,
  IconLock,
  IconTrash,
  IconMap,
  IconShieldCheck,
  IconRestore,
  IconCheck,
  IconHourglass,
  IconUserPlus,
  IconUsers,
  IconSchool,
  IconBuildings,
  IconLicense,
  IconSearch,
  IconPlus,
  IconDownload,
  IconReceipt,
  IconUpload,
  IconUsersPlus,
  IconArrowLeft,
  IconChevronLeft,
  IconAlertTriangle,
} from '@tabler/icons-react';
import { BRAND_PRIMARY } from "../theme/brandTheme.js";
import { SortOrderToggle, DEFAULT_SORT_ORDER } from "./SortOrderToggle.jsx";
import { TableLoadingCard } from "./TableLoadingRow.jsx";

const PLAN_EXTRA_LOCALE = 'PLAN_EXTRA_LOCALE';
const LOADING_CARD_ICON_PROPS = { size: 40, stroke: 1.5, color: 'var(--mantine-color-gray-6)' };
const DEFAULT_SUBSCRIPTION_DURATION_MONTHS = '12';
const ADMIN_ACTIVE_TAB_STORAGE_KEY = 'ev_admin_active_tab';
const ADMIN_TAB_COUNT = 5;

function createSubscriptionLocalesDialogState(overrides = {}) {
  return {
    open: false,
    subscription: null,
    rows: [],
    loading: false,
    searchMode: "keyword",
    searchText: "",
    searchSourceResults: [],
    selectedSearchSource: null,
    searchResults: [],
    selectedLocaleIds: [],
    selectedAssignedLocaleIds: [],
    searchLoading: false,
    assigning: false,
    removing: false,
    ...overrides,
  };
}

function createSubscriptionCreateDialogState(overrides = {}) {
  return {
    open: false,
    name: "",
    name_en: "",
    remarks: "",
    is_template: false,
    loading: false,
    templates: [],
    templatesLoading: false,
    copyExtraLocales: false,
    sourcePrivileges: [],
    sourcePrivilegesLoading: false,
    returnToSchoolSubscription: false,
    ...overrides,
  };
}

function loadAdminActiveTab() {
  try {
    const stored = window.localStorage.getItem(ADMIN_ACTIVE_TAB_STORAGE_KEY);
    if (stored != null) {
      const index = parseInt(stored, 10);
      if (Number.isInteger(index) && index >= 0 && index < ADMIN_TAB_COUNT) {
        return index;
      }
    }
  } catch (e) {
    // localStorage throws in private mode / when storage is disabled.
  }
  return 0;
}

function saveAdminActiveTab(index) {
  try {
    window.localStorage.setItem(ADMIN_ACTIVE_TAB_STORAGE_KEY, String(index));
  } catch (e) {
    // localStorage throws in private mode / when storage is disabled.
  }
}

function isSubscriptionActive(dateStart, dateExpire) {
  const start = String(dateStart || '').slice(0, 10);
  const expire = String(dateExpire || '').slice(0, 10);
  if (!start || !expire) return false;

  const now = new Date();
  const today = [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
  ].join('-');

  return start <= today && today <= expire;
}

function getTodayDateString() {
  const now = new Date();
  return [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
  ].join('-');
}

function getSubscriptionEndDate(dateStart, durationMonths) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStart || '');
  const months = Number(durationMonths);
  if (!match || !Number.isInteger(months) || months <= 0) return '';

  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  const day = Number(match[3]);
  const targetMonthIndex = monthIndex + months;
  const targetYear = year + Math.floor(targetMonthIndex / 12);
  const targetMonth = targetMonthIndex % 12;
  const daysInTargetMonth = new Date(Date.UTC(targetYear, targetMonth + 1, 0)).getUTCDate();
  const boundary = new Date(Date.UTC(targetYear, targetMonth, Math.min(day, daysInTargetMonth)));

  // The subscription ends the day before the matching date in the target month.
  boundary.setUTCDate(boundary.getUTCDate() - 1);
  return [
    boundary.getUTCFullYear(),
    String(boundary.getUTCMonth() + 1).padStart(2, '0'),
    String(boundary.getUTCDate()).padStart(2, '0'),
  ].join('-');
}

class SubscriptionPicker extends React.Component {
  constructor(props) {
    super(props);
    const selected = props.selected || null;
    this.state = {
      inputValue: selected ? (selected.name || selected.name_en || "") : "",
      suggestions: [],
      selected,
      loading: false,
    };
    this.handleChange = this.handleChange.bind(this);
    this.handleSelect = this.handleSelect.bind(this);
    this.fetchSuggestions = this.fetchSuggestions.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
  }

  handleChange(e) {
    const val = e.target.value;
    this.setState({ inputValue: val, selected: null });
    if (val && val.trim().length > 0) {
      this.fetchSuggestions(val.trim());
    } else {
      this.setState({ suggestions: [] });
    }
  }

  handleSelect(s) {
    this.setState({
      selected: this.props.searchOnly ? null : s,
      inputValue: this.props.searchOnly ? "" : (s.name || s.name_en || ""),
      suggestions: [],
    });
    if (this.props.onSelect) {
      this.props.onSelect(s);
    }
  }

  handleBlur() {
    if (this.props.searchOnly) {
      this.setState({ suggestions: [] });
      return;
    }
    if (!this.state.selected) {
      this.setState({ inputValue: "", suggestions: [] });
      if (this.props.onSelect) {
        this.props.onSelect(null);
      }
    }
  }

  fetchSuggestions(keyword) {
    if (!this.props.root || !this.props.root.refs || !this.props.root.refs.API) {
      return;
    }
    this.setState({ loading: true });
    this.props.root.refs.API.request(
      "subscription_get",
      { keyword, limit: 10, sort_by: "date_create", sort_dir: DEFAULT_SORT_ORDER },
      (res) => {
        const list = Array.isArray(res?.body?.result) ? res.body.result : [];
        this.setState({ suggestions: list, loading: false });
      },
      () => {
        this.setState({ suggestions: [], loading: false });
      }
    );
  }

  render() {
    const { placeholder, disabled } = this.props;
    return (
      <div style={{ position: 'relative' }}>
        <TextInput
          value={this.state.inputValue}
          onChange={this.handleChange}
          onBlur={this.handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          leftSection={this.props.searchOnly ? <IconSearch size={16} /> : undefined}
          rightSection={this.state.loading ? <Loader size={16} /> : undefined}
        />
        {this.state.suggestions.length > 0 && (
          <div style={{
            position: 'absolute',
            top: 'calc(100% + 2px)',
            left: 0,
            right: 0,
            background: '#fff',
            border: '1px solid #ccc',
            borderRadius: '4px',
            maxHeight: '200px',
            overflowY: 'auto',
            zIndex: 10
          }}>
            {this.state.suggestions.map((s, idx) => (
              <div
                key={idx}
                onMouseDown={() => this.handleSelect(s)}
                style={{ padding: '8px 10px', cursor: 'pointer' }}
              >
                <div>{s.name || '-'}</div>
                <div style={{ fontSize: '12px', color: '#666' }}>{s.name_en || ''}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }
}

class AdminTaskDialog extends React.Component {
  constructor(props) {
    super(props);
    this.refs = React.createRef();

    // state variables here
    this.state = {
      // aNumber : 1,
      // aString: "abc",
      // anArray: [],
      // anObject: {},
      open: false,
      activeTab: loadAdminActiveTab(),
      searchText: "",
      totalPage: 0,
      currentPage: 0,
      schoolList: [],
      schoolSearchResults: [],
      schoolSearchText: "",
      schoolFilters: {
        school_type: "",
        order_by: "id",
        order: DEFAULT_SORT_ORDER,
        limit: 100,
      },
      schoolSubscriptionFilter: null,
      hasSchoolSearched: false,
      schoolLoading: false,
      schoolNavigationDirection: null,
      newUser: {
        username: "",
        displayName: "",
        email: "",
        password0: "",
        accessRight: 0,
        schoolID: "",
      },
      importedSchoolInfo: {
        id: "",
        name: "",
        name_eng: "",
        isImported: false,
      },
      newSchool: {
        name: "",
        name_eng: "",
        key: "",
        locale: "",
        school_type: 0,
        school_level: 0,
      },
      tableRows: [],
      subscriptionList: [],
      subscriptionSearchText: "",
      subscriptionSortBy: "date_create",
      subscriptionSortDir: DEFAULT_SORT_ORDER,
      subscriptionLimit: 100,
      hasSubscriptionSearched: false,
      subscriptionLoading: false,
      subscriptionEditingCell: null,
      subscriptionUpdatingFields: {},
      subscriptionNavigationDirection: null,
      subscriptionLocalesDialog: createSubscriptionLocalesDialogState(),
      subscriptionPrivilegeDialog: {
        open: false,
        subscription: null,
        rows: [],
        loading: false,
      },
      subscriptionCreateDialog: createSubscriptionCreateDialogState(),
      subscriptionDeleteDialog: {
        open: false,
        subscription: null,
        loading: false,
        adminPassword: "",
        assignedSchools: [],
        blocked: false,
      },
      subscriptionCopyPrivilegeFrom: null,
      schoolSubscriptionDialog: {
        open: false,
        school: null,
        rows: [],
        loading: false,
        addSelected: null,
        addLoading: false,
        addDateStart: getTodayDateString(),
        addDurationMonths: DEFAULT_SUBSCRIPTION_DURATION_MONTHS,
        pickerKey: 0,
        editingRow: null,
        editingField: null,
        updateLoading: false,
        deleteLoading: false,
        deleteConfirmRow: null,
      },
      // Password change dialog state
      passwordChangeDialog: {
        open: false,
        targetUserId: 0,
        targetUsername: "",
        adminPassword: "",
        newPassword: "",
        confirmPassword: "",
        isLoading: false,
      },
      // Delete user dialog state
      deleteUserDialog: {
        open: false,
        targetUserId: 0,
        targetUsername: "",
        adminPassword: "",
        isLoading: false,
      },
      // Edit school dialog state
      editSchoolDialog: {
        open: false,
        targetSchoolId: 0,
        name: "",
        nameEng: "",
        key: "",
        locale: "",
        schoolType: 0,
        schoolLevel: 0,
        adminPassword: "",
        isLoading: false,
      },
      // Edit user dialog state
      editUserDialog: {
        open: false,
        targetUserId: 0,
        targetUsername: "",
        displayName: "",
        email: "",
        role: 0,
        adminPassword: "",
        isLoading: false,
      },
      // Batch create user (CSV) dialog state
      batchCreateDialog: this.getInitialBatchCreateDialogState(),
    };
    this.subscriptionLocaleSearchTimer = null;
    this.subscriptionLocaleSearchRequestId = 0;
    autoBind(this);
    
    // Only load school list if API ref is ready
    if (this.props.root.refs && this.props.root.refs.API) {
      this.props.root.refs.API.request("get_school_list", {}, this.getSchoolListOK, this.getSchoolListFailed);
    }

  }

  componentWillUnmount() {
    if (this.subscriptionLocaleSearchTimer) {
      clearTimeout(this.subscriptionLocaleSearchTimer);
    }
    this.subscriptionLocaleSearchRequestId += 1;
  }

  getTasks() {
    return [
      Localization.strings.CREATE_USER,
      Localization.strings.USER_LIST,
      Localization.strings.CREATE_SCHOOL,
      Localization.strings.SCHOOL_LIST,
      Localization.strings.SUBSCRIPTION_TAB,
    ];
  }

  loadSchoolList() {
    // Load school list if API ref is ready
    if (this.props.root.refs && this.props.root.refs.API) {
      this.props.root.refs.API.request("get_school_list", {}, this.getSchoolListOK, this.getSchoolListFailed);
    }
  }

  getSchoolListOK(res) {
    let tmpList = [];
    let schoolList = Array.isArray(res?.body?.result) ? res.body.result : [];
    for (let i = 0; i < schoolList.length; i++) {
      let schoolRow = schoolList[i];
      tmpList.push({
        school_id: schoolRow['id'],
        shcool_name: schoolRow['name'],
        school_eng_name: schoolRow['name_eng'],
        school_locale: schoolRow['locale'],
        school_key: schoolRow['key'],
        school_type: schoolRow['school_type']
      });
    }
    this.setState({
      schoolList: tmpList,
    });
    this.state.schoolList = tmpList;
  }

  getSchoolListFailed(err) {
    console.log("get school list failed");
  }

  changeTab(index) {
    let newIndex = this.state.activeTab;
    if (newIndex == index) {
      return;
    }
    newIndex = index;
    this.setState({
      activeTab: newIndex,
      searchText: "",
      tableRows: [],
    });
    this.state.activeTab = newIndex;
    this.state.searchText = "";
    this.state.tableRows = [];
    saveAdminActiveTab(newIndex);
    this.ensureActiveTabData();
  }

  ensureActiveTabData() {
    const tab = this.state.activeTab;
    // Auto-search for school list when entering that tab for the first time
    if (tab === 3 && !this.state.hasSchoolSearched) {
      setTimeout(() => {
        this.handleSchoolSearchClicked();
      }, 100);
    }

    // Auto-search subscriptions on first enter
    if (tab === 4 && !this.state.hasSubscriptionSearched) {
      setTimeout(() => {
        this.handleSubscriptionSearch();
      }, 100);
    }
  }

  openDialog() {
    this.setState({ open: true });
    this.state.open = true;
    this.ensureActiveTabData();
  }

  handleTabChange = (event, index) => {
    this.changeTab(index);
  }

  close() {
    this.setState({ open: false });
    this.state.open = false;
  }

  handleClose() {
    if (this.subscriptionLocaleSearchTimer) {
      clearTimeout(this.subscriptionLocaleSearchTimer);
      this.subscriptionLocaleSearchTimer = null;
    }
    this.subscriptionLocaleSearchRequestId += 1;
    // Reset form data, but keep the last side-menu tab for next open.
    this.setState({ 
      searchText: "",
      schoolSearchText: "",
      tableRows: [],
      schoolSubscriptionFilter: null,
      subscriptionList: [],
      subscriptionSearchText: "",
      subscriptionSortBy: "date_create",
      subscriptionSortDir: DEFAULT_SORT_ORDER,
      subscriptionLimit: 100,
      hasSubscriptionSearched: false,
      subscriptionLoading: false,
      subscriptionEditingCell: null,
      subscriptionUpdatingFields: {},
      subscriptionNavigationDirection: null,
      subscriptionLocalesDialog: createSubscriptionLocalesDialogState(),
      subscriptionPrivilegeDialog: {
        open: false,
        subscription: null,
        rows: [],
        loading: false,
      },
      subscriptionCreateDialog: createSubscriptionCreateDialogState(),
      subscriptionCopyPrivilegeFrom: null,
      schoolSubscriptionFilter: null,
      schoolSubscriptionDialog: {
        open: false,
        school: null,
        rows: [],
        loading: false,
        addSelected: null,
        addLoading: false,
        addDateStart: getTodayDateString(),
        addDurationMonths: DEFAULT_SUBSCRIPTION_DURATION_MONTHS,
        pickerKey: 0,
        editingRow: null,
        editingField: null,
        updateLoading: false,
        deleteLoading: false,
        deleteConfirmRow: null,
      },
      schoolSearchResults: [],
      hasSchoolSearched: false,
      schoolLoading: false,
      schoolNavigationDirection: null,
      newUser: {
        username: "",
        displayName: "",
        email: "",
        password0: "",
        accessRight: 0,
        schoolID: "",
      },
      importedSchoolInfo: {
        id: "",
        name: "",
        name_eng: "",
        isImported: false,
      },
      newSchool: {
        name: "",
        name_eng: "",
        key: "",
        locale: "",
        school_type: 0,
        school_level: 0,
      },
      schoolFilters: {
        school_type: "",
        order_by: "id",
        order: DEFAULT_SORT_ORDER,
        limit: 100,
      },
      passwordChangeDialog: {
        open: false,
        targetUserId: 0,
        targetUsername: "",
        adminPassword: "",
        newPassword: "",
        confirmPassword: "",
        isLoading: false,
      },
      subscriptionDeleteDialog: {
        open: false,
        subscription: null,
        loading: false,
        adminPassword: "",
        assignedSchools: [],
        blocked: false,
      },
      batchCreateDialog: this.getInitialBatchCreateDialogState(),
    });

    // Update state variables directly as well
    this.state.searchText = "";
    this.state.schoolSearchText = "";
    this.state.tableRows = [];
    this.state.schoolSubscriptionFilter = null;
    this.state.subscriptionList = [];
    this.state.subscriptionSearchText = "";
    this.state.subscriptionSortBy = "date_create";
    this.state.subscriptionSortDir = DEFAULT_SORT_ORDER;
    this.state.subscriptionLimit = 100;
    this.state.hasSubscriptionSearched = false;
    this.state.subscriptionLoading = false;
    this.state.subscriptionEditingCell = null;
    this.state.subscriptionUpdatingFields = {};
    this.state.subscriptionNavigationDirection = null;
    this.state.subscriptionLocalesDialog = createSubscriptionLocalesDialogState();
    this.state.subscriptionPrivilegeDialog = {
      open: false,
      subscription: null,
      rows: [],
      loading: false,
    };
    this.state.subscriptionCreateDialog = createSubscriptionCreateDialogState();
    this.state.subscriptionCopyPrivilegeFrom = null;
    this.state.schoolSubscriptionDialog = {
      open: false,
      school: null,
      rows: [],
      loading: false,
      addSelected: null,
      addLoading: false,
      addDateStart: getTodayDateString(),
      addDurationMonths: DEFAULT_SUBSCRIPTION_DURATION_MONTHS,
      pickerKey: 0,
      editingRow: null,
      editingField: null,
      updateLoading: false,
      deleteLoading: false,
      deleteConfirmRow: null,
    };
    this.state.schoolSearchResults = [];
    this.state.hasSchoolSearched = false;
    this.state.schoolLoading = false;
    this.state.schoolNavigationDirection = null;
    this.state.newUser = {
      username: "",
      displayName: "",
      email: "",
      password0: "",
      accessRight: 0,
      schoolID: "",
    };
    this.state.importedSchoolInfo = {
      id: "",
      name: "",
      name_eng: "",
      isImported: false,
    };
    this.state.newSchool = {
      name: "",
      name_eng: "",
      key: "",
      locale: "",
      school_type: 0,
      school_level: 0,
    };
    this.state.schoolFilters = {
      school_type: "",
      school_level: "",
      order_by: "id",
      order: DEFAULT_SORT_ORDER,
      limit: 100,
    };
    this.state.passwordChangeDialog = {
      open: false,
      targetUserId: 0,
      targetUsername: "",
      adminPassword: "",
      newPassword: "",
      confirmPassword: "",
      isLoading: false,
    };
    this.state.subscriptionDeleteDialog = {
      open: false,
      subscription: null,
      loading: false,
      adminPassword: "",
      assignedSchools: [],
      blocked: false,
    };
    this.state.editUserDialog = {
      open: false,
      targetUserId: 0,
      targetUsername: "",
      displayName: "",
      email: "",
      role: 0,
      adminPassword: "",
      isLoading: false,
    };
    this.state.editSchoolDialog = {
      open: false,
      targetSchoolId: 0,
      name: "",
      nameEng: "",
      key: "",
      locale: "",
      schoolType: 0,
      schoolLevel: 0,
      adminPassword: "",
      isLoading: false,
    };
    this.state.batchCreateDialog = this.getInitialBatchCreateDialogState();

    this.close();
  }

  handleChange = name => event => {
    let user = this.state.newUser;
    user[name] = event.target.value;
    this.setState({ newUser: user });
    this.state.newUser = user;
    
    // If user manually edits schoolID, clear imported school info
    if (name === 'schoolID' && this.state.importedSchoolInfo.isImported) {
      this.setState({
        importedSchoolInfo: {
          id: "",
          name: "",
          name_eng: "",
          isImported: false,
        }
      });
      this.state.importedSchoolInfo = {
        id: "",
        name: "",
        name_eng: "",
        isImported: false,
      };
    }
  }

  handleSchoolChange = name => event => {
    let school = this.state.newSchool;
    school[name] = event.target.value;
    this.setState({ newSchool: school });
    this.state.newSchool = school;
  }

  createUserClicked() {
    let user = this.state.newUser;
    
    // Validation with proper error messages
    if (!user.username || user.username.trim() === "") {
      this.props.root.addNotification("請輸入用戶名稱", false);
      return;
    }
    
    if (!user.password0 || user.password0.trim() === "") {
      this.props.root.addNotification("請輸入密碼", false);
      return;
    }
    
    if (user.password0.length < 6) {
      this.props.root.addNotification("密碼長度至少需要6個字符", false);
      return;
    }
    
    if (!user.schoolID || user.schoolID.trim() === "") {
      this.props.root.addNotification("請輸入學校編號", false);
      return;
    }

    let requestObj = {
      username: user.username.trim(),
      display_name: user.displayName.trim(),
      email: user.email.trim(),
      role: user.accessRight,
      school_id: user.schoolID.trim(),
      new_password: user.password0,
    }
    const submitCreateUser = () => {
      this.props.root.refs.API.request("user_admin_create_user", requestObj, this.createUserOK, this.createUserFailed);
    };

    if (Number(user.accessRight) >= 3) {
      const title = Localization.strings.PLATFORM_ADMIN_ROLE || 'CLST Administrator';
      const warning = Localization.strings.PLATFORM_ADMIN_CREATE_WARNING
        || 'This account can manage data across all schools. Create it only for authorized platform staff.';
      const confirmDialog = this.props.root?.refs?.confirmDialog;
      if (confirmDialog) {
        confirmDialog.show(
          { title, content: warning },
          submitCreateUser,
          () => {}
        );
      } else if (window.confirm(warning)) {
        submitCreateUser();
      }
      return;
    }

    submitCreateUser();
  }

  createUserOK(res) {
    console.log("create user ok");
    this.props.root.addNotification(Localization.strings.CREATED, true);
    
    // Reset imported school info after successful user creation
    this.setState({
      importedSchoolInfo: {
        id: "",
        name: "",
        name_eng: "",
        isImported: false,
      }
    });
    this.state.importedSchoolInfo = {
      id: "",
      name: "",
      name_eng: "",
      isImported: false,
    };
  }

  createUserFailed(err) {
    console.log("create user failed", err);
    
    let errorMessage = "建立用戶失敗";
    
    // Try to extract specific error message from the response
    if (err && err.body) {
      if (err.body.remarks) {
        errorMessage += ": " + err.body.remarks;
      } else if (err.body.result === "-1") {
        errorMessage += ": 請檢查輸入資料是否正確";
      }
    } else if (err && err.remarks) {
      errorMessage += ": " + err.remarks;
    } else if (err && typeof err === 'string') {
      errorMessage += ": " + err;
    } else {
      errorMessage += ": 請檢查網路連線或聯絡系統管理員";
    }
    
    this.props.root.addNotification(errorMessage, false);
  }

  createSchoolClicked() {
    let school = this.state.newSchool;
    
    // Validation: Check required fields
    if (
      school.name.trim() === "" ||
      school.name_eng.trim() === "" ||
      school.key.trim() === ""
    ) {
      this.props.root.addNotification("Please fill in all required fields (Name, English Name, and Key)", false);
      return;
    }

    // Validate key format (should be alphanumeric and underscores only)
    const keyPattern = /^[a-zA-Z0-9_]+$/;
    if (!keyPattern.test(school.key)) {
      this.props.root.addNotification("School Key should contain only letters, numbers, and underscores", false);
      return;
    }

    let requestObj = {
      name: school.name.trim(),
      name_eng: school.name_eng.trim(),
      key: school.key.trim(),
      locale: school.locale.trim(),
      school_type: parseInt(school.school_type) || 0,
      school_level: parseInt(school.school_level) || 0,
    };

    this.props.root.refs.API.request("create_school", requestObj, this.createSchoolOK, this.createSchoolFailed);
  }

  createSchoolOK(res) {
    console.log("create school ok");
    this.props.root.addNotification("School created successfully", true);
    
    // Reset form
    this.setState({
      newSchool: {
        name: "",
        name_eng: "",
        key: "",
        locale: "",
        school_type: 0,
        school_level: 0,
      }
    });
    this.state.newSchool = {
      name: "",
      name_eng: "",
      key: "",
      locale: "",
      school_type: 0,
      school_level: 0,
    };
  }

  createSchoolFailed(err) {
    console.log("create school failed " + err);
    this.props.root.addNotification("Failed to create school: " + err, false);
  }

  // Import latest school methods
  handleImportLatestSchool = () => {
    // Call API to get the latest school (limit 1, order by id DESC)
    let requestObj = {
      order_by: "id",
      order: DEFAULT_SORT_ORDER,
      limit: 1
    };

    this.props.root.refs.API.request("get_filtered_school_list", requestObj, this.importLatestSchoolOK, this.importLatestSchoolFailed);
  }

  importLatestSchoolOK = (res) => {
    console.log("import latest school ok", res);
    let schoolList = res.body['result'];
    
    if (Array.isArray(schoolList) && schoolList.length > 0) {
      let latestSchool = schoolList[0];
      
      // Update the schoolID field
      let user = { ...this.state.newUser };
      user.schoolID = latestSchool.id.toString();
      
      // Update imported school info
      let importedInfo = {
        id: latestSchool.id.toString(),
        name: latestSchool.name || "",
        name_eng: latestSchool.name_eng || "",
        isImported: true,
      };
      
      this.setState({ 
        newUser: user,
        importedSchoolInfo: importedInfo 
      });
      this.state.newUser = user;
      this.state.importedSchoolInfo = importedInfo;
      
      this.props.root.addNotification(`已導入最新學校 ID: ${latestSchool.id}`, true);
    } else {
      this.props.root.addNotification("沒有找到學校資料", false);
    }
  }

  importLatestSchoolFailed = (err) => {
    console.log("import latest school failed", err);
    this.props.root.addNotification("獲取最新學校失敗", false);
  }

  // ===== Batch create user (CSV) =====

  getInitialBatchCreateDialogState() {
    return {
      open: false,
      step: 'upload', // 'upload' | 'confirm' | 'result'
      fileName: "",
      users: [],       // parsed data rows sent to the server
      lines: [],       // original CSV line number of each row (for display)
      reviewRows: [],  // per-row validation results from the dry-run
      resultRows: [],  // per-row results from the real insert
      validCount: 0,
      invalidCount: 0,
      insertedCount: 0,
      adminPassword: "",
      isLoading: false,
      fileInputKey: 0,
    };
  }

  setBatchCreateDialog(patch) {
    const dialog = { ...this.state.batchCreateDialog, ...patch };
    this.setState({ batchCreateDialog: dialog });
    this.state.batchCreateDialog = dialog;
  }

  handleBatchCreateOpen = () => {
    this.setBatchCreateDialog({ ...this.getInitialBatchCreateDialogState(), open: true });
  }

  handleBatchCreateClose = () => {
    if (this.state.batchCreateDialog.isLoading) {
      return;
    }
    this.setBatchCreateDialog(this.getInitialBatchCreateDialogState());
  }

  handleBatchCreateBackToUpload = () => {
    this.setBatchCreateDialog({
      ...this.getInitialBatchCreateDialogState(),
      open: true,
      fileInputKey: this.state.batchCreateDialog.fileInputKey + 1,
    });
  }

  handleBatchTemplateDownload = () => {
    // UTF-8 BOM so Excel opens the file correctly
    const content = "\uFEFF" + "username,password,school_id,display_name,email,role\r\n";
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'batch_create_user_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // Excel with some regional settings exports semicolon- or tab-separated files
  sniffCSVDelimiter(text) {
    const firstLine = text.split(/\r\n|\r|\n/, 1)[0] || "";
    let best = ',';
    let bestCount = 0;
    [',', ';', '\t'].forEach((d) => {
      const count = firstLine.split(d).length - 1;
      if (count > bestCount) {
        best = d;
        bestCount = count;
      }
    });
    return best;
  }

  // Minimal CSV parser: quoted fields, escaped quotes ("") and CR/LF/CRLF line ends
  parseCSVText(text, delimiter) {
    const rows = [];
    let row = [];
    let field = "";
    let inQuotes = false;
    for (let i = 0; i < text.length; i++) {
      const c = text[i];
      if (inQuotes) {
        if (c === '"') {
          if (text[i + 1] === '"') {
            field += '"';
            i++;
          } else {
            inQuotes = false;
          }
        } else {
          field += c;
        }
      } else if (c === '"') {
        inQuotes = true;
      } else if (c === delimiter) {
        row.push(field);
        field = "";
      } else if (c === '\n' || c === '\r') {
        row.push(field);
        rows.push(row);
        row = [];
        field = "";
        if (c === '\r' && text[i + 1] === '\n') {
          i++;
        }
      } else {
        field += c;
      }
    }
    if (field !== "" || row.length > 0) {
      row.push(field);
      rows.push(row);
    }
    return rows;
  }

  normalizeBatchHeaderName(name) {
    const key = String(name || "").replace(/^\uFEFF/, "").trim().toLowerCase().replace(/[\s-]+/g, '_');
    const aliases = {
      username: 'username', user_name: 'username', account: 'username',
      password: 'password', pwd: 'password', pass: 'password',
      school_id: 'school_id', schoolid: 'school_id', school: 'school_id', school_key: 'school_id',
      display_name: 'display_name', displayname: 'display_name', display: 'display_name', nickname: 'display_name',
      email: 'email', e_mail: 'email', mail: 'email',
      role: 'role', access_right: 'role', permission: 'role',
    };
    return aliases[key] || null;
  }

  handleBatchFileSelected = (file) => {
    if (!file) {
      return;
    }
    // bump the FileButton key so picking the same file again re-triggers onChange
    this.setBatchCreateDialog({ fileInputKey: this.state.batchCreateDialog.fileInputKey + 1 });
    if (file.size > 2 * 1024 * 1024) {
      this.props.root.addNotification("CSV 檔案過大 (上限 2MB)", false);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      this.processBatchCSVText(String(reader.result || ""), file.name);
    };
    reader.onerror = () => {
      this.props.root.addNotification("讀取檔案失敗", false);
    };
    reader.readAsText(file, 'utf-8');
  }

  processBatchCSVText = (rawText, fileName) => {
    const text = rawText.replace(/^\uFEFF/, "");
    if (text.trim() === "") {
      this.props.root.addNotification("CSV 檔案是空的", false);
      return;
    }
    const parsed = this.parseCSVText(text, this.sniffCSVDelimiter(text));
    // keep original line numbers, then drop fully empty rows
    const entries = parsed
      .map((cells, i) => ({ cells, line: i + 1 }))
      .filter((e) => e.cells.some((c) => String(c).trim() !== ""));
    if (entries.length < 2) {
      this.props.root.addNotification("CSV 沒有資料行 (第一行必須是欄位標題，第二行開始才是用戶資料)", false);
      return;
    }
    const columnMap = {};
    entries[0].cells.forEach((h, idx) => {
      const canonical = this.normalizeBatchHeaderName(h);
      if (canonical && columnMap[canonical] === undefined) {
        columnMap[canonical] = idx;
      }
    });
    const missing = ['username', 'password', 'school_id'].filter((h) => columnMap[h] === undefined);
    if (missing.length > 0) {
      this.props.root.addNotification("CSV 缺少必要欄位: " + missing.join(", "), false);
      return;
    }
    const dataEntries = entries.slice(1);
    if (dataEntries.length > 500) {
      this.props.root.addNotification("一次最多只能建立 500 名用戶，請分批上傳", false);
      return;
    }
    const pick = (cells, key) => {
      const idx = columnMap[key];
      return (idx === undefined || idx >= cells.length) ? "" : String(cells[idx]).trim();
    };
    const users = [];
    const lines = [];
    dataEntries.forEach((e) => {
      users.push({
        username: pick(e.cells, 'username'),
        password: pick(e.cells, 'password'),
        school_id: pick(e.cells, 'school_id'),
        display_name: pick(e.cells, 'display_name'),
        email: pick(e.cells, 'email'),
        role: pick(e.cells, 'role'),
      });
      lines.push(e.line);
    });

    this.setBatchCreateDialog({ fileName, users, lines, isLoading: true });
    // dry-run: server validates every row (duplicates, existing usernames, school lookup)
    this.props.root.refs.API.request(
      "user_admin_batch_create_users",
      { users: users, validate_only: true },
      this.batchValidateOK,
      this.batchValidateFailed
    );
  }

  batchValidateOK = (res) => {
    const result = res && res.body ? res.body.result : null;
    if (!result || !Array.isArray(result.rows)) {
      this.props.root.addNotification("驗證失敗: 伺服器回應格式錯誤", false);
      this.setBatchCreateDialog({ isLoading: false });
      return;
    }
    const lines = this.state.batchCreateDialog.lines;
    this.setBatchCreateDialog({
      step: 'confirm',
      reviewRows: result.rows.map((r) => ({ ...r, line: lines[r.index] })),
      validCount: result.valid_count || 0,
      invalidCount: result.invalid_count || 0,
      isLoading: false,
    });
  }

  batchValidateFailed = (err) => {
    let msg = "CSV 驗證失敗";
    if (err && err.body && err.body.remarks) {
      msg += ": " + err.body.remarks;
    }
    this.props.root.addNotification(msg, false);
    this.setBatchCreateDialog({ isLoading: false });
  }

  handleBatchAdminPasswordChange = (event) => {
    this.setBatchCreateDialog({ adminPassword: event.target.value });
  }

  handleBatchConfirmSubmit = () => {
    const dialog = this.state.batchCreateDialog;
    if (dialog.validCount <= 0) {
      this.props.root.addNotification("沒有可建立的用戶", false);
      return;
    }
    if (!dialog.adminPassword) {
      this.props.root.addNotification("請輸入管理員密碼以確認", false);
      return;
    }
    let adminId = 0;
    try {
      adminId = this.props.root.getMyUserId();
    } catch (e) {
      console.log("Could not get admin ID:", e);
    }
    if (!adminId || adminId <= 0) {
      this.props.root.addNotification("無法獲取管理員身份，請重新登入", false);
      return;
    }
    this.setBatchCreateDialog({ isLoading: true });
    this.props.root.refs.API.request(
      "user_admin_batch_create_users",
      {
        users: dialog.users,
        validate_only: false,
        admin_id: adminId,
        admin_password: dialog.adminPassword,
      },
      this.batchInsertOK,
      this.batchInsertFailed
    );
  }

  batchInsertOK = (res) => {
    const result = res && res.body ? res.body.result : null;
    if (!result || !Array.isArray(result.rows)) {
      this.props.root.addNotification("建立失敗: 伺服器回應格式錯誤", false);
      this.setBatchCreateDialog({ isLoading: false });
      return;
    }
    const lines = this.state.batchCreateDialog.lines;
    const insertedCount = result.inserted_count || 0;
    this.setBatchCreateDialog({
      step: 'result',
      resultRows: result.rows.map((r) => ({ ...r, line: lines[r.index] })),
      insertedCount: insertedCount,
      invalidCount: result.invalid_count || 0,
      isLoading: false,
      adminPassword: "",
    });
    this.props.root.addNotification(`成功建立 ${insertedCount} 名用戶`, insertedCount > 0);
  }

  batchInsertFailed = (err) => {
    let msg = "批量建立用戶失敗";
    if (err && err.body && err.body.remarks) {
      msg += ": " + err.body.remarks;
    }
    this.props.root.addNotification(msg, false);
    this.setBatchCreateDialog({ isLoading: false });
  }

  getBatchReasonText(reason) {
    const map = {
      invalid_row: '資料格式錯誤',
      missing_username: '缺少用戶名稱',
      username_too_long: '用戶名稱過長 (最多 100 字元)',
      reserved_username: '用戶名稱不可以 "Deleted-" 開頭',
      missing_password: '缺少密碼',
      password_too_short: '密碼長度至少需要6個字符',
      missing_school: '缺少學校編號',
      invalid_role: '權限值無效 (只接受 0, 1, 2)',
      invalid_email: '電郵格式錯誤',
      display_name_too_long: '顯示名稱過長 (最多 100 字元)',
      duplicate_in_file: 'CSV 檔案內用戶名稱重複',
      school_not_found: '找不到學校 (可輸入學校編號或 School Key)',
      username_exists: '用戶名稱已存在',
      insert_failed: '寫入資料庫失敗',
    };
    return map[reason] || reason || '';
  }

  createUser() {
    const imported = this.state.importedSchoolInfo;
    const currentUserRole = Number(
      this.props.root?.refs?.userDataProfile?.state?.userInfo?.role
    );
    const canCreatePlatformAdmin = currentUserRole >= 3;
    const roleOptions = [
      { value: '0', label: '一般用戶' },
      { value: '1', label: '進階用戶' },
      { value: '2', label: '管理員' },
      ...(canCreatePlatformAdmin
        ? [{ value: '3', label: Localization.strings.PLATFORM_ADMIN_ROLE || 'CLST Administrator' }]
        : []),
    ];
    return (
      <Stack gap="lg" maw={900}>
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" verticalSpacing="md">
          <TextInput
            label="用戶名稱"
            value={this.state.newUser.username}
            onChange={this.handleChange('username')}
          />
          <TextInput
            label="顯示名稱 (optional)"
            value={this.state.newUser.displayName}
            onChange={this.handleChange('displayName')}
          />
          <TextInput
            label="密碼"
            type="password"
            value={this.state.newUser.password0}
            onChange={this.handleChange('password0')}
          />
          <TextInput
            label="Email (optional)"
            value={this.state.newUser.email}
            onChange={this.handleChange('email')}
          />
          <Box>
            <TextInput
              label="學校編號"
              value={this.state.newUser.schoolID}
              onChange={this.handleChange('schoolID')}
            />
            <Button
              variant="light"
              size="xs"
              mt={8}
              leftSection={<IconDownload size={14} />}
              onClick={this.handleImportLatestSchool}
            >
              導入最近建立學校ID
            </Button>
            {imported.isImported && (
              <Box
                mt={8}
                style={{
                  fontSize: 12,
                  color: '#666',
                  padding: '6px 10px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: 6,
                  border: '1px solid #e0e0e0',
                }}
              >
                <strong>學校資訊:</strong> {imported.name}
                {imported.name_eng && imported.name_eng !== imported.name && (
                  <span style={{ color: '#999', marginLeft: 8 }}>({imported.name_eng})</span>
                )}
              </Box>
            )}
          </Box>
          <Select
            label="權限"
            allowDeselect={false}
            value={String(this.state.newUser.accessRight)}
            onChange={(value) => this.handleChange('accessRight')({ target: { value: Number(value) } })}
            data={roleOptions}
          />
        </SimpleGrid>
        {Number(this.state.newUser.accessRight) >= 3 && (
          <Alert
            color="orange"
            variant="light"
            title={Localization.strings.PLATFORM_ADMIN_ROLE || 'CLST Administrator'}
            icon={<IconAlertTriangle size={20} />}
          >
            {Localization.strings.PLATFORM_ADMIN_CREATE_WARNING
              || 'This account can manage data across all schools. Create it only for authorized platform staff.'}
          </Alert>
        )}
        <Group>
          <Button onClick={this.createUserClicked} leftSection={<IconUserPlus size={16} />}>
            {Localization.strings.CREATE}
          </Button>
          <Button variant="light" onClick={this.handleBatchCreateOpen} leftSection={<IconUsersPlus size={16} />}>
            批量建立用戶 (CSV)
          </Button>
        </Group>
      </Stack>
    );
  }

  createSchool() {
    const resetForm = () => {
      const blank = { name: "", name_eng: "", key: "", locale: "", school_type: 0, school_level: 0 };
      this.setState({ newSchool: blank });
      this.state.newSchool = blank;
    };
    return (
      <Stack gap="lg" maw={900}>
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" verticalSpacing="md">
          <TextInput
            label="學校名稱 (中文)"
            required
            value={this.state.newSchool.name}
            onChange={this.handleSchoolChange('name')}
          />
          <TextInput
            label="學校名稱 (英文)"
            required
            value={this.state.newSchool.name_eng}
            onChange={this.handleSchoolChange('name_eng')}
          />
          <TextInput
            label="School Key"
            required
            placeholder="AB1234"
            description="Unique identifier (letters, numbers, underscores only)"
            value={this.state.newSchool.key}
            onChange={this.handleSchoolChange('key')}
          />
          <TextInput
            label="地區 (Optional)"
            placeholder="e.g.: hk, cn, tw, mo, us"
            value={this.state.newSchool.locale}
            onChange={this.handleSchoolChange('locale')}
          />
          <Select
            label="學校類型 (Optional)"
            allowDeselect={false}
            value={String(this.state.newSchool.school_type)}
            onChange={(value) => this.handleSchoolChange('school_type')({ target: { value: Number(value) } })}
            data={[
              { value: '0', label: 'Unclassified' },
              { value: '1', label: 'Primary School' },
              { value: '2', label: 'Secondary School' },
              { value: '3', label: 'University/Post-secondary College/Government Institution' },
              { value: '4', label: 'Special School' },
              { value: '5', label: 'Management Organization' },
              { value: '6', label: 'Commercial Organization' },
            ]}
          />
          <Select
            label="學校級別 (Optional)"
            allowDeselect={false}
            value={String(this.state.newSchool.school_level)}
            onChange={(value) => this.handleSchoolChange('school_level')({ target: { value: Number(value) } })}
            data={[
              { value: '0', label: 'Not Specified' },
              { value: '1', label: 'Level 1' },
              { value: '2', label: 'Level 2' },
              { value: '3', label: 'Level 3' },
              { value: '4', label: 'Level 4' },
              { value: '5', label: 'Level 5' },
            ]}
          />
        </SimpleGrid>
        <Group justify="center">
          <Button variant="default" onClick={resetForm}>
            {Localization.strings.CLEAR}
          </Button>
          <Button onClick={this.createSchoolClicked} leftSection={<IconSchool size={16} />}>
            {Localization.strings.CREATE}
          </Button>
        </Group>
      </Stack>
    );
  }

  userList() {
    const rows = this.state.tableRows;
    return (
      <Stack gap="md">
        <Group gap="sm" wrap="wrap">
          <TextInput
            style={{ flex: 1, minWidth: 280 }}
            value={this.state.searchText}
            onChange={this.searchTextChanged}
            onKeyDown={this.handleSearchEnterPressed}
            placeholder={Localization.strings.SEARCH + "(可搜尋用戶名稱、顯示名稱、電郵)..."}
            leftSection={<IconSearch size={16} />}
          />
          <Button onClick={this.handleSearchClicked} leftSection={<IconSearch size={16} />}>
            搜尋
          </Button>
          <Tooltip label="重新整理搜尋結果">
            <ActionIcon variant="light" size="lg" onClick={this.handleSearchClicked}>
              <IconRefresh size={18} />
            </ActionIcon>
          </Tooltip>
        </Group>

        <Table.ScrollContainer minWidth={900}>
          <Table striped highlightOnHover verticalSpacing="sm" stickyHeader>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>編號</Table.Th>
                <Table.Th>帳號名稱</Table.Th>
                <Table.Th>顯示名稱</Table.Th>
                <Table.Th>電郵</Table.Th>
                <Table.Th>權限</Table.Th>
                <Table.Th>學校名稱</Table.Th>
                <Table.Th>最近登入</Table.Th>
                <Table.Th>操作</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((user, i) => (
                <Table.Tr key={i}>
                  <Table.Td>{user.id}</Table.Td>
                  <Table.Td>{user.username}</Table.Td>
                  <Table.Td>{user.display_name || '-'}</Table.Td>
                  <Table.Td>{user.email || '-'}</Table.Td>
                  <Table.Td>{this.getRoleText(user.role)}</Table.Td>
                  <Table.Td>{this.formatSchoolName(user.school_name, user.school_name_eng)}</Table.Td>
                  <Table.Td>{this.formatLastLogin(user.last_login_time)}</Table.Td>
                  <Table.Td>
                    <Group gap={4} wrap="nowrap">
                      <Tooltip label="編輯用戶">
                        <ActionIcon variant="subtle" color="brand" onClick={() => this.handleEditUserOpen(user)}>
                          <IconEdit size={18} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label="更改密碼">
                        <ActionIcon variant="subtle" color="orange" onClick={() => this.handlePasswordChangeOpen(user.id, user.username)}>
                          <IconLock size={18} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label="刪除用戶">
                        <ActionIcon variant="subtle" color="red" onClick={() => this.handleDeleteUserOpen(user.id, user.username)}>
                          <IconTrash size={18} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
              {rows.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={8} style={{ textAlign: 'center', padding: 20, color: '#666' }}>
                    請輸入搜尋條件並點擊搜尋按鈕(可搜尋用戶名稱、顯示名稱、電郵)
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Stack>
    );
  }

  getRoleText(role) {
    switch(parseInt(role)) {
      case 0: return '一般用戶';
      case 1: return '進階用戶';
      case 2: return '管理員';
      case 3: return Localization.strings.PLATFORM_ADMIN_ROLE || 'CLST Administrator';
      default: return '未知';
    }
  }

  formatLastLogin(lastLoginTime) {
    if (!lastLoginTime || lastLoginTime === '0000-00-00 00:00:00') {
      return '從未登入';
    }
    
    try {
      // Convert MySQL datetime to readable format in 24-hour format
      const date = new Date(lastLoginTime);
      if (isNaN(date.getTime())) {
        return '無效日期';
      }
      
      return date.toLocaleString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false // Use 24-hour format
      });
    } catch (e) {
      return '格式錯誤';
    }
  }

  formatSchoolName(schoolName, schoolNameEng) {
    // If no school name, return dash
    if (!schoolName) {
      return '-';
    }

    // If no English name or English name is empty, return only Chinese name
    if (!schoolNameEng || schoolNameEng.trim().length === 0) {
      return schoolName;
    }

    // If English name is the same as Chinese name, return only Chinese name
    if (schoolNameEng.trim() === schoolName.trim()) {
      return schoolName;
    }

    // If English name is different, display both with English name in gray
    return (
      <div>
        <div>{schoolName}</div>
        <div style={{ color: '#999', fontSize: '12px', marginTop: '2px' }}>
          {schoolNameEng}
        </div>
      </div>
    );
  }

  renderEditSchoolPage() {
    const dialog = this.state.editSchoolDialog;
    const title = `編輯學校資料 - ${dialog.name || `ID ${dialog.targetSchoolId}`}`;

    return (
      <Stack gap="md">
        {this.renderAdminDetailPageHeader(title, this.handleEditSchoolClose, dialog.isLoading)}
        <Box maw={900}>
          <Stack gap="md">
            <Box style={{ padding: 12, backgroundColor: '#f5f5f5', borderRadius: 6 }}>
              <strong>目標學校：</strong> ID: {dialog.targetSchoolId}
            </Box>
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" verticalSpacing="md">
              <TextInput
                label="學校名稱 (中文)"
                required
                value={dialog.name}
                onChange={this.handleEditSchoolFieldChange('name')}
                disabled={dialog.isLoading}
              />
              <TextInput
                label="學校名稱 (英文)"
                required
                value={dialog.nameEng}
                onChange={this.handleEditSchoolFieldChange('nameEng')}
                disabled={dialog.isLoading}
              />
              <TextInput
                label="代碼 (Key)"
                required
                description="只能包含英文字母、數字及底線"
                value={dialog.key}
                onChange={this.handleEditSchoolFieldChange('key')}
                disabled={dialog.isLoading}
              />
              <TextInput
                label="地區"
                placeholder="e.g.: hk, cn, tw, mo, us"
                value={dialog.locale}
                onChange={this.handleEditSchoolFieldChange('locale')}
                disabled={dialog.isLoading}
              />
              <Select
                label="學校類型"
                allowDeselect={false}
                disabled={dialog.isLoading}
                value={String(dialog.schoolType)}
                onChange={(v) => this.handleEditSchoolFieldChange('schoolType')({ target: { value: Number(v) } })}
                data={[
                  { value: '0', label: 'Unclassified' },
                  { value: '1', label: 'Primary School' },
                  { value: '2', label: 'Secondary School' },
                  { value: '3', label: 'University / Post-secondary / Gov.' },
                  { value: '4', label: 'Special School' },
                  { value: '5', label: 'Management Organization' },
                  { value: '6', label: 'Commercial Organization' },
                ]}
              />
              <Select
                label="學校級別"
                allowDeselect={false}
                disabled={dialog.isLoading}
                value={String(dialog.schoolLevel)}
                onChange={(v) => this.handleEditSchoolFieldChange('schoolLevel')({ target: { value: Number(v) } })}
                data={[
                  { value: '0', label: 'Not Specified' },
                  { value: '1', label: 'Level 1' },
                  { value: '2', label: 'Level 2' },
                  { value: '3', label: 'Level 3' },
                  { value: '4', label: 'Level 4' },
                  { value: '5', label: 'Level 5' },
                ]}
              />
            </SimpleGrid>
            <Divider />
            <TextInput
              type="password"
              label="管理員密碼"
              required
              description="輸入您的管理員密碼以確認身份"
              value={dialog.adminPassword}
              onChange={this.handleEditSchoolFieldChange('adminPassword')}
              disabled={dialog.isLoading}
              maw={440}
            />
            <Group justify="flex-end" mt="sm">
              <Button onClick={this.handleEditSchoolSubmit} loading={dialog.isLoading}>
                儲存變更
              </Button>
            </Group>
          </Stack>
        </Box>
      </Stack>
    );
  }

  renderSchoolSubscriptionPage() {
    const dialog = this.state.schoolSubscriptionDialog;
    const schoolName = dialog.school
      ? (dialog.school.name || dialog.school.name_eng || '')
      : '';
    const title = `${Localization.strings.SCHOOL_SUBSCRIPTION_DIALOG_TITLE || '學校訂閱'}${schoolName ? ` - ${schoolName}` : ''}`;

    return (
      <Stack gap="md">
        {this.renderAdminDetailPageHeader(title, this.closeSchoolSubscriptionDialog)}
        <Group justify="flex-start" align="flex-end" wrap="nowrap">
          <Box w="45%" miw={260} maw={440}>
            <SubscriptionPicker
              key={dialog.pickerKey}
              root={this.props.root}
              placeholder={Localization.strings.SCHOOL_SUBSCRIPTION_SEARCH_PLACEHOLDER || '搜尋訂閱'}
              disabled={dialog.addLoading || dialog.loading}
              selected={dialog.addSelected}
              onSelect={(sub) => {
                const updated = { ...dialog, addSelected: sub };
                this.setState({ schoolSubscriptionDialog: updated });
                this.state.schoolSubscriptionDialog = updated;
              }}
            />
          </Box>
          <Button
            variant="light"
            onClick={this.openSchoolSubscriptionCreatePage}
            disabled={dialog.addLoading || dialog.loading}
            leftSection={<IconPlus size={16} />}
          >
            {Localization.strings.SCHOOL_SUBSCRIPTION_CREATE || '創建訂閱'}
          </Button>
        </Group>
        <Group justify="flex-start" align="flex-end">
          <TextInput
            type="date"
            label={Localization.strings.SCHOOL_SUBSCRIPTION_START || '開始日'}
            value={dialog.addDateStart}
            disabled={dialog.addLoading || dialog.loading}
            onChange={(e) => {
              const updated = { ...dialog, addDateStart: e.target.value };
              this.setState({ schoolSubscriptionDialog: updated });
              this.state.schoolSubscriptionDialog = updated;
            }}
            w={180}
          />
          <Select
            label={Localization.strings.SCHOOL_SUBSCRIPTION_DURATION || '期限'}
            value={dialog.addDurationMonths}
            data={[
              { value: '6', label: Localization.strings.SCHOOL_SUBSCRIPTION_HALF_YEAR || '半年' },
              { value: '12', label: Localization.strings.SCHOOL_SUBSCRIPTION_ONE_YEAR || '1 年' },
              { value: '24', label: Localization.strings.SCHOOL_SUBSCRIPTION_TWO_YEARS || '2 年' },
              { value: '36', label: Localization.strings.SCHOOL_SUBSCRIPTION_THREE_YEARS || '3 年' },
            ]}
            allowDeselect={false}
            disabled={dialog.addLoading || dialog.loading}
            onChange={(value) => {
              const updated = {
                ...dialog,
                addDurationMonths: value || DEFAULT_SUBSCRIPTION_DURATION_MONTHS,
              };
              this.setState({ schoolSubscriptionDialog: updated });
              this.state.schoolSubscriptionDialog = updated;
            }}
            w={180}
          />
          <Button
            onClick={this.handleSchoolSubscriptionAdd}
            loading={dialog.addLoading}
            disabled={dialog.loading}
            leftSection={<IconPlus size={16} />}
          >
            {Localization.strings.SCHOOL_SUBSCRIPTION_ADD || '為學校加入訂閱'}
          </Button>
        </Group>
        {dialog.loading ? (
          <Group justify="center" p="md">
            <Loader size="sm" />
            <Text c="dimmed">{Localization.strings.LOADING_DATA}</Text>
          </Group>
        ) : (
          <Table.ScrollContainer minWidth={900}>
            <Table striped verticalSpacing="sm" style={{ tableLayout: 'fixed', minWidth: 900 }}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ width: 56 }}>ID</Table.Th>
                  <Table.Th>{Localization.strings.SUBSCRIPTION_NAME_ZH}</Table.Th>
                  <Table.Th>{Localization.strings.SUBSCRIPTION_NAME_EN}</Table.Th>
                  <Table.Th style={{ width: 142 }}>{Localization.strings.SCHOOL_SUBSCRIPTION_START || '開始日'}</Table.Th>
                  <Table.Th style={{ width: 142 }}>{Localization.strings.SCHOOL_SUBSCRIPTION_EXPIRE || '到期日'}</Table.Th>
                  <Table.Th style={{ width: 92 }}>{Localization.strings.SCHOOL_SUBSCRIPTION_STATUS || '狀態'}</Table.Th>
                  <Table.Th>{Localization.strings.REMARK}</Table.Th>
                  <Table.Th style={{ width: 76 }}></Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {dialog.rows.map((row, idx) => {
                  const isEditing = dialog.editingRow?.school_subscription_id === row.school_subscription_id;
                  const editingRow = dialog.editingRow;
                  const isUpdateLoading = dialog.updateLoading;
                  const isDeleteLoading = dialog.deleteLoading;
                  const isBusy = isUpdateLoading || isDeleteLoading || dialog.loading;
                  const canStartEditing = !editingRow && !isBusy;
                  const displayedDates = isEditing ? editingRow : row;
                  const isActive = isSubscriptionActive(displayedDates.date_start, displayedDates.date_expire);
                  const editableCellStyle = {
                    width: '100%',
                    minHeight: 30,
                    padding: 0,
                    border: 0,
                    background: 'transparent',
                    color: 'inherit',
                    font: 'inherit',
                    textAlign: 'left',
                    cursor: canStartEditing ? 'text' : 'default',
                  };
                  return (
                    <Table.Tr
                      key={row.school_subscription_id || idx}
                      onBlur={isEditing ? this.handleSchoolSubscriptionEditBlur : undefined}
                    >
                      <Table.Td>{row.subscription_id}</Table.Td>
                      <Table.Td>{row.subscription_name || '-'}</Table.Td>
                      <Table.Td>{row.subscription_name_en || '-'}</Table.Td>
                      <Table.Td>
                        {isEditing ? (
                          <TextInput
                            type="date"
                            size="xs"
                            value={editingRow.date_start}
                            autoFocus={dialog.editingField === 'date_start'}
                            disabled={isUpdateLoading}
                            onChange={(e) => {
                              const updated = { ...dialog, editingRow: { ...editingRow, date_start: e.target.value } };
                              this.setState({ schoolSubscriptionDialog: updated });
                              this.state.schoolSubscriptionDialog = updated;
                            }}
                          />
                        ) : (
                          <Box
                            component="button"
                            type="button"
                            disabled={!canStartEditing}
                            style={editableCellStyle}
                            onClick={() => this.handleSchoolSubscriptionEditStart(row, 'date_start')}
                          >
                            {row.date_start || '-'}
                          </Box>
                        )}
                      </Table.Td>
                      <Table.Td>
                        {isEditing ? (
                          <TextInput
                            type="date"
                            size="xs"
                            value={editingRow.date_expire}
                            autoFocus={dialog.editingField === 'date_expire'}
                            disabled={isUpdateLoading}
                            onChange={(e) => {
                              const updated = { ...dialog, editingRow: { ...editingRow, date_expire: e.target.value } };
                              this.setState({ schoolSubscriptionDialog: updated });
                              this.state.schoolSubscriptionDialog = updated;
                            }}
                          />
                        ) : (
                          <Box
                            component="button"
                            type="button"
                            disabled={!canStartEditing}
                            style={editableCellStyle}
                            onClick={() => this.handleSchoolSubscriptionEditStart(row, 'date_expire')}
                          >
                            {row.date_expire || '-'}
                          </Box>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Badge color={isActive ? 'green' : 'red'} variant="light" radius="sm">
                          {isActive
                            ? (Localization.strings.SCHOOL_SUBSCRIPTION_ACTIVE || 'Active')
                            : (Localization.strings.SCHOOL_SUBSCRIPTION_EXPIRED || 'Expired')}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        {isEditing ? (
                          <TextInput
                            size="xs"
                            value={editingRow.remark}
                            autoFocus={dialog.editingField === 'remark'}
                            disabled={isUpdateLoading}
                            placeholder={Localization.strings.REMARK || '備註'}
                            onChange={(e) => {
                              const updated = { ...dialog, editingRow: { ...editingRow, remark: e.target.value } };
                              this.setState({ schoolSubscriptionDialog: updated });
                              this.state.schoolSubscriptionDialog = updated;
                            }}
                          />
                        ) : (
                          <Box
                            component="button"
                            type="button"
                            disabled={!canStartEditing}
                            style={editableCellStyle}
                            onClick={() => this.handleSchoolSubscriptionEditStart(row, 'remark')}
                          >
                            {row.school_subscription_remark || '-'}
                          </Box>
                        )}
                      </Table.Td>
                      <Table.Td style={{ whiteSpace: 'nowrap' }}>
                        {isEditing ? (
                          <Group gap={4} wrap="nowrap">
                            <Tooltip label={Localization.strings.SAVE || '儲存'}>
                              <ActionIcon color="green" variant="subtle" onClick={this.handleSchoolSubscriptionUpdate} loading={isUpdateLoading}>
                                <IconCheck size={18} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip label={Localization.strings.CANCEL || '取消'}>
                              <ActionIcon color="red" variant="subtle" onClick={this.handleSchoolSubscriptionEditCancel} disabled={isUpdateLoading}>
                                <IconX size={18} />
                              </ActionIcon>
                            </Tooltip>
                          </Group>
                        ) : (
                          <Tooltip label={Localization.strings.SCHOOL_SUBSCRIPTION_DELETE || '移除訂閱'}>
                            <ActionIcon variant="subtle" color="red" onClick={() => this.handleSchoolSubscriptionDelete(row)} disabled={isBusy} loading={isDeleteLoading}>
                              <IconTrash size={18} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
                {dialog.rows.length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={8} style={{ textAlign: 'center', padding: 12 }}>
                      {Localization.strings.SCHOOL_SUBSCRIPTION_NONE || '暫無訂閱'}
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Stack>
    );
  }

  schoolList() {
    const direction = this.state.schoolNavigationDirection;
    const animationClassName = direction
      ? `admin-page-transition admin-page-transition--${direction}`
      : 'admin-page-transition';

    if (this.state.subscriptionCreateDialog.open && this.state.subscriptionCreateDialog.returnToSchoolSubscription) {
      return (
        <Box key="subscription-create" className={animationClassName}>
          {this.renderSubscriptionCreatePage()}
        </Box>
      );
    }
    if (this.state.editSchoolDialog.open) {
      return (
        <Box key="school-edit" className={animationClassName}>
          {this.renderEditSchoolPage()}
        </Box>
      );
    }
    if (this.state.schoolSubscriptionDialog.open) {
      return (
        <Box key="school-subscriptions" className={animationClassName}>
          {this.renderSchoolSubscriptionPage()}
        </Box>
      );
    }

    const results = this.state.schoolSearchResults;
    // school_type filter is mixed-type: "" means "all", otherwise a number.
    // Mantine Select is string-only, so map "" <-> 'ALL' sentinel at the boundary.
    const typeFilterValue = this.state.schoolFilters.school_type === "" ? 'ALL' : String(this.state.schoolFilters.school_type);
    return (
      <Box
        key="school-list"
        className={animationClassName}
        style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 'calc(85vh - 140px)' }}
      >
        <Stack gap="md" style={{ flex: 1, minHeight: 0 }}>
        <Group gap="sm" wrap="wrap" align="flex-end">
          <TextInput
            style={{ flex: 1, minWidth: 220 }}
            value={this.state.schoolSearchText}
            onChange={this.schoolSearchTextChanged}
            onKeyDown={this.handleSchoolSearchEnterPressed}
            placeholder="搜尋關鍵詞(學校名稱、英文名稱或代碼)..."
            leftSection={<IconSearch size={16} />}
          />
          <Select
            w={170}
            allowDeselect={false}
            value={typeFilterValue}
            onChange={(v) => this.handleSchoolFilterChange('school_type')({ target: { value: v === 'ALL' ? "" : Number(v) } })}
            data={[
              { value: 'ALL', label: '所有學校類型' },
              { value: '0', label: 'Unclassified' },
              { value: '1', label: 'Primary School' },
              { value: '2', label: 'Secondary School' },
              { value: '3', label: 'University / Post-secondary / Gov.' },
              { value: '4', label: 'Special School' },
              { value: '5', label: 'Management Organization' },
              { value: '6', label: 'Commercial Organization' },
            ]}
          />
          <Box w={230}>
            <SubscriptionPicker
              root={this.props.root}
              placeholder={Localization.strings.SCHOOL_SUBSCRIPTION_FILTER || '搜尋訂閱以篩選學校'}
              onSelect={(sub) => {
                const prevId = this.state.schoolSubscriptionFilter && this.state.schoolSubscriptionFilter.id;
                const nextId = sub && sub.id;
                this.setState({ schoolSubscriptionFilter: sub });
                this.state.schoolSubscriptionFilter = sub;
                if (prevId !== nextId) {
                  this.handleSchoolSearchClicked();
                }
              }}
            />
          </Box>
          <Select
            w={140}
            allowDeselect={false}
            value={this.state.schoolFilters.order_by}
            onChange={(v) => this.handleSchoolFilterChange('order_by')({ target: { value: v } })}
            data={[
              { value: 'id', label: '按創建時間' },
              { value: 'name', label: '按中文名稱' },
              { value: 'name_eng', label: '按英文名稱' },
              { value: 'school_type', label: '按學校類型' },
            ]}
          />
          <SortOrderToggle
            value={this.state.schoolFilters.order}
            onChange={(next) => this.handleSchoolFilterChange('order')({ target: { value: next } })}
          />
          <Select
            w={120}
            allowDeselect={false}
            value={String(this.state.schoolFilters.limit)}
            onChange={(v) => this.handleSchoolFilterChange('limit')({ target: { value: Number(v) } })}
            data={[
              { value: '50', label: '50 個結果' },
              { value: '100', label: '100 個結果' },
              { value: '200', label: '200 個結果' },
              { value: '500', label: '500 個結果' },
            ]}
          />
          <Tooltip label="重新整理搜尋結果">
            <ActionIcon variant="light" size="lg" onClick={this.handleSchoolRefreshClicked}>
              <IconRefresh size={18} />
            </ActionIcon>
          </Tooltip>
          <Button onClick={this.handleSchoolSearchClicked} leftSection={<IconSearch size={16} />}>
            搜尋
          </Button>
        </Group>

        {this.state.schoolLoading ? (
          <TableLoadingCard icon={<IconSchool {...LOADING_CARD_ICON_PROPS} />} />
        ) : (
          <Table.ScrollContainer minWidth={1000}>
            <Table striped highlightOnHover verticalSpacing="sm" stickyHeader>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>編號</Table.Th>
                  <Table.Th>操作</Table.Th>
                  <Table.Th>學校名稱</Table.Th>
                  <Table.Th>英文名稱</Table.Th>
                  <Table.Th>代碼</Table.Th>
                  <Table.Th>地區</Table.Th>
                  <Table.Th>類型</Table.Th>
                  <Table.Th>級別</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {results.map((school, i) => (
                  <Table.Tr key={i}>
                    <Table.Td>{school.id}</Table.Td>
                    <Table.Td>
                      <Group gap={4} wrap="nowrap">
                        <Tooltip label="編輯學校">
                          <ActionIcon variant="subtle" color="brand" onClick={() => this.handleEditSchoolOpen(school)}>
                            <IconEdit size={18} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label={Localization.strings.SCHOOL_SUBSCRIPTION_ACTION || '訂閱'}>
                          <ActionIcon variant="subtle" color="blue" onClick={() => this.openSchoolSubscriptionDialog(school)}>
                            <IconReceipt size={18} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Table.Td>
                    <Table.Td>{school.name || '-'}</Table.Td>
                    <Table.Td>{school.name_eng || '-'}</Table.Td>
                    <Table.Td>{school.key || '-'}</Table.Td>
                    <Table.Td>{school.locale || '-'}</Table.Td>
                    <Table.Td>{this.getSchoolTypeText(school.school_type)}</Table.Td>
                    <Table.Td>{this.getSchoolLevelText(school.school_level)}</Table.Td>
                  </Table.Tr>
                ))}
                {results.length === 0 && this.state.hasSchoolSearched && (
                  <Table.Tr>
                    <Table.Td colSpan={8} style={{ textAlign: 'center', padding: 20, color: '#666' }}>
                      沒有找到符合條件的學校
                    </Table.Td>
                  </Table.Tr>
                )}
                {results.length === 0 && !this.state.hasSchoolSearched && (
                  <Table.Tr>
                    <Table.Td colSpan={8} style={{ textAlign: 'center', padding: 20, color: '#666' }}>
                      請點擊搜尋按鈕以載入學校列表
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
        </Stack>
      </Box>
    );
  }

  renderAdminDetailPageHeader(title, onBack, disabled = false) {
    return (
      <>
        <Group gap="md" align="center">
          <Button
            variant="subtle"
            color="gray"
            onClick={onBack}
            disabled={disabled}
            leftSection={<IconArrowLeft size={18} />}
          >
            {Localization.strings.BACK || 'Back'}
          </Button>
          <Text fw={600} size="lg">{title}</Text>
        </Group>
        <Divider />
      </>
    );
  }

  renderSubscriptionLocalesPage() {
    const dialog = this.state.subscriptionLocalesDialog;
    const title = `${Localization.strings.SUBSCRIPTION_LOCALE_TITLE}${dialog.subscription ? ` - ${dialog.subscription.name}` : ''}`;
    const assignedRows = dialog.rows || [];
    const maxLocaleCount = Math.max(0, Number(dialog.subscription?.max_extra_locale_number) || 0);
    const remainingCapacity = Math.max(0, maxLocaleCount - assignedRows.length);
    const assignedLocaleIds = new Set(assignedRows.map((row) => String(row.epack_id)));
    const availableRows = (dialog.searchResults || []).filter((row) => !assignedLocaleIds.has(String(row.id)));
    const availableLocaleIds = new Set(availableRows.map((row) => String(row.id)));
    const selectedLocaleIds = (dialog.selectedLocaleIds || []).filter((id) => availableLocaleIds.has(String(id)));
    const selectedLocaleIdSet = new Set(selectedLocaleIds.map(String));
    const assignedJoinIds = new Set(
      assignedRows
        .map((row) => row.subscription_epack_id || row.id)
        .filter((id) => id !== null && id !== undefined)
        .map(String)
    );
    const selectedAssignedLocaleIds = (dialog.selectedAssignedLocaleIds || [])
      .map(String)
      .filter((id) => assignedJoinIds.has(id));
    const selectedAssignedLocaleIdSet = new Set(selectedAssignedLocaleIds);
    const isOverLimit = assignedRows.length > maxLocaleCount;
    const canAssign = selectedLocaleIds.length > 0
      && selectedLocaleIds.length <= remainingCapacity
      && !dialog.loading
      && !dialog.assigning
      && !dialog.removing;
    const panelHeight = 'clamp(22rem, calc(85vh - 14rem), 36rem)';
    const assignLabel = remainingCapacity === 0
      ? (Localization.strings.SUBSCRIPTION_LOCALE_LIMIT_REACHED || 'LOCALE limit reached')
      : (Localization.strings.SUBSCRIPTION_LOCALE_ASSIGN_SELECTED || 'Assign selected LOCALEs');
    const searchMode = dialog.searchMode || 'keyword';
    const isSourceSearchMode = searchMode === 'user' || searchMode === 'subscription';
    const selectedSearchSource = dialog.selectedSearchSource || null;
    const searchSourceResults = dialog.searchSourceResults || [];
    const searchModeOptions = [
      {
        value: 'user',
        label: Localization.strings.SUBSCRIPTION_LOCALE_SEARCH_BY_USER || 'User',
      },
      {
        value: 'subscription',
        label: Localization.strings.SUBSCRIPTION_LOCALE_SEARCH_BY_SUBSCRIPTION || 'Other subscription',
      },
      {
        value: 'keyword',
        label: Localization.strings.SUBSCRIPTION_LOCALE_SEARCH_BY_KEYWORD || 'Keyword',
      },
    ];
    const searchPlaceholder = {
      user: Localization.strings.SUBSCRIPTION_LOCALE_USER_SEARCH_PLACEHOLDER
        || 'Search username or display name',
      subscription: Localization.strings.SUBSCRIPTION_LOCALE_SUBSCRIPTION_SEARCH_PLACEHOLDER
        || 'Search subscription name',
      keyword: Localization.strings.SUBSCRIPTION_LOCALE_SEARCH_PLACEHOLDER
        || 'Search LOCALE name',
    }[searchMode];
    const searchPrompt = {
      user: Localization.strings.SUBSCRIPTION_LOCALE_USER_SEARCH_PROMPT
        || 'Search by username or display name',
      subscription: Localization.strings.SUBSCRIPTION_LOCALE_SUBSCRIPTION_SEARCH_PROMPT
        || 'Search another subscription by its name',
      keyword: Localization.strings.SUBSCRIPTION_LOCALE_SEARCH_PROMPT
        || 'Search for LOCALEs to assign',
    }[searchMode];
    const noSourceResultText = searchMode === 'user'
      ? (Localization.strings.SUBSCRIPTION_LOCALE_NO_USER_RESULT || 'No matching users found')
      : (Localization.strings.SUBSCRIPTION_LOCALE_NO_SUBSCRIPTION_RESULT || 'No matching subscriptions found');
    const filterTemplate = searchMode === 'user'
      ? (Localization.strings.SUBSCRIPTION_LOCALE_USER_FILTER || 'User: {0}')
      : (Localization.strings.SUBSCRIPTION_LOCALE_SUBSCRIPTION_FILTER || 'Subscription: {0}');
    const selectedFilterLabel = selectedSearchSource
      ? filterTemplate.replace('{0}', selectedSearchSource.label)
      : '';

    return (
      <Stack gap="md">
        {this.renderAdminDetailPageHeader(
          title,
          this.closeSubscriptionLocalesDialog,
          dialog.assigning || dialog.removing
        )}
        <Flex gap="md" align="stretch" style={{ minWidth: 0 }}>
          <Paper
            withBorder
            radius="md"
            style={{
              flex: 1,
              minWidth: 0,
              height: panelHeight,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <Box px="md" py="sm" style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}>
              <Text fw={600} c={isOverLimit ? 'red' : undefined}>
                {Localization.strings.SUBSCRIPTION_ASSIGNED_LOCALES || 'Assigned LOCALES'} ({assignedRows.length}/{maxLocaleCount})
              </Text>
            </Box>
            <ScrollArea style={{ flex: 1, minHeight: 0 }} type="auto">
              {dialog.loading ? (
                <Group justify="center" p="xl">
                  <Loader size="sm" />
                  <Text c="dimmed">{Localization.strings.LOADING_DATA}</Text>
                </Group>
              ) : assignedRows.length > 0 ? (
                assignedRows.map((row, idx) => {
                  const rowId = String(row.subscription_epack_id || row.id);
                  const authorName = row.user_display_name || row.user_username || '-';
                  const localeMetadata = [
                    authorName,
                    this.formatDateTime(row.date_created),
                    this.formatDateTime(row.date_update),
                  ].join(' | ');
                  return (
                    <Box
                      key={rowId || idx}
                      px="md"
                      py="sm"
                      style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}
                    >
                      <Checkbox
                        checked={selectedAssignedLocaleIdSet.has(rowId)}
                        disabled={dialog.assigning || dialog.removing}
                        label={
                          <Box style={{ minWidth: 0 }}>
                            <Text size="sm" lineClamp={2}>{row.epack_name || '-'}</Text>
                            <Text size="xs" c="dimmed" truncate>{localeMetadata}</Text>
                          </Box>
                        }
                        aria-label={row.epack_name || '-'}
                        onChange={(event) => this.handleAssignedLocaleSelectionChange(rowId, event.currentTarget.checked)}
                      />
                    </Box>
                  );
                })
              ) : (
                <Text ta="center" c="dimmed" size="sm" p="xl">
                  {Localization.strings.SUBSCRIPTION_NO_LOCALE}
                </Text>
              )}
            </ScrollArea>
            <Box px="md" py="sm" style={{ borderTop: '1px solid var(--mantine-color-gray-3)' }}>
              <Group justify="flex-end">
                <Button
                  color="red"
                  variant="light"
                  leftSection={<IconTrash size={16} />}
                  loading={dialog.removing}
                  disabled={
                    selectedAssignedLocaleIds.length === 0
                    || dialog.loading
                    || dialog.assigning
                  }
                  onClick={this.handleRemoveSelectedLocales}
                >
                  {Localization.strings.SUBSCRIPTION_REMOVE_SELECTED || 'Remove selected'}
                </Button>
              </Group>
            </Box>
          </Paper>

          <Flex align="center" justify="center" style={{ width: 56, flexShrink: 0 }}>
            <Tooltip label={assignLabel}>
              <ActionIcon
                size="xl"
                radius="xl"
                variant="filled"
                loading={dialog.assigning}
                disabled={!canAssign}
                aria-label={assignLabel}
                onClick={this.handleAssignSelectedLocales}
              >
                <IconChevronLeft size={24} stroke={2} />
              </ActionIcon>
            </Tooltip>
          </Flex>

          <Paper
            withBorder
            radius="md"
            style={{
              flex: 1,
              minWidth: 0,
              height: panelHeight,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <Stack gap="sm" px="md" py="sm" style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}>
              <SegmentedControl
                fullWidth
                value={searchMode}
                data={searchModeOptions}
                disabled={dialog.loading || dialog.assigning || dialog.removing}
                onChange={this.handleSubscriptionLocaleSearchModeChange}
              />
              <TextInput
                value={dialog.searchText || ''}
                placeholder={searchPlaceholder}
                leftSection={<IconSearch size={16} />}
                rightSection={dialog.searchLoading ? <Loader size={16} /> : null}
                disabled={dialog.loading || dialog.assigning || dialog.removing}
                aria-label={searchPlaceholder}
                onChange={this.handleSubscriptionLocaleSearchChange}
              />
              {selectedSearchSource ? (
                <Group gap="xs">
                  <Pill
                    withRemoveButton
                    onRemove={this.handleClearSubscriptionLocaleSearchSource}
                  >
                    {selectedFilterLabel}
                  </Pill>
                </Group>
              ) : null}
            </Stack>
            <ScrollArea style={{ flex: 1, minHeight: 0 }} type="auto">
              {dialog.searchLoading ? (
                <Group justify="center" p="xl">
                  <Loader size="sm" />
                  <Text c="dimmed">{Localization.strings.SUBSCRIPTION_SEARCHING}</Text>
                </Group>
              ) : !(dialog.searchText || '').trim() ? (
                <Text ta="center" c="dimmed" size="sm" p="xl">
                  {searchPrompt}
                </Text>
              ) : isSourceSearchMode && !selectedSearchSource ? (
                searchSourceResults.length === 0 ? (
                  <Text ta="center" c="dimmed" size="sm" p="xl">
                    {noSourceResultText}
                  </Text>
                ) : (
                  searchSourceResults.map((row) => {
                    const isUser = searchMode === 'user';
                    const primaryText = isUser
                      ? (row.display_name || row.username || '-')
                      : (row.name || row.name_en || '-');
                    const secondaryParts = isUser
                      ? [
                        row.username !== primaryText ? row.username : null,
                        row.school_name || row.school_name_eng,
                      ]
                      : [row.name_en !== primaryText ? row.name_en : null];
                    const secondaryText = secondaryParts.filter(Boolean).join(' | ');
                    return (
                      <UnstyledButton
                        key={row.id}
                        w="100%"
                        px="md"
                        py="sm"
                        disabled={dialog.assigning || dialog.removing}
                        aria-label={primaryText}
                        onClick={() => this.handleSubscriptionLocaleSearchSourceSelect(row)}
                        style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}
                      >
                        <Group justify="space-between" gap="sm" wrap="nowrap">
                          <Box style={{ flex: 1, minWidth: 0 }}>
                            <Text size="sm" fw={500} truncate>{primaryText}</Text>
                            {secondaryText ? (
                              <Text size="xs" c="dimmed" truncate>{secondaryText}</Text>
                            ) : null}
                          </Box>
                          <Badge variant="light" size="sm">
                            {row.locale_count || 0} LOCALE
                          </Badge>
                        </Group>
                      </UnstyledButton>
                    );
                  })
                )
              ) : availableRows.length === 0 ? (
                <Text ta="center" c="dimmed" size="sm" p="xl">
                  {Localization.strings.SUBSCRIPTION_LOCALE_NO_SEARCH_RESULT || 'No available LOCALES found'}
                </Text>
              ) : (
                availableRows.map((row) => {
                  const rowId = String(row.id);
                  const checked = selectedLocaleIdSet.has(rowId);
                  const selectionLimitReached = !checked && selectedLocaleIds.length >= remainingCapacity;
                  const authorName = row.user_display_name || row.user_username || '-';
                  const localeMetadata = [
                    authorName,
                    this.formatDateTime(row.date_created),
                    this.formatDateTime(row.date_update),
                  ].join(' | ');
                  return (
                    <Box
                      key={rowId}
                      px="md"
                      py="sm"
                      style={{
                        display: 'block',
                        borderBottom: '1px solid var(--mantine-color-gray-2)',
                        cursor: selectionLimitReached || dialog.assigning || dialog.removing ? 'not-allowed' : 'pointer',
                      }}
                    >
                      <Checkbox
                        checked={checked}
                        disabled={selectionLimitReached || dialog.assigning || dialog.removing}
                        label={
                          <Box>
                            <Text size="sm">{row.name || '-'}</Text>
                            <Text size="xs" c="dimmed" truncate>
                              {localeMetadata}
                            </Text>
                          </Box>
                        }
                        aria-label={row.name || '-'}
                        onChange={(event) => this.handleSubscriptionLocaleSelectionChange(row.id, event.currentTarget.checked)}
                      />
                    </Box>
                  );
                })
              )}
            </ScrollArea>
          </Paper>
        </Flex>
      </Stack>
    );
  }

  renderSubscriptionPrivilegePage() {
    const dialog = this.state.subscriptionPrivilegeDialog;
    const title = `${Localization.strings.SUBSCRIPTION_PRIVILEGE_TITLE}${dialog.subscription ? ` - ${dialog.subscription.name}` : ''}`;

    return (
      <Stack gap="md">
        {this.renderAdminDetailPageHeader(title, this.closeSubscriptionPrivilegeDialog)}
        {dialog.loading ? (
          <Group justify="center" p="md">
            <Loader size="sm" />
            <Text c="dimmed">{Localization.strings.LOADING_DATA}</Text>
          </Group>
        ) : (
          <Table.ScrollContainer minWidth={760}>
            <Table striped verticalSpacing="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>ID</Table.Th>
                  <Table.Th>{Localization.strings.SUBSCRIPTION_PRIVILEGE_NAME}</Table.Th>
                  <Table.Th>{Localization.strings.VALUE}</Table.Th>
                  <Table.Th>{Localization.strings.VALUE} 2</Table.Th>
                  <Table.Th w={80}></Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {dialog.rows.map((row, idx) => (
                  <Table.Tr key={row.subscription_privilege_id || row.id || idx}>
                    <Table.Td>{row.privilege_id}</Table.Td>
                    <Table.Td>
                      <Text>{row.name_long_zh || row.name_long_en || row.name || '-'}</Text>
                      {this.privilegeName(row) === PLAN_EXTRA_LOCALE && (
                        <Text size="xs" c="dimmed">
                          {Localization.strings.PLAN_EXTRA_LOCALE_ADMIN_HELP}
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      {this.privilegeIsBoolean(row) ? (
                        <Switch
                          checked={this.privilegeTruthy(row.value)}
                          onChange={(e) => this.commitPrivilegeField(row, 'value', e.currentTarget.checked ? 1 : 0)}
                        />
                      ) : row.name === 'STORAGE_LIMIT' ? (
                        (() => {
                          const gbDisplay = this.mbToGbDisplay(row.value);
                          return (
                            <TextInput
                              key={`val-${row.subscription_privilege_id || row.id}-${row.value}`}
                              type="number"
                              defaultValue={gbDisplay}
                              onBlur={(e) => {
                                this.commitStoragePrivilegeGb(row, gbDisplay, e.target.value);
                                const gb = parseFloat(e.target.value);
                                const normalized = (isNaN(gb) || gb < 0) ? 0 : gb;
                                e.target.value = this.mbToGbDisplay(Math.round(normalized * 1024));
                              }}
                              onKeyDown={(e) => { if (e.key === 'Enter') { e.target.blur(); } }}
                              min={0}
                              step={0.1}
                              rightSection={<Text size="sm" c="dimmed">GB</Text>}
                              rightSectionWidth={40}
                              w={140}
                            />
                          );
                        })()
                      ) : (
                        <TextInput
                          key={`val-${row.subscription_privilege_id || row.id}-${row.value}`}
                          type="number"
                          defaultValue={row.value ?? ''}
                          onBlur={(e) => this.commitPrivilegeField(row, 'value', e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.target.blur(); } }}
                          min={0}
                          rightSection={this.privilegeName(row) === PLAN_EXTRA_LOCALE ? <Text size="xs" c="dimmed">LOCALE</Text> : undefined}
                          rightSectionWidth={this.privilegeName(row) === PLAN_EXTRA_LOCALE ? 62 : undefined}
                          w={this.privilegeName(row) === PLAN_EXTRA_LOCALE ? 170 : 110}
                        />
                      )}
                    </Table.Td>
                    <Table.Td>
                      {row.name === 'AI_3D_MODEL' ? (
                        <TextInput
                          key={`val2-${row.subscription_privilege_id || row.id}-${row.value_2}`}
                          type="number"
                          defaultValue={row.value_2 ?? ''}
                          disabled={!this.privilegeTruthy(row.value)}
                          onBlur={(e) => this.commitPrivilegeField(row, 'value_2', e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.target.blur(); } }}
                          min={0}
                          w={110}
                        />
                      ) : (
                        <TextInput type="number" value={row.value_2 ?? ''} disabled readOnly w={110} />
                      )}
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'center' }}>
                      <Tooltip label={Localization.strings.RESET || 'Reset'}>
                        <ActionIcon variant="subtle" onClick={() => this.handlePrivilegeReset(row)}>
                          <IconRestore size={16} />
                        </ActionIcon>
                      </Tooltip>
                    </Table.Td>
                  </Table.Tr>
                ))}
                {dialog.rows.length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={5} style={{ textAlign: 'center', padding: 12 }}>
                      {Localization.strings.SUBSCRIPTION_NO_PRIVILEGE}
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Stack>
    );
  }

  renderSubscriptionCreatePage() {
    const dialog = this.state.subscriptionCreateDialog;
    const sourceSubscription = this.state.subscriptionCopyPrivilegeFrom;
    const sourceName = sourceSubscription
      ? (sourceSubscription.name || sourceSubscription.name_en || `#${sourceSubscription.id}`)
      : '';
    const sourceNameEn = sourceSubscription
      && sourceSubscription.name_en
      && sourceSubscription.name_en !== sourceName
      ? sourceSubscription.name_en
      : '';

    return (
      <Stack gap="md">
        {this.renderAdminDetailPageHeader(
          Localization.strings.SUBSCRIPTION_CREATE,
          this.closeSubscriptionCreateDialog,
          dialog.loading
        )}
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl" verticalSpacing="lg">
          <Stack gap="md">
            <TextInput
              label={Localization.strings.SUBSCRIPTION_NAME_ZH}
              required
              placeholder={Localization.strings.SUBSCRIPTION_NAME_ZH}
              value={dialog.name}
              onChange={this.handleSubscriptionCreateFieldChange('name')}
              disabled={dialog.loading}
              autoFocus={!!dialog.returnToSchoolSubscription}
            />
            <TextInput
              label={Localization.strings.SUBSCRIPTION_NAME_EN}
              required
              placeholder={Localization.strings.SUBSCRIPTION_NAME_EN}
              value={dialog.name_en}
              onChange={this.handleSubscriptionCreateFieldChange('name_en')}
              disabled={dialog.loading}
            />
            <Textarea
              label={Localization.strings.REMARK}
              placeholder={Localization.strings.REMARK}
              minRows={2}
              autosize
              value={dialog.remarks}
              onChange={this.handleSubscriptionCreateFieldChange('remarks')}
              disabled={dialog.loading}
            />
            <Switch
              label={Localization.strings.SUBSCRIPTION_IS_TEMPLATE || 'Is template'}
              checked={!!dialog.is_template}
              disabled={dialog.loading}
              onChange={(event) => {
                const updated = {
                  ...this.state.subscriptionCreateDialog,
                  is_template: event.currentTarget.checked,
                };
                this.setState({ subscriptionCreateDialog: updated });
                this.state.subscriptionCreateDialog = updated;
              }}
            />
          </Stack>

          <Stack gap="md">
            <Group align="center" wrap="nowrap">
              <Text size="sm" fw={500} w={190} style={{ flexShrink: 0 }}>
                {Localization.strings.SUBSCRIPTION_COPY_PRIV_LABEL}
              </Text>
              <Box style={{ flex: 1, minWidth: 0 }}>
                <SubscriptionPicker
                  root={this.props.root}
                  placeholder={Localization.strings.SUBSCRIPTION_PICKER_PLACEHOLDER}
                  disabled={dialog.loading}
                  searchOnly
                  onSelect={this.handleSubscriptionCopySourceSelect}
                />
              </Box>
            </Group>

            <Group align="center" gap="xs" wrap="nowrap" mih={40}>
              <Text size="sm" fw={500}>
                {Localization.strings.SUBSCRIPTION_SOURCE_LABEL || 'Source'}:
              </Text>
              {sourceSubscription ? (
                <>
                  <Popover key={sourceSubscription.id} width={380} position="bottom-start" withArrow shadow="md">
                    <Popover.Target>
                      <ActionIcon
                        variant="light"
                        color="grape"
                        aria-label={Localization.strings.SUBSCRIPTION_SOURCE_PRIVILEGES || 'Source privileges'}
                        title={Localization.strings.SUBSCRIPTION_SOURCE_PRIVILEGES || 'Source privileges'}
                      >
                        <IconShieldCheck size={18} />
                      </ActionIcon>
                    </Popover.Target>
                    <Popover.Dropdown>
                      <Text size="sm" fw={600} mb="xs">
                        {Localization.strings.SUBSCRIPTION_SOURCE_PRIVILEGES || 'Source privileges'}
                      </Text>
                      {dialog.sourcePrivilegesLoading ? (
                        <Group justify="center" py="lg">
                          <Loader size="sm" />
                        </Group>
                      ) : dialog.sourcePrivileges.length === 0 ? (
                        <Text size="sm" c="dimmed">
                          {Localization.strings.SUBSCRIPTION_NO_PRIVILEGE}
                        </Text>
                      ) : (
                        <ScrollArea h={280} type="auto">
                          <Stack gap={0} pr="xs">
                            {dialog.sourcePrivileges.map((privilege, index) => {
                              const privilegeLabel = Localization.strings.getLanguage() === 'en'
                                ? (privilege.name_long_en || privilege.name_long_zh || privilege.name || '-')
                                : (privilege.name_long_zh || privilege.name_long_en || privilege.name || '-');
                              const isBoolean = this.privilegeIsBoolean(privilege);
                              const valueText = isBoolean
                                ? (this.privilegeTruthy(privilege.value)
                                  ? Localization.strings.SUPPORTED
                                  : Localization.strings.NOT_SUPPORTED)
                                : [privilege.value, privilege.value_2]
                                  .filter((value) => value !== undefined && value !== null && value !== '')
                                  .join(' / ');
                              return (
                                <Group
                                  key={privilege.subscription_privilege_id || privilege.id || index}
                                  justify="space-between"
                                  align="flex-start"
                                  gap="sm"
                                  wrap="nowrap"
                                  py="xs"
                                  style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}
                                >
                                  <Text size="sm" style={{ flex: 1 }}>{privilegeLabel}</Text>
                                  <Badge
                                    variant="light"
                                    color={isBoolean ? (this.privilegeTruthy(privilege.value) ? 'green' : 'gray') : 'blue'}
                                    style={{ flexShrink: 0 }}
                                  >
                                    {valueText || '-'}
                                  </Badge>
                                </Group>
                              );
                            })}
                          </Stack>
                        </ScrollArea>
                      )}
                    </Popover.Dropdown>
                  </Popover>
                  <Box style={{ minWidth: 0 }}>
                    <Text size="sm" fw={500} truncate>{sourceName}</Text>
                    {sourceNameEn ? <Text size="xs" c="dimmed" truncate>{sourceNameEn}</Text> : null}
                  </Box>
                </>
              ) : (
                <Text size="sm" c="dimmed">
                  {Localization.strings.SUBSCRIPTION_SOURCE_NOT_SELECTED || 'Not selected'}
                </Text>
              )}
            </Group>

            <Box>
              <Text size="sm" fw={500} mb={6}>
                {Localization.strings.SUBSCRIPTION_SELECT_FROM_TEMPLATES || 'Select from templates'}
              </Text>
              <Paper withBorder radius="md" p="sm">
                <ScrollArea h={150} type="auto" offsetScrollbars>
                  {dialog.templatesLoading ? (
                    <Group justify="center" h={130}>
                      <Loader size="sm" />
                    </Group>
                  ) : dialog.templates.length === 0 ? (
                    <Text size="sm" c="dimmed">
                      {Localization.strings.SUBSCRIPTION_NO_TEMPLATES || 'No templates'}
                    </Text>
                  ) : (
                    <Group gap="xs" align="flex-start">
                      {dialog.templates.map((template) => {
                        const selected = sourceSubscription && sourceSubscription.id === template.id;
                        const templateName = template.name || template.name_en || `#${template.id}`;
                        return (
                          <Tooltip
                            key={template.id}
                            label={[template.name, template.name_en].filter(Boolean).join(' / ')}
                          >
                            <UnstyledButton
                              type="button"
                              disabled={dialog.loading}
                              aria-pressed={!!selected}
                              onClick={() => this.handleSubscriptionCopySourceSelect(template)}
                              style={{
                                border: `1px solid ${selected ? BRAND_PRIMARY : 'var(--mantine-color-gray-3)'}`,
                                borderRadius: 999,
                                padding: '5px 12px',
                                backgroundColor: selected ? BRAND_PRIMARY : 'var(--mantine-color-gray-1)',
                                color: selected ? 'white' : 'var(--mantine-color-gray-7)',
                                fontSize: 'var(--mantine-font-size-sm)',
                                lineHeight: 1.35,
                                cursor: dialog.loading ? 'default' : 'pointer',
                              }}
                            >
                              {templateName}
                            </UnstyledButton>
                          </Tooltip>
                        );
                      })}
                    </Group>
                  )}
                </ScrollArea>
              </Paper>
            </Box>

            <Switch
              label={Localization.strings.SUBSCRIPTION_COPY_LOCALE_LABEL}
              checked={!!dialog.copyExtraLocales}
              disabled={dialog.loading || !sourceSubscription}
              onChange={this.handleSubscriptionCopyExtraLocalesChange}
            />
          </Stack>
        </SimpleGrid>

        <Group justify="flex-end" mt="sm">
          <Button
            onClick={this.handleSubscriptionCreateSubmit}
            loading={dialog.loading}
            leftSection={<IconPlus size={16} />}
          >
            {Localization.strings.CREATE}
          </Button>
        </Group>
      </Stack>
    );
  }

  renderEditableSubscriptionName(row, field) {
    const editingCell = this.state.subscriptionEditingCell;
    const isEditing = editingCell?.id === row.id && editingCell.field === field;
    const updateKey = `${row.id}:${field}`;
    const isUpdating = !!this.state.subscriptionUpdatingFields[updateKey];
    const label = field === 'name'
      ? Localization.strings.SUBSCRIPTION_NAME_ZH
      : Localization.strings.SUBSCRIPTION_NAME_EN;

    if (isEditing) {
      return (
        <TextInput
          size="xs"
          variant="unstyled"
          value={editingCell.value}
          autoFocus
          maxLength={512}
          aria-label={label}
          onFocus={(event) => event.currentTarget.select()}
          onChange={this.handleSubscriptionNameEditChange}
          onBlur={this.handleSubscriptionNameEditCommit}
          onKeyDown={this.handleSubscriptionNameEditKeyDown}
        />
      );
    }

    return (
      <Box
        component="button"
        type="button"
        disabled={isUpdating}
        aria-label={`${Localization.strings.EDIT || 'Edit'} ${label}`}
        title={Localization.strings.SUBSCRIPTION_DOUBLE_CLICK_EDIT || 'Double-click to edit'}
        onDoubleClick={() => this.handleSubscriptionNameEditStart(row, field)}
        style={{
          width: '100%',
          minHeight: 30,
          padding: 0,
          border: 0,
          background: 'transparent',
          color: 'inherit',
          font: 'inherit',
          textAlign: 'left',
          cursor: isUpdating ? 'wait' : 'text',
        }}
      >
        {row[field] || '-'}
      </Box>
    );
  }

  subscriptionTab() {
    const direction = this.state.subscriptionNavigationDirection;
    const animationClassName = direction
      ? `admin-page-transition admin-page-transition--${direction}`
      : 'admin-page-transition';

    if (this.state.subscriptionCreateDialog.open) {
      return (
        <Box key="subscription-create" className={animationClassName}>
          {this.renderSubscriptionCreatePage()}
        </Box>
      );
    }
    if (this.state.subscriptionLocalesDialog.open) {
      return (
        <Box key="subscription-locales" className={animationClassName}>
          {this.renderSubscriptionLocalesPage()}
        </Box>
      );
    }
    if (this.state.subscriptionPrivilegeDialog.open) {
      return (
        <Box key="subscription-privileges" className={animationClassName}>
          {this.renderSubscriptionPrivilegePage()}
        </Box>
      );
    }

    const rows = this.state.subscriptionList;
    return (
      <Box
        key="subscription-list"
        className={animationClassName}
        style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 'calc(85vh - 140px)' }}
      >
        <Stack gap="md" style={{ flex: 1, minHeight: 0 }}>
          <Group gap="sm" wrap="wrap" align="flex-end">
          <TextInput
            style={{ flex: 1, minWidth: 240 }}
            label={Localization.strings.SUBSCRIPTION_KEYWORD_SEARCH}
            placeholder={Localization.strings.SUBSCRIPTION_KEYWORD_HINT}
            value={this.state.subscriptionSearchText}
            onChange={this.handleSubscriptionKeywordChange}
            onKeyDown={this.handleSubscriptionSearchEnter}
            leftSection={<IconSearch size={16} />}
          />
          <Select
            w={170}
            allowDeselect={false}
            value={this.state.subscriptionSortBy}
            onChange={(v) => this.handleSubscriptionSortByChange({ target: { value: v } })}
            data={[
              { value: 'date_create', label: Localization.strings.SUBSCRIPTION_SORT_CREATED },
              { value: 'name', label: Localization.strings.SUBSCRIPTION_SORT_NAME_ZH },
              { value: 'name_en', label: Localization.strings.SUBSCRIPTION_SORT_NAME_EN },
            ]}
          />
          <SortOrderToggle
            value={this.state.subscriptionSortDir}
            onChange={(next) => this.handleSubscriptionSortDirChange({ target: { value: next } })}
          />
          <Select
            w={120}
            allowDeselect={false}
            value={String(this.state.subscriptionLimit)}
            onChange={(v) => this.handleSubscriptionLimitChange({ target: { value: Number(v) } })}
            data={[
              { value: '50', label: '50 個結果' },
              { value: '100', label: '100 個結果' },
              { value: '200', label: '200 個結果' },
              { value: '500', label: '500 個結果' },
            ]}
          />
          <Button onClick={this.handleSubscriptionSearch} loading={this.state.subscriptionLoading} leftSection={<IconSearch size={16} />}>
            {this.state.subscriptionLoading ? Localization.strings.SUBSCRIPTION_SEARCHING : Localization.strings.SEARCH}
          </Button>
          <Button variant="light" onClick={this.openSubscriptionCreateDialog} leftSection={<IconPlus size={16} />}>
            {Localization.strings.SUBSCRIPTION_CREATE}
          </Button>
        </Group>

        {this.state.subscriptionLoading ? (
          <TableLoadingCard icon={<IconLicense {...LOADING_CARD_ICON_PROPS} />} />
        ) : (
          <Table.ScrollContainer minWidth={1366}>
            <Table
              striped
              highlightOnHover
              verticalSpacing="sm"
              stickyHeader
              style={{ tableLayout: 'fixed', width: 1366, minWidth: 1366 }}
            >
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ width: 64 }}>ID</Table.Th>
                  <Table.Th style={{ width: 180 }}>{Localization.strings.ACTION}</Table.Th>
                  <Table.Th style={{ width: 300 }}>{Localization.strings.SUBSCRIPTION_NAME_ZH}</Table.Th>
                  <Table.Th style={{ width: 280 }}>{Localization.strings.SUBSCRIPTION_NAME_EN}</Table.Th>
                  <Table.Th style={{ width: 96 }}>{Localization.strings.SUBSCRIPTION_IS_TEMPLATE || 'Is template'}</Table.Th>
                  <Table.Th style={{ width: 170 }}>{Localization.strings.SUBSCRIPTION_SORT_CREATED}</Table.Th>
                  <Table.Th style={{ width: 220 }}>{Localization.strings.REMARK}</Table.Th>
                  <Table.Th style={{ width: 56 }}></Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((row) => {
                  const templateUpdateKey = `${row.id}:is_template`;
                  const isTemplateUpdating = !!this.state.subscriptionUpdatingFields[templateUpdateKey];
                  const assignedExtraLocaleCount = Number(row.assigned_extra_locale_count) || 0;
                  const maxExtraLocaleNumber = Number(row.max_extra_locale_number) || 0;
                  const isExtraLocaleOverLimit = assignedExtraLocaleCount > maxExtraLocaleNumber;
                  return (
                  <Table.Tr key={row.id}>
                    <Table.Td>{row.id}</Table.Td>
                    <Table.Td>
                      <Group gap={4} wrap="nowrap">
                        <Tooltip label={Localization.strings.SUBSCRIPTION_EXTRA_LOCALE_USAGE || 'Assigned extra LOCALEs / maximum'}>
                          <Badge
                            color={isExtraLocaleOverLimit ? 'red' : 'blue'}
                            variant="light"
                            radius="sm"
                            style={{ width: 64, minWidth: 64, flexShrink: 0 }}
                          >
                            {`${assignedExtraLocaleCount}/${maxExtraLocaleNumber}`}
                          </Badge>
                        </Tooltip>
                        <Tooltip label={Localization.strings.SUBSCRIPTION_LOCALE_BUTTON}>
                          <ActionIcon variant="subtle" color="blue" onClick={() => this.openSubscriptionLocalesDialog(row)}>
                            <IconMap size={18} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label={Localization.strings.SUBSCRIPTION_PRIVILEGE_BUTTON}>
                          <ActionIcon variant="subtle" color="grape" onClick={() => this.openSubscriptionPrivilegeDialog(row)}>
                            <IconShieldCheck size={18} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Table.Td>
                    <Table.Td style={{ overflow: 'hidden', wordBreak: 'break-word' }}>
                      {this.renderEditableSubscriptionName(row, 'name')}
                    </Table.Td>
                    <Table.Td style={{ overflow: 'hidden', wordBreak: 'break-word' }}>
                      {this.renderEditableSubscriptionName(row, 'name_en')}
                    </Table.Td>
                    <Table.Td>
                      <Switch
                        checked={this.isSubscriptionTemplate(row.is_template)}
                        disabled={isTemplateUpdating}
                        aria-label={Localization.strings.SUBSCRIPTION_IS_TEMPLATE || 'Is template'}
                        onChange={(event) => this.handleSubscriptionTemplateChange(row, event.currentTarget.checked)}
                      />
                    </Table.Td>
                    <Table.Td style={{ whiteSpace: 'nowrap' }}>{this.formatDateTime(row.date_create)}</Table.Td>
                    <Table.Td style={{ overflow: 'hidden', wordBreak: 'break-all' }}>
                      {row.remarks || '-'}
                    </Table.Td>
                    <Table.Td style={{ width: 56, whiteSpace: 'nowrap', textAlign: 'center' }}>
                      <Tooltip label={Localization.strings.SUBSCRIPTION_DELETE || Localization.strings.DELETE || 'Delete'}>
                        <ActionIcon variant="subtle" color="red" onClick={() => this.openSubscriptionDeleteDialog(row)}>
                          <IconTrash size={18} />
                        </ActionIcon>
                      </Tooltip>
                    </Table.Td>
                  </Table.Tr>
                  );
                })}
                {rows.length === 0 && this.state.hasSubscriptionSearched && (
                  <Table.Tr>
                    <Table.Td colSpan={8} style={{ textAlign: 'center', padding: 20, color: '#666' }}>
                      {Localization.strings.SUBSCRIPTION_NO_RESULT}
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
        </Stack>
      </Box>
    );
  }

  searchTextChanged(e) {
    this.setState({ searchText: e.target.value });
    this.state.searchText = e.target.value;
  }

  handleSearchClicked() {
    if (!this.state.searchText || this.state.searchText.trim() === "") {
      this.props.root.addNotification("請輸入搜尋條件", false);
      return;
    }

    let requestObj = {
      search_term: this.state.searchText.trim()
    };

    this.props.root.refs.API.request("search_user", requestObj, this.searchUserOK, this.searchUserFailed);
  }

  searchUserOK(res) {
    console.log("search user ok", res);
    let userList = res.body['result'];
    
    if (Array.isArray(userList)) {
      this.setState({
        tableRows: userList,
      });
      this.state.tableRows = userList;
      
      this.props.root.addNotification(`找到 ${userList.length} 名用戶`, true);
    } else {
      this.setState({
        tableRows: [],
      });
      this.state.tableRows = [];
      this.props.root.addNotification("搜尋結果為空", false);
    }
  }

  searchUserFailed(err) {
    console.log("search user failed", err);
    this.setState({
      tableRows: [],
    });
    this.state.tableRows = [];
    this.props.root.addNotification("搜尋失敗", false);
  }

  handleSearchEnterPressed(e) {
    if (e.key === "Enter") {
      this.handleSearchClicked();
    }
  }

  // School search methods
  schoolSearchTextChanged = (e) => {
    this.setState({ schoolSearchText: e.target.value });
    this.state.schoolSearchText = e.target.value;
  }

  handleSchoolFilterChange = (field) => (e) => {
    let filters = { ...this.state.schoolFilters };
    filters[field] = e.target.value;
    this.setState({ schoolFilters: filters });
    this.state.schoolFilters = filters;
    this.handleSchoolSearchClicked();
  }

  handleSchoolSearchClicked = () => {
    if (!this.props.root || !this.props.root.refs || !this.props.root.refs.API) {
      return;
    }

    const hasSubscriptionFilter = this.state.schoolSubscriptionFilter && this.state.schoolSubscriptionFilter.id;

    let requestObj = {
      order_by: this.state.schoolFilters.order_by,
      order: this.state.schoolFilters.order || DEFAULT_SORT_ORDER,
      limit: parseInt(this.state.schoolFilters.limit) || 100,
    };

    if (this.state.schoolSearchText && this.state.schoolSearchText.trim() !== "") {
      requestObj.keyword = this.state.schoolSearchText.trim();
    }
    
    if (this.state.schoolFilters.school_type !== "" && this.state.schoolFilters.school_type != null) {
      requestObj.school_type = parseInt(this.state.schoolFilters.school_type);
    }

    this.setState({
      schoolSearchResults: [],
      schoolLoading: true,
    });
    this.state.schoolSearchResults = [];
    this.state.schoolLoading = true;

    if (hasSubscriptionFilter) {
      requestObj.subscription_id = this.state.schoolSubscriptionFilter.id;
      this.props.root.refs.API.request("get_schools_by_subscription", requestObj, this.schoolSearchOK, this.schoolSearchFailed);
    } else {
      this.props.root.refs.API.request("get_filtered_school_list", requestObj, this.schoolSearchOK, this.schoolSearchFailed);
    }
  }

  schoolSearchOK = (res) => {
    console.log("school search ok", res);
    let schoolList = res.body['result'];
    
    if (Array.isArray(schoolList)) {
      this.setState({
        schoolSearchResults: schoolList,
        hasSchoolSearched: true,
        schoolLoading: false,
      });
      this.state.schoolSearchResults = schoolList;
      this.state.hasSchoolSearched = true;
      this.state.schoolLoading = false;
    } else {
      this.setState({
        schoolSearchResults: [],
        hasSchoolSearched: true,
        schoolLoading: false,
      });
      this.state.schoolSearchResults = [];
      this.state.hasSchoolSearched = true;
      this.state.schoolLoading = false;
      this.props.root.addNotification("搜尋結果為空", false);
    }
  }

  schoolSearchFailed = (err) => {
    console.log("school search failed", err);
    this.setState({
      schoolSearchResults: [],
      hasSchoolSearched: true,
      schoolLoading: false,
    });
    this.state.schoolSearchResults = [];
    this.state.hasSchoolSearched = true;
    this.state.schoolLoading = false;
    this.props.root.addNotification("搜尋失敗", false);
  }

  handleSchoolSearchEnterPressed = (e) => {
    if (e.key === "Enter") {
      this.handleSchoolSearchClicked();
    }
  }

  handleSchoolRefreshClicked = () => {
    this.handleSchoolSearchClicked();
  }

  openSchoolSubscriptionDialog = (school) => {
    if (!school || !school.id) return;
    const dialog = {
      open: true,
      school,
      rows: [],
      loading: true,
      addSelected: null,
      addLoading: false,
      addDateStart: getTodayDateString(),
      addDurationMonths: DEFAULT_SUBSCRIPTION_DURATION_MONTHS,
      pickerKey: Date.now(),
      editingRow: null,
      editingField: null,
      updateLoading: false,
      deleteLoading: false,
      deleteConfirmRow: null,
    };
    this.setState({
      schoolNavigationDirection: 'forward',
      schoolSubscriptionDialog: dialog,
    });
    this.state.schoolSubscriptionDialog = dialog;
    this.state.schoolNavigationDirection = 'forward';

    this.fetchSchoolSubscriptions(school.id);
  }

  closeSchoolSubscriptionDialog = () => {
    const dialog = {
      open: false,
      school: null,
      rows: [],
      loading: false,
      addSelected: null,
      addLoading: false,
      addDateStart: getTodayDateString(),
      addDurationMonths: DEFAULT_SUBSCRIPTION_DURATION_MONTHS,
      pickerKey: Date.now(),
      editingRow: null,
      editingField: null,
      updateLoading: false,
      deleteLoading: false,
      deleteConfirmRow: null,
    };
    this.setState({
      schoolNavigationDirection: 'back',
      schoolSubscriptionDialog: dialog,
    });
    this.state.schoolSubscriptionDialog = dialog;
    this.state.schoolNavigationDirection = 'back';
  }

  fetchSchoolSubscriptions = (schoolId) => {
    const targetId = schoolId || (this.state.schoolSubscriptionDialog.school ? this.state.schoolSubscriptionDialog.school.id : 0);
    if (!targetId || !this.props.root.refs || !this.props.root.refs.API) {
      return;
    }
    this.setState({
      schoolSubscriptionDialog: {
        ...this.state.schoolSubscriptionDialog,
        loading: true,
      }
    });
    this.state.schoolSubscriptionDialog.loading = true;
    this.props.root.refs.API.request(
      "get_subscriptions_by_school_id",
      { school_id: targetId, include_inactive: true },
      this.schoolSubscriptionListOK,
      this.schoolSubscriptionListFailed
    );
  }

  schoolSubscriptionListOK = (res) => {
    const rows = Array.isArray(res?.body?.result) ? res.body.result : [];
    this.setState({
      schoolSubscriptionDialog: {
        ...this.state.schoolSubscriptionDialog,
        rows,
        loading: false,
      }
    });
    this.state.schoolSubscriptionDialog.rows = rows;
    this.state.schoolSubscriptionDialog.loading = false;
  }

  schoolSubscriptionListFailed = (err) => {
    console.log("school subscription list failed", err);
    this.setState({
      schoolSubscriptionDialog: {
        ...this.state.schoolSubscriptionDialog,
        rows: [],
        loading: false,
      }
    });
    this.state.schoolSubscriptionDialog.rows = [];
    this.state.schoolSubscriptionDialog.loading = false;
    this.props.root.addNotification(Localization.strings.SCHOOL_SUBSCRIPTION_LOAD_FAILED || "載入訂閱失敗", false);
  }

  handleSchoolSubscriptionAdd = () => {
    const dialog = this.state.schoolSubscriptionDialog;
    if (!dialog.school || !dialog.school.id) return;
    if (!dialog.addSelected || !dialog.addSelected.id) {
      this.props.root.addNotification(Localization.strings.SCHOOL_SUBSCRIPTION_SELECT || "請先選擇訂閱", false);
      return;
    }
    const dateExpire = getSubscriptionEndDate(dialog.addDateStart, dialog.addDurationMonths);
    if (!dialog.addDateStart || !dateExpire) {
      this.props.root.addNotification(Localization.strings.SCHOOL_SUBSCRIPTION_START_REQUIRED || "請選擇開始日", false);
      return;
    }
    const payload = {
      school_id: dialog.school.id,
      subscription_id: dialog.addSelected.id,
      date_start: dialog.addDateStart,
      date_expire: dateExpire,
    };
    this.setState({
      schoolSubscriptionDialog: {
        ...dialog,
        addLoading: true,
      }
    });
    this.state.schoolSubscriptionDialog.addLoading = true;
    this.props.root.refs.API.request(
      "school_subscription_add",
      payload,
      () => {
        this.props.root.addNotification(Localization.strings.SCHOOL_SUBSCRIPTION_ADDED || "已加入訂閱", true);
        const resetDialog = {
          ...this.state.schoolSubscriptionDialog,
          addSelected: null,
          addLoading: false,
          addDateStart: getTodayDateString(),
          addDurationMonths: DEFAULT_SUBSCRIPTION_DURATION_MONTHS,
          pickerKey: Date.now(),
        };
        this.setState({ schoolSubscriptionDialog: resetDialog });
        this.state.schoolSubscriptionDialog = resetDialog;
        this.fetchSchoolSubscriptions(dialog.school.id);
      },
      (err) => {
        const msg = err?.body?.remarks || Localization.strings.SCHOOL_SUBSCRIPTION_LOAD_FAILED || "新增失敗";
        this.props.root.addNotification(msg, false);
        this.setState({
          schoolSubscriptionDialog: {
            ...this.state.schoolSubscriptionDialog,
            addLoading: false,
          }
        });
        this.state.schoolSubscriptionDialog.addLoading = false;
      }
    );
  }

  handleSchoolSubscriptionEditStart = (row, editingField) => {
    const editingRow = {
      subscription_id: row.subscription_id,
      school_subscription_id: row.school_subscription_id,
      date_start: row.date_start || '',
      date_expire: row.date_expire || '',
      remark: row.school_subscription_remark || '',
    };
    const updated = { ...this.state.schoolSubscriptionDialog, editingRow, editingField };
    this.setState({ schoolSubscriptionDialog: updated });
    this.state.schoolSubscriptionDialog = updated;
  }

  handleSchoolSubscriptionEditCancel = () => {
    const updated = { ...this.state.schoolSubscriptionDialog, editingRow: null, editingField: null };
    this.setState({ schoolSubscriptionDialog: updated });
    this.state.schoolSubscriptionDialog = updated;
  }

  handleSchoolSubscriptionEditBlur = (event) => {
    const nextTarget = event.relatedTarget;
    if (nextTarget && event.currentTarget.contains(nextTarget)) return;
    if (this.state.schoolSubscriptionDialog.updateLoading) return;

    this.handleSchoolSubscriptionUpdate();
  }

  handleSchoolSubscriptionUpdate = () => {
    const dialog = this.state.schoolSubscriptionDialog;
    if (!dialog.school || !dialog.editingRow) return;
    const { school_subscription_id, date_start, date_expire, remark } = dialog.editingRow;
    const payload = {
      // school_id is part of the route path on the new surface
      // (PATCH /api/school/{school_id}/subscription/{school_subscription_id}),
      // so it has to be in the payload even though the dialog already holds it.
      school_id: dialog.school.id,
      school_subscription_id,
      date_start,
      date_expire,
      remark,
    };
    const updated = { ...dialog, updateLoading: true };
    this.setState({ schoolSubscriptionDialog: updated });
    this.state.schoolSubscriptionDialog.updateLoading = true;
    this.props.root.refs.API.request(
      "school_subscription_update",
      payload,
      () => {
        this.props.root.addNotification(Localization.strings.SCHOOL_SUBSCRIPTION_UPDATED || "訂閱記錄已更新", true);
        const reset = { ...this.state.schoolSubscriptionDialog, editingRow: null, editingField: null, updateLoading: false };
        this.setState({ schoolSubscriptionDialog: reset });
        this.state.schoolSubscriptionDialog = reset;
        this.fetchSchoolSubscriptions(dialog.school.id);
      },
      (err) => {
        const msg = err?.body?.remarks || Localization.strings.SCHOOL_SUBSCRIPTION_UPDATE_FAILED || "更新失敗";
        this.props.root.addNotification(msg, false);
        const reset = { ...this.state.schoolSubscriptionDialog, updateLoading: false };
        this.setState({ schoolSubscriptionDialog: reset });
        this.state.schoolSubscriptionDialog.updateLoading = false;
      }
    );
  }

  handleSchoolSubscriptionDelete = (row) => {
    const updated = { ...this.state.schoolSubscriptionDialog, deleteConfirmRow: row };
    this.setState({ schoolSubscriptionDialog: updated });
    this.state.schoolSubscriptionDialog = updated;
  }

  handleSchoolSubscriptionDeleteCancel = () => {
    const updated = { ...this.state.schoolSubscriptionDialog, deleteConfirmRow: null };
    this.setState({ schoolSubscriptionDialog: updated });
    this.state.schoolSubscriptionDialog = updated;
  }

  handleSchoolSubscriptionDeleteConfirm = () => {
    const dialog = this.state.schoolSubscriptionDialog;
    const row = dialog.deleteConfirmRow;
    if (!row) return;
    const updated = { ...dialog, deleteLoading: true };
    this.setState({ schoolSubscriptionDialog: updated });
    this.state.schoolSubscriptionDialog = updated;
    this.props.root.refs.API.request(
      "school_subscription_delete",
      {
        // school_id is part of the route path on the new surface
        // (DELETE /api/school/{school_id}/subscription/{school_subscription_id}).
        school_id: dialog.school ? dialog.school.id : undefined,
        school_subscription_id: row.school_subscription_id,
      },
      () => {
        this.props.root.addNotification(Localization.strings.SCHOOL_SUBSCRIPTION_DELETED || "訂閱已移除", true);
        const reset = { ...this.state.schoolSubscriptionDialog, deleteLoading: false, deleteConfirmRow: null };
        this.setState({ schoolSubscriptionDialog: reset });
        this.state.schoolSubscriptionDialog = reset;
        this.fetchSchoolSubscriptions(dialog.school.id);
      },
      (err) => {
        const msg = err?.body?.remarks || Localization.strings.SCHOOL_SUBSCRIPTION_DELETE_FAILED || "移除失敗";
        this.props.root.addNotification(msg, false);
        const reset = { ...this.state.schoolSubscriptionDialog, deleteLoading: false, deleteConfirmRow: null };
        this.setState({ schoolSubscriptionDialog: reset });
        this.state.schoolSubscriptionDialog = reset;
      }
    );
  }

  getSchoolTypeText(schoolType) {
    switch(parseInt(schoolType)) {
      case 0: return '-';
      case 1: return 'Pri';
      case 2: return 'Sec';
      case 3: return 'Uni/Gov';
      case 4: return 'Spec';
      case 5: return 'Mgmt';
      case 6: return 'Biz';
      default: return '-';
    }
  }

  getSchoolLevelText(schoolLevel) {
    switch(parseInt(schoolLevel)) {
      case 0: return '-';
      case 1: return 'Lv 1';
      case 2: return 'Lv 2';
      case 3: return 'Lv 3';
      case 4: return 'Lv 4';
      case 5: return 'Lv 5';
      default: return '-';
    }
  }

  formatDateTime(dateStr) {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) {
      return dateStr;
    }
    return date.toLocaleString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  }

  // Subscription tab handlers
  handleSubscriptionKeywordChange = (e) => {
    this.setState({ subscriptionSearchText: e.target.value });
    this.state.subscriptionSearchText = e.target.value;
  }

  handleSubscriptionSortByChange = (e) => {
    this.setState({ subscriptionSortBy: e.target.value });
    this.state.subscriptionSortBy = e.target.value;
    this.handleSubscriptionSearch();
  }

  handleSubscriptionSortDirChange = (e) => {
    this.setState({ subscriptionSortDir: e.target.value });
    this.state.subscriptionSortDir = e.target.value;
    this.handleSubscriptionSearch();
  }

  handleSubscriptionLimitChange = (e) => {
    this.setState({ subscriptionLimit: e.target.value });
    this.state.subscriptionLimit = e.target.value;
    this.handleSubscriptionSearch();
  }

  handleSubscriptionSearchEnter = (e) => {
    if (e.key === 'Enter') {
      this.handleSubscriptionSearch();
    }
  }

  isSubscriptionTemplate = (value) => {
    return value === true || String(value) === '1';
  }

  handleSubscriptionNameEditStart = (row, field) => {
    const updateKey = `${row.id}:${field}`;
    if (this.state.subscriptionUpdatingFields[updateKey]) return;

    const editingCell = {
      id: row.id,
      field,
      value: row[field] || '',
      originalValue: row[field] || '',
      row,
    };
    this.setState({ subscriptionEditingCell: editingCell });
    this.state.subscriptionEditingCell = editingCell;
  }

  handleSubscriptionNameEditChange = (event) => {
    if (!this.state.subscriptionEditingCell) return;

    const editingCell = {
      ...this.state.subscriptionEditingCell,
      value: event.target.value,
    };
    this.setState({ subscriptionEditingCell: editingCell });
    this.state.subscriptionEditingCell = editingCell;
  }

  handleSubscriptionNameEditKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.currentTarget.blur();
    }
  }

  handleSubscriptionNameEditCommit = () => {
    const editingCell = this.state.subscriptionEditingCell;
    if (!editingCell) return;

    const value = editingCell.value.trim();
    this.setState({ subscriptionEditingCell: null });
    this.state.subscriptionEditingCell = null;

    if (!value) {
      const message = editingCell.field === 'name'
        ? Localization.strings.SUBSCRIPTION_NAME_REQUIRED
        : Localization.strings.SUBSCRIPTION_NAME_EN_REQUIRED;
      this.props.root.addNotification(message, false);
      return;
    }

    if (value === String(editingCell.originalValue).trim()) return;
    this.updateSubscriptionField(editingCell.row, editingCell.field, value);
  }

  handleSubscriptionTemplateChange = (row, checked) => {
    this.updateSubscriptionField(row, 'is_template', checked ? 1 : 0);
  }

  updateSubscriptionField = (row, field, value) => {
    if (!row || !row.id || !this.props.root.refs?.API) return;

    const updateKey = `${row.id}:${field}`;
    if (this.state.subscriptionUpdatingFields[updateKey]) return;

    const previousValue = row[field];
    const subscriptionList = this.state.subscriptionList.map((item) => (
      item.id === row.id ? { ...item, [field]: value } : item
    ));
    const subscriptionUpdatingFields = {
      ...this.state.subscriptionUpdatingFields,
      [updateKey]: true,
    };
    this.setState({ subscriptionList, subscriptionUpdatingFields });
    this.state.subscriptionList = subscriptionList;
    this.state.subscriptionUpdatingFields = subscriptionUpdatingFields;

    this.props.root.refs.API.request(
      "subscription_update",
      { id: row.id, [field]: value },
      () => {
        const nextUpdatingFields = { ...this.state.subscriptionUpdatingFields };
        delete nextUpdatingFields[updateKey];
        this.setState({ subscriptionUpdatingFields: nextUpdatingFields });
        this.state.subscriptionUpdatingFields = nextUpdatingFields;
        this.props.root.addNotification(
          Localization.strings.SUBSCRIPTION_UPDATE_SUCCESS || "Subscription updated",
          true
        );
      },
      (err) => {
        const revertedList = this.state.subscriptionList.map((item) => (
          item.id === row.id ? { ...item, [field]: previousValue } : item
        ));
        const nextUpdatingFields = { ...this.state.subscriptionUpdatingFields };
        delete nextUpdatingFields[updateKey];
        this.setState({
          subscriptionList: revertedList,
          subscriptionUpdatingFields: nextUpdatingFields,
        });
        this.state.subscriptionList = revertedList;
        this.state.subscriptionUpdatingFields = nextUpdatingFields;
        const message = err?.body?.remarks
          || err?.body?.detail
          || err?.body?.error
          || Localization.strings.SUBSCRIPTION_UPDATE_FAILED
          || "Failed to update subscription";
        this.props.root.addNotification(message, false);
      }
    );
  }

  handleSubscriptionSearch = () => {
    if (!this.props.root.refs || !this.props.root.refs.API) {
      return;
    }

    const payload = {
      sort_by: this.state.subscriptionSortBy || 'date_create',
      sort_dir: this.state.subscriptionSortDir || DEFAULT_SORT_ORDER,
      limit: parseInt(this.state.subscriptionLimit) || 100,
    };

    if (this.state.subscriptionSearchText && this.state.subscriptionSearchText.trim() !== "") {
      payload.keyword = this.state.subscriptionSearchText.trim();
    }

    this.setState({
      subscriptionList: [],
      subscriptionLoading: true,
    });
    this.state.subscriptionList = [];
    this.state.subscriptionLoading = true;

    this.props.root.refs.API.request("subscription_get", payload, this.subscriptionSearchOK, this.subscriptionSearchFailed);
  }

  subscriptionSearchOK = (res) => {
    const list = Array.isArray(res?.body?.result) ? res.body.result : [];
    this.setState({
      subscriptionList: list,
      hasSubscriptionSearched: true,
      subscriptionLoading: false,
    });
    this.state.subscriptionList = list;
    this.state.hasSubscriptionSearched = true;
    this.state.subscriptionLoading = false;
    const foundTemplate = Localization.strings.SUBSCRIPTION_FOUND_COUNT || '找到 {0} 個訂閱計劃';
    this.props.root.addNotification(foundTemplate.replace('{0}', list.length), true);
  }

  subscriptionSearchFailed = (err) => {
    console.log("subscription search failed", err);
    this.setState({
      subscriptionList: [],
      hasSubscriptionSearched: true,
      subscriptionLoading: false,
    });
    this.state.subscriptionList = [];
    this.state.hasSubscriptionSearched = true;
    this.state.subscriptionLoading = false;
    this.props.root.addNotification(Localization.strings.SUBSCRIPTION_SEARCH_FAILED, false);
  }

  openSubscriptionLocalesDialog = (subscription) => {
    if (!subscription) return;
    if (this.subscriptionLocaleSearchTimer) {
      clearTimeout(this.subscriptionLocaleSearchTimer);
      this.subscriptionLocaleSearchTimer = null;
    }
    this.subscriptionLocaleSearchRequestId += 1;
    const localeDialog = createSubscriptionLocalesDialogState({
      open: true,
      subscription,
      loading: true,
    });
    this.setState({
      subscriptionNavigationDirection: 'forward',
      subscriptionLocalesDialog: localeDialog,
    });
    this.state.subscriptionLocalesDialog = localeDialog;
    this.state.subscriptionNavigationDirection = 'forward';

    this.props.root.refs.API.request(
      "subscription_epack_list",
      { subscription_id: subscription.id },
      this.subscriptionLocalesOK,
      this.subscriptionLocalesFailed
    );
  }

  subscriptionLocalesOK = (res) => {
    const rows = Array.isArray(res?.body?.result) ? res.body.result : [];
    const subscriptionId = this.state.subscriptionLocalesDialog.subscription?.id;
    const subscriptionList = this.state.subscriptionList.map((subscription) => (
      subscription.id === subscriptionId
        ? { ...subscription, assigned_extra_locale_count: rows.length }
        : subscription
    ));
    this.setState({
      subscriptionList,
      subscriptionLocalesDialog: {
        ...this.state.subscriptionLocalesDialog,
        rows,
        loading: false,
      }
    });
    this.state.subscriptionList = subscriptionList;
    this.state.subscriptionLocalesDialog.rows = rows;
    this.state.subscriptionLocalesDialog.loading = false;
  }

  subscriptionLocalesFailed = (err) => {
    console.log("subscription locales failed", err);
    this.setState({
      subscriptionLocalesDialog: {
        ...this.state.subscriptionLocalesDialog,
        rows: [],
        loading: false,
      }
    });
    this.state.subscriptionLocalesDialog.rows = [];
    this.state.subscriptionLocalesDialog.loading = false;
    this.props.root.addNotification(Localization.strings.SUBSCRIPTION_LOCALE_FAILED, false);
  }

  closeSubscriptionLocalesDialog = () => {
    if (this.subscriptionLocaleSearchTimer) {
      clearTimeout(this.subscriptionLocaleSearchTimer);
      this.subscriptionLocaleSearchTimer = null;
    }
    this.subscriptionLocaleSearchRequestId += 1;
    const localeDialog = createSubscriptionLocalesDialogState();
    this.setState({
      subscriptionNavigationDirection: 'back',
      subscriptionLocalesDialog: localeDialog,
    });
    this.state.subscriptionLocalesDialog = localeDialog;
    this.state.subscriptionNavigationDirection = 'back';
  }

  openSubscriptionPrivilegeDialog = (subscription) => {
    if (!subscription) return;
    this.setState({
      subscriptionNavigationDirection: 'forward',
      subscriptionPrivilegeDialog: {
        open: true,
        subscription,
        rows: [],
        loading: true,
      }
    });
    this.state.subscriptionPrivilegeDialog = {
      open: true,
      subscription,
      rows: [],
      loading: true,
    };
    this.state.subscriptionNavigationDirection = 'forward';

    this.props.root.refs.API.request(
      "subscription_privilege_list",
      { subscription_id: subscription.id },
      this.subscriptionPrivilegeOK,
      this.subscriptionPrivilegeFailed
    );
  }

  subscriptionPrivilegeOK = (res) => {
    const rows = Array.isArray(res?.body?.result) ? res.body.result : [];
    const subscriptionId = this.state.subscriptionPrivilegeDialog.subscription?.id;
    const extraLocalePrivilege = rows.find((row) => this.privilegeName(row) === PLAN_EXTRA_LOCALE);
    const maxExtraLocaleNumber = Number(extraLocalePrivilege?.value) || 0;
    const subscriptionList = this.state.subscriptionList.map((subscription) => (
      subscription.id === subscriptionId
        ? { ...subscription, max_extra_locale_number: maxExtraLocaleNumber }
        : subscription
    ));
    this.setState({
      subscriptionList,
      subscriptionPrivilegeDialog: {
        ...this.state.subscriptionPrivilegeDialog,
        rows,
        loading: false,
      }
    });
    this.state.subscriptionList = subscriptionList;
    this.state.subscriptionPrivilegeDialog.rows = rows;
    this.state.subscriptionPrivilegeDialog.loading = false;
  }

  subscriptionPrivilegeFailed = (err) => {
    console.log("subscription privilege failed", err);
    this.setState({
      subscriptionPrivilegeDialog: {
        ...this.state.subscriptionPrivilegeDialog,
        rows: [],
        loading: false,
      }
    });
    this.state.subscriptionPrivilegeDialog.rows = [];
    this.state.subscriptionPrivilegeDialog.loading = false;
    this.props.root.addNotification(Localization.strings.SUBSCRIPTION_PRIVILEGE_FAILED, false);
  }

  closeSubscriptionPrivilegeDialog = () => {
    this.setState({
      subscriptionNavigationDirection: 'back',
      subscriptionPrivilegeDialog: {
        open: false,
        subscription: null,
        rows: [],
        loading: false,
      }
    });
    this.state.subscriptionPrivilegeDialog = {
      open: false,
      subscription: null,
      rows: [],
      loading: false,
    };
    this.state.subscriptionNavigationDirection = 'back';
  }

  // Subscription create dialog handlers
  openSubscriptionCreateDialog = () => {
    const dialog = createSubscriptionCreateDialogState({
      open: true,
      templatesLoading: true,
    });
    this.setState({
      subscriptionNavigationDirection: 'forward',
      subscriptionCreateDialog: dialog,
      subscriptionCopyPrivilegeFrom: null,
    });
    this.state.subscriptionCreateDialog = dialog;
    this.state.subscriptionNavigationDirection = 'forward';
    this.state.subscriptionCopyPrivilegeFrom = null;
    this.loadSubscriptionTemplates();
  }

  openSchoolSubscriptionCreatePage = () => {
    const school = this.state.schoolSubscriptionDialog.school;
    if (!school) return;
    const zhName = (school.name || school.name_eng || '').trim();
    const enName = (school.name_eng || school.name || '').trim();
    const dialog = createSubscriptionCreateDialogState({
      open: true,
      name: `[訂閱]${zhName}`,
      name_en: `[Sub]${enName}`,
      templatesLoading: true,
      returnToSchoolSubscription: true,
    });
    this.setState({
      schoolNavigationDirection: 'forward',
      subscriptionCreateDialog: dialog,
      subscriptionCopyPrivilegeFrom: null,
    });
    this.state.subscriptionCreateDialog = dialog;
    this.state.schoolNavigationDirection = 'forward';
    this.state.subscriptionCopyPrivilegeFrom = null;
    this.loadSubscriptionTemplates();
  }

  closeSubscriptionCreateDialog = () => {
    const returnToSchool = !!this.state.subscriptionCreateDialog.returnToSchoolSubscription;
    const dialog = createSubscriptionCreateDialogState();
    if (returnToSchool) {
      this.setState({
        activeTab: 3,
        schoolNavigationDirection: 'back',
        subscriptionCreateDialog: dialog,
        subscriptionCopyPrivilegeFrom: null,
      });
      this.state.activeTab = 3;
      this.state.schoolNavigationDirection = 'back';
      saveAdminActiveTab(3);
    } else {
      this.setState({
        subscriptionNavigationDirection: 'back',
        subscriptionCreateDialog: dialog,
        subscriptionCopyPrivilegeFrom: null,
      });
      this.state.subscriptionNavigationDirection = 'back';
    }
    this.state.subscriptionCreateDialog = dialog;
    this.state.subscriptionCopyPrivilegeFrom = null;
  }

  loadSubscriptionTemplates = () => {
    if (!this.props.root?.refs?.API) {
      const dialog = {
        ...this.state.subscriptionCreateDialog,
        templatesLoading: false,
      };
      this.setState({ subscriptionCreateDialog: dialog });
      this.state.subscriptionCreateDialog = dialog;
      return;
    }

    this.props.root.refs.API.request(
      "subscription_get",
      {
        is_template: 1,
        limit: 500,
        sort_by: "name",
        sort_dir: "ASC",
      },
      (res) => {
        if (!this.state.subscriptionCreateDialog.open) return;
        const templates = Array.isArray(res?.body?.result) ? res.body.result : [];
        const dialog = {
          ...this.state.subscriptionCreateDialog,
          templates,
          templatesLoading: false,
        };
        this.setState({ subscriptionCreateDialog: dialog });
        this.state.subscriptionCreateDialog = dialog;
      },
      () => {
        if (!this.state.subscriptionCreateDialog.open) return;
        const dialog = {
          ...this.state.subscriptionCreateDialog,
          templates: [],
          templatesLoading: false,
        };
        this.setState({ subscriptionCreateDialog: dialog });
        this.state.subscriptionCreateDialog = dialog;
      }
    );
  }

  handleSubscriptionCopySourceSelect = (subscription) => {
    const sourcePrivilegesLoading = !!subscription?.id;
    const dialog = {
      ...this.state.subscriptionCreateDialog,
      sourcePrivileges: [],
      sourcePrivilegesLoading,
      copyExtraLocales: subscription ? this.state.subscriptionCreateDialog.copyExtraLocales : false,
    };
    this.setState({
      subscriptionCopyPrivilegeFrom: subscription || null,
      subscriptionCreateDialog: dialog,
    });
    this.state.subscriptionCopyPrivilegeFrom = subscription || null;
    this.state.subscriptionCreateDialog = dialog;

    if (!subscription?.id || !this.props.root?.refs?.API) {
      if (sourcePrivilegesLoading) {
        const idleDialog = {
          ...dialog,
          sourcePrivilegesLoading: false,
        };
        this.setState({ subscriptionCreateDialog: idleDialog });
        this.state.subscriptionCreateDialog = idleDialog;
      }
      return;
    }

    const sourceId = subscription.id;
    this.props.root.refs.API.request(
      "subscription_privilege_list",
      { subscription_id: sourceId },
      (res) => {
        if (this.state.subscriptionCopyPrivilegeFrom?.id !== sourceId) return;
        const sourcePrivileges = Array.isArray(res?.body?.result) ? res.body.result : [];
        const updatedDialog = {
          ...this.state.subscriptionCreateDialog,
          sourcePrivileges,
          sourcePrivilegesLoading: false,
        };
        this.setState({ subscriptionCreateDialog: updatedDialog });
        this.state.subscriptionCreateDialog = updatedDialog;
      },
      () => {
        if (this.state.subscriptionCopyPrivilegeFrom?.id !== sourceId) return;
        const updatedDialog = {
          ...this.state.subscriptionCreateDialog,
          sourcePrivileges: [],
          sourcePrivilegesLoading: false,
        };
        this.setState({ subscriptionCreateDialog: updatedDialog });
        this.state.subscriptionCreateDialog = updatedDialog;
      }
    );
  }

  handleSubscriptionCopyExtraLocalesChange = (event) => {
    const dialog = {
      ...this.state.subscriptionCreateDialog,
      copyExtraLocales: event.currentTarget.checked,
    };
    this.setState({ subscriptionCreateDialog: dialog });
    this.state.subscriptionCreateDialog = dialog;
  }

  openSubscriptionDeleteDialog = (subscription) => {
    if (!subscription || !subscription.id) return;
    const dialog = {
      open: true,
      subscription,
      loading: false,
      adminPassword: "",
      assignedSchools: [],
      blocked: false,
    };
    this.setState({ subscriptionDeleteDialog: dialog });
    this.state.subscriptionDeleteDialog = dialog;
  }

  closeSubscriptionDeleteDialog = () => {
    const dialog = {
      open: false,
      subscription: null,
      loading: false,
      adminPassword: "",
      assignedSchools: [],
      blocked: false,
    };
    this.setState({ subscriptionDeleteDialog: dialog });
    this.state.subscriptionDeleteDialog = dialog;
  }

  handleSubscriptionDeleteConfirm = () => {
    const dialog = this.state.subscriptionDeleteDialog;
    if (!dialog.subscription || !dialog.subscription.id) return;
    if (!dialog.adminPassword) {
      this.props.root.addNotification("請輸入管理員密碼", false);
      return;
    }

    let adminId = 0;
    try {
      adminId = this.props.root.getMyUserId();
    } catch (e) {
      console.log("Could not get admin ID:", e);
    }

    if (!adminId || adminId <= 0) {
      this.props.root.addNotification("無法獲取管理員身份，請重新登入", false);
      return;
    }

    const loadingDialog = {
      ...dialog,
      loading: true,
    };
    this.setState({ subscriptionDeleteDialog: loadingDialog });
    this.state.subscriptionDeleteDialog = loadingDialog;

    this.props.root.refs.API.request(
      "subscription_delete",
      {
        id: dialog.subscription.id,
        admin_id: adminId,
        admin_password: dialog.adminPassword,
      },
      this.subscriptionDeleteOK,
      this.subscriptionDeleteFailed
    );
  }

  subscriptionDeleteOK = (res) => {
    const result = res?.body?.result;
    if (result && result.success === 1) {
      this.props.root.addNotification(Localization.strings.SUBSCRIPTION_DELETE_SUCCESS || "Subscription deleted", true);
      this.closeSubscriptionDeleteDialog();
      this.handleSubscriptionSearch();
      return;
    }

    const assignedSchools = Array.isArray(result?.assigned_schools) ? result.assigned_schools : [];
    const isBlocked = assignedSchools.length > 0;
    const message = result?.error || res?.body?.remarks || Localization.strings.SUBSCRIPTION_DELETE_FAILED || "Failed to delete subscription";

    this.setState({
      subscriptionDeleteDialog: {
        ...this.state.subscriptionDeleteDialog,
        loading: false,
        blocked: isBlocked,
        assignedSchools,
      }
    });
    this.state.subscriptionDeleteDialog.loading = false;
    this.state.subscriptionDeleteDialog.blocked = isBlocked;
    this.state.subscriptionDeleteDialog.assignedSchools = assignedSchools;

    this.props.root.addNotification(
      isBlocked
        ? (Localization.strings.SUBSCRIPTION_DELETE_BLOCKED || "Please remove assigned schools first")
        : message,
      false
    );
  }

  subscriptionDeleteFailed = (err) => {
    const message = err?.body?.remarks || err?.remarks || Localization.strings.SUBSCRIPTION_DELETE_FAILED || "Failed to delete subscription";
    this.setState({
      subscriptionDeleteDialog: {
        ...this.state.subscriptionDeleteDialog,
        loading: false,
      }
    });
    this.state.subscriptionDeleteDialog.loading = false;
    this.props.root.addNotification(message, false);
  }

  handleSubscriptionCreateFieldChange = (field) => (e) => {
    const dialog = { ...this.state.subscriptionCreateDialog };
    dialog[field] = e.target.value;
    this.setState({ subscriptionCreateDialog: dialog });
    this.state.subscriptionCreateDialog = dialog;
  }

  handleSubscriptionCreateSubmit = () => {
    const dialog = this.state.subscriptionCreateDialog;
    const name = (dialog.name || "").trim();
    const name_en = (dialog.name_en || "").trim();
    const remarks = (dialog.remarks || "").trim();

    if (name === "") {
      this.props.root.addNotification(Localization.strings.SUBSCRIPTION_NAME_REQUIRED, false);
      return;
    }
    if (name_en === "") {
      this.props.root.addNotification(Localization.strings.SUBSCRIPTION_NAME_EN_REQUIRED, false);
      return;
    }

    const payload = {
      name,
      name_en,
      remarks,
      is_template: dialog.is_template ? 1 : 0,
    };

    this.setState({
      subscriptionCreateDialog: {
        ...dialog,
        loading: true,
      }
    });
    this.state.subscriptionCreateDialog.loading = true;

    this.props.root.refs.API.request(
      "subscription_create",
      payload,
      this.subscriptionCreateOK,
      this.subscriptionCreateFailed
    );
  }

  subscriptionCreateOK = (res) => {
    const result = res?.body?.result;
    if (!result || result <= 0) {
      const msg = res?.body?.remarks || Localization.strings.SUBSCRIPTION_CREATE_FAILED;
      this.props.root.addNotification(msg, false);
      this.setState({
        subscriptionCreateDialog: {
          ...this.state.subscriptionCreateDialog,
          loading: false,
        }
      });
      this.state.subscriptionCreateDialog.loading = false;
      return;
    }

    const newSubId = result;
    const createDialog = this.state.subscriptionCreateDialog;
    const createdFromSchool = !!createDialog.returnToSchoolSubscription;
    const createdSubscription = {
      id: newSubId,
      name: (createDialog.name || "").trim(),
      name_en: (createDialog.name_en || "").trim(),
    };
    const tasks = [];
    if (this.state.subscriptionCopyPrivilegeFrom && this.state.subscriptionCopyPrivilegeFrom.id) {
      tasks.push(this.copyPrivilegesFromSubscription(newSubId, this.state.subscriptionCopyPrivilegeFrom.id));
    } else {
      tasks.push(
        this.apiRequestPromise("privilege_get_all", {})
          .then((resAll) => {
            const rows = Array.isArray(resAll?.body?.result) ? resAll.body.result : [];
            if (rows.length === 0) {
              return null;
            }
            const payload = {
              subscription_id: newSubId,
              privilege_array: rows.map((p) => ({
                privilege_id: p.id,
                value: p.value_default,
                value_2: p.value_2_default,
              })),
            };
            return this.apiRequestPromise("subscription_privilege_add", payload);
          })
      );
    }
    if (
      this.state.subscriptionCreateDialog.copyExtraLocales
      && this.state.subscriptionCopyPrivilegeFrom
      && this.state.subscriptionCopyPrivilegeFrom.id
    ) {
      tasks.push(this.copyLocalesFromSubscription(newSubId, this.state.subscriptionCopyPrivilegeFrom.id));
    }

    let copyFailed = false;
    Promise.all(tasks)
      .catch((err) => {
        copyFailed = true;
        const msg = err?.remarks || err?.body?.remarks || Localization.strings.SUBSCRIPTION_CREATE_FAILED;
        this.props.root.addNotification(msg, false);
      })
      .finally(() => {
        if (!copyFailed) {
          this.props.root.addNotification(Localization.strings.SUBSCRIPTION_CREATE_SUCCESS, true);
        }
        const resetCreateDialog = createSubscriptionCreateDialogState();
        this.state.subscriptionCopyPrivilegeFrom = null;
        this.state.subscriptionCreateDialog = resetCreateDialog;

        if (createdFromSchool && this.state.schoolSubscriptionDialog.open) {
          const schoolDialog = {
            ...this.state.schoolSubscriptionDialog,
            addSelected: createdSubscription,
            pickerKey: Date.now(),
          };
          this.setState({
            activeTab: 3,
            schoolNavigationDirection: 'back',
            subscriptionCopyPrivilegeFrom: null,
            subscriptionCreateDialog: resetCreateDialog,
            schoolSubscriptionDialog: schoolDialog,
          });
          this.state.activeTab = 3;
          this.state.schoolNavigationDirection = 'back';
          this.state.schoolSubscriptionDialog = schoolDialog;
          saveAdminActiveTab(3);
          return;
        }

        this.setState({
          subscriptionSearchText: "",
          subscriptionSortBy: "date_create",
          subscriptionSortDir: DEFAULT_SORT_ORDER,
          subscriptionLimit: 100,
          hasSubscriptionSearched: false,
          subscriptionNavigationDirection: 'back',
          subscriptionCopyPrivilegeFrom: null,
          subscriptionCreateDialog: resetCreateDialog,
        });
        this.state.subscriptionSearchText = "";
        this.state.subscriptionSortBy = "date_create";
        this.state.subscriptionSortDir = DEFAULT_SORT_ORDER;
        this.state.subscriptionLimit = 100;
        this.state.hasSubscriptionSearched = false;
        this.state.subscriptionNavigationDirection = 'back';
        this.handleSubscriptionSearch();
      });
  }

  subscriptionCreateFailed = (err) => {
    const msg = err?.body?.remarks || err?.remarks || Localization.strings.SUBSCRIPTION_CREATE_FAILED;
    this.props.root.addNotification(msg, false);
    this.setState({
      subscriptionCreateDialog: {
        ...this.state.subscriptionCreateDialog,
        loading: false,
      }
    });
    this.state.subscriptionCreateDialog.loading = false;
  }

  apiRequestPromise(cmd, payload) {
    return new Promise((resolve, reject) => {
      if (!this.props.root.refs || !this.props.root.refs.API) {
        reject({ remarks: "API unavailable" });
        return;
      }
      this.props.root.refs.API.request(cmd, payload, (res) => resolve(res), (err) => reject(err));
    });
  }

  copyPrivilegesFromSubscription(newSubId, sourceSubId) {
    return this.apiRequestPromise("subscription_privilege_list", { subscription_id: sourceSubId })
      .then((res) => {
        const rows = Array.isArray(res?.body?.result) ? res.body.result : [];
        if (rows.length === 0) {
          return null;
        }
        return this.apiRequestPromise("subscription_privilege_add", {
          subscription_id: newSubId,
          privilege_array: rows,
        });
      });
  }

  copyLocalesFromSubscription(newSubId, sourceSubId) {
    return this.apiRequestPromise("subscription_epack_list", { subscription_id: sourceSubId })
      .then((res) => {
        const rows = Array.isArray(res?.body?.result) ? res.body.result : [];
        if (rows.length === 0) {
          return null;
        }
        const epackArr = rows.map(r => ({ epack_id: r.epack_id }));
        return this.apiRequestPromise("subscription_epack_add", {
          subscription_id: newSubId,
          epack_array: epackArr,
        });
      });
  }

  refreshSubscriptionLocaleList() {
    const sub = this.state.subscriptionLocalesDialog.subscription;
    if (!sub) return;
    this.setState({
      subscriptionLocalesDialog: {
        ...this.state.subscriptionLocalesDialog,
        loading: true,
      }
    });
    this.props.root.refs.API.request(
      "subscription_epack_list",
      { subscription_id: sub.id },
      this.subscriptionLocalesOK,
      this.subscriptionLocalesFailed
    );
  }

  handleAssignedLocaleSelectionChange = (subscriptionEpackId, checked) => {
    const rowId = String(subscriptionEpackId);
    this.setState((prevState) => {
      const dialog = prevState.subscriptionLocalesDialog;
      const selectedIds = (dialog.selectedAssignedLocaleIds || []).map(String);
      const selectedAssignedLocaleIds = checked
        ? (selectedIds.includes(rowId) ? selectedIds : [...selectedIds, rowId])
        : selectedIds.filter((id) => id !== rowId);
      return {
        subscriptionLocalesDialog: {
          ...dialog,
          selectedAssignedLocaleIds,
        },
      };
    });
  }

  handleRemoveSelectedLocales = () => {
    const dialog = this.state.subscriptionLocalesDialog;
    const ids = [...new Set((dialog.selectedAssignedLocaleIds || []).map(String).filter(Boolean))];
    if (ids.length === 0 || dialog.removing || dialog.assigning) {
      return;
    }
    const confirmTemplate = Localization.strings.SUBSCRIPTION_REMOVE_SELECTED_CONFIRM
      || 'Remove {0} selected LOCALE(s)?';
    const confirmMsg = confirmTemplate.replace('{0}', ids.length);
    const title = Localization.strings.SUBSCRIPTION_LOCALE_TITLE || "";
    const doDelete = async () => {
      this.setState((prevState) => ({
        subscriptionLocalesDialog: {
          ...prevState.subscriptionLocalesDialog,
          removing: true,
        },
      }));

      const results = await Promise.all(ids.map((id) => (
        this.apiRequestPromise("subscription_epack_delete", { id })
          .then(() => ({ ok: true }))
          .catch((error) => ({ ok: false, error }))
      )));
      const failedCount = results.filter((result) => !result.ok).length;
      this.setState((prevState) => ({
        subscriptionLocalesDialog: {
          ...prevState.subscriptionLocalesDialog,
          selectedAssignedLocaleIds: [],
          removing: false,
        },
      }), this.refreshSubscriptionLocaleList);

      if (failedCount > 0) {
        const failureTemplate = Localization.strings.SUBSCRIPTION_REMOVE_SELECTED_FAILED
          || 'Failed to remove {0} LOCALE(s)';
        this.props.root.addNotification(failureTemplate.replace('{0}', failedCount), false);
      }
    };
    const confirmDialog = this.props.root?.refs?.confirmDialog;
    if (confirmDialog) {
      confirmDialog.show(
        { title, content: confirmMsg },
        doDelete,
        () => {}
      );
    } else if (window.confirm(confirmMsg)) {
      doDelete();
    }
  }

  refreshSubscriptionPrivilegeList() {
    const sub = this.state.subscriptionPrivilegeDialog.subscription;
    if (!sub) return;
    this.setState({
      subscriptionPrivilegeDialog: {
        ...this.state.subscriptionPrivilegeDialog,
        loading: true,
      }
    });
    this.props.root.refs.API.request(
      "subscription_privilege_list",
      { subscription_id: sub.id },
      this.subscriptionPrivilegeOK,
      this.subscriptionPrivilegeFailed
    );
  }

  handleSubscriptionLocaleSearchModeChange = (searchMode) => {
    if (!['user', 'subscription', 'keyword'].includes(searchMode)) {
      return;
    }
    if (this.subscriptionLocaleSearchTimer) {
      clearTimeout(this.subscriptionLocaleSearchTimer);
      this.subscriptionLocaleSearchTimer = null;
    }
    this.subscriptionLocaleSearchRequestId += 1;
    this.setState((prevState) => ({
      subscriptionLocalesDialog: {
        ...prevState.subscriptionLocalesDialog,
        searchMode,
        searchText: '',
        searchSourceResults: [],
        selectedSearchSource: null,
        searchResults: [],
        selectedLocaleIds: [],
        searchLoading: false,
      },
    }));
  }

  handleSubscriptionLocaleSearchChange = (event) => {
    const searchText = event.currentTarget.value;
    if (this.subscriptionLocaleSearchTimer) {
      clearTimeout(this.subscriptionLocaleSearchTimer);
      this.subscriptionLocaleSearchTimer = null;
    }
    this.subscriptionLocaleSearchRequestId += 1;
    this.setState((prevState) => ({
      subscriptionLocalesDialog: {
        ...prevState.subscriptionLocalesDialog,
        searchText,
        searchSourceResults: [],
        selectedSearchSource: null,
        searchResults: [],
        selectedLocaleIds: [],
        searchLoading: false,
      },
    }));

    const keyword = searchText.trim();
    if (!keyword) {
      return;
    }

    this.subscriptionLocaleSearchTimer = setTimeout(() => {
      this.subscriptionLocaleSearchTimer = null;
      const searchMode = this.state.subscriptionLocalesDialog.searchMode || 'keyword';
      if (searchMode === 'keyword') {
        this.searchSubscriptionLocales(keyword, searchMode);
      } else {
        this.searchSubscriptionLocaleSources(keyword, searchMode);
      }
    }, 350);
  }

  searchSubscriptionLocaleSources(keyword, searchMode) {
    if (
      !this.props.root?.refs?.API
      || !keyword
      || !['user', 'subscription'].includes(searchMode)
    ) {
      return;
    }
    const requestId = ++this.subscriptionLocaleSearchRequestId;
    this.setState((prevState) => ({
      subscriptionLocalesDialog: {
        ...prevState.subscriptionLocalesDialog,
        searchLoading: true,
        searchSourceResults: [],
        selectedSearchSource: null,
        searchResults: [],
        selectedLocaleIds: [],
      },
    }));

    this.props.root.refs.API.request(
      "search_epack_sources",
      {
        keyword,
        search_mode: searchMode,
        exclude_subscription_id: this.state.subscriptionLocalesDialog.subscription?.id,
        limit: 50,
      },
      (res) => {
        if (requestId !== this.subscriptionLocaleSearchRequestId) {
          return;
        }
        const rows = Array.isArray(res?.body?.result) ? res.body.result : [];
        this.setState((prevState) => {
          const currentDialog = prevState.subscriptionLocalesDialog;
          if (
            !currentDialog.open
            || currentDialog.searchText.trim() !== keyword
            || (currentDialog.searchMode || 'keyword') !== searchMode
            || currentDialog.selectedSearchSource
          ) {
            return null;
          }
          return {
            subscriptionLocalesDialog: {
              ...currentDialog,
              searchSourceResults: rows,
              searchLoading: false,
            },
          };
        });
      },
      (err) => {
        if (requestId !== this.subscriptionLocaleSearchRequestId) {
          return;
        }
        this.setState((prevState) => ({
          subscriptionLocalesDialog: {
            ...prevState.subscriptionLocalesDialog,
            searchSourceResults: [],
            searchLoading: false,
          },
        }));
        const msg = err?.body?.remarks
          || Localization.strings.SUBSCRIPTION_LOCALE_SOURCE_SEARCH_FAILED
          || 'Failed to search users or subscriptions';
        this.props.root.addNotification(msg, false);
      }
    );
  }

  handleSubscriptionLocaleSearchSourceSelect = (row) => {
    const dialog = this.state.subscriptionLocalesDialog;
    const searchMode = dialog.searchMode || 'keyword';
    const sourceId = Number(row?.id);
    const keyword = (dialog.searchText || '').trim();
    if (
      !Number.isInteger(sourceId)
      || sourceId <= 0
      || !keyword
      || !['user', 'subscription'].includes(searchMode)
    ) {
      return;
    }
    const label = searchMode === 'user'
      ? (row.display_name || row.username || '-')
      : (row.name || row.name_en || '-');
    this.setState((prevState) => ({
      subscriptionLocalesDialog: {
        ...prevState.subscriptionLocalesDialog,
        selectedSearchSource: { ...row, id: sourceId, label },
        searchResults: [],
        selectedLocaleIds: [],
      },
    }), () => {
      this.searchSubscriptionLocales(keyword, searchMode, sourceId);
    });
  }

  handleClearSubscriptionLocaleSearchSource = () => {
    this.subscriptionLocaleSearchRequestId += 1;
    this.setState((prevState) => ({
      subscriptionLocalesDialog: {
        ...prevState.subscriptionLocalesDialog,
        selectedSearchSource: null,
        searchResults: [],
        selectedLocaleIds: [],
        searchLoading: false,
      },
    }));
  }

  searchSubscriptionLocales(keyword, searchMode = 'keyword', sourceId = null) {
    if (!this.props.root?.refs?.API || !keyword) {
      return;
    }
    const requestId = ++this.subscriptionLocaleSearchRequestId;
    this.setState((prevState) => ({
      subscriptionLocalesDialog: {
        ...prevState.subscriptionLocalesDialog,
        searchLoading: true,
        searchResults: [],
        selectedLocaleIds: [],
      },
    }));

    this.props.root.refs.API.request(
      "search_epack_by_keyword",
      {
        keyword,
        search_mode: searchMode,
        exclude_subscription_id: this.state.subscriptionLocalesDialog.subscription?.id,
        source_id: sourceId,
        limit: 100,
      },
      (res) => {
        if (requestId !== this.subscriptionLocaleSearchRequestId) {
          return;
        }
        const rows = Array.isArray(res?.body?.result) ? res.body.result : [];
        this.setState((prevState) => {
          const currentDialog = prevState.subscriptionLocalesDialog;
          if (
            !currentDialog.open
            || currentDialog.searchText.trim() !== keyword
            || (currentDialog.searchMode || 'keyword') !== searchMode
            || (
              sourceId !== null
              && String(currentDialog.selectedSearchSource?.id) !== String(sourceId)
            )
          ) {
            return null;
          }
          return {
            subscriptionLocalesDialog: {
              ...currentDialog,
              searchResults: rows,
              selectedLocaleIds: [],
              searchLoading: false,
            },
          };
        });
      },
      (err) => {
        if (requestId !== this.subscriptionLocaleSearchRequestId) {
          return;
        }
        this.setState((prevState) => ({
          subscriptionLocalesDialog: {
            ...prevState.subscriptionLocalesDialog,
            searchResults: [],
            selectedLocaleIds: [],
            searchLoading: false,
          },
        }));
        const msg = err?.body?.remarks
          || Localization.strings.SUBSCRIPTION_LOCALE_SEARCH_FAILED
          || 'Failed to search LOCALEs';
        this.props.root.addNotification(msg, false);
      }
    );
  }

  handleSubscriptionLocaleSelectionChange = (epackId, checked) => {
    const localeId = String(epackId);
    this.setState((prevState) => {
      const dialog = prevState.subscriptionLocalesDialog;
      const selectedLocaleIds = (dialog.selectedLocaleIds || []).map(String);
      const maxLocaleCount = Math.max(0, Number(dialog.subscription?.max_extra_locale_number) || 0);
      const remainingCapacity = Math.max(0, maxLocaleCount - (dialog.rows || []).length);
      let nextSelectedIds;

      if (checked) {
        if (selectedLocaleIds.includes(localeId) || selectedLocaleIds.length >= remainingCapacity) {
          return null;
        }
        nextSelectedIds = [...selectedLocaleIds, localeId];
      } else {
        nextSelectedIds = selectedLocaleIds.filter((id) => id !== localeId);
      }

      return {
        subscriptionLocalesDialog: {
          ...dialog,
          selectedLocaleIds: nextSelectedIds,
        },
      };
    });
  }

  handleAssignSelectedLocales = () => {
    const dialog = this.state.subscriptionLocalesDialog;
    const sub = dialog.subscription;
    if (!sub || dialog.assigning || dialog.removing) {
      return;
    }

    const assignedIds = new Set((dialog.rows || []).map((row) => String(row.epack_id)));
    const selectedIds = new Set((dialog.selectedLocaleIds || []).map(String));
    const maxLocaleCount = Math.max(0, Number(sub.max_extra_locale_number) || 0);
    const remainingCapacity = Math.max(0, maxLocaleCount - (dialog.rows || []).length);
    const epackIds = (dialog.searchResults || [])
      .filter((row) => selectedIds.has(String(row.id)) && !assignedIds.has(String(row.id)))
      .map((row) => Number(row.id))
      .filter((id) => Number.isInteger(id) && id > 0);

    if (epackIds.length === 0 || epackIds.length > remainingCapacity) {
      return;
    }

    this.setState((prevState) => ({
      subscriptionLocalesDialog: {
        ...prevState.subscriptionLocalesDialog,
        assigning: true,
      },
    }));
    this.props.root.refs.API.request(
      "subscription_epack_add",
      { subscription_id: sub.id, epack_ids: epackIds },
      () => {
        const addedIds = new Set(epackIds.map(String));
        this.setState((prevState) => ({
          subscriptionLocalesDialog: {
            ...prevState.subscriptionLocalesDialog,
            searchResults: (prevState.subscriptionLocalesDialog.searchResults || [])
              .filter((row) => !addedIds.has(String(row.id))),
            selectedLocaleIds: [],
            assigning: false,
          },
        }), this.refreshSubscriptionLocaleList);
      },
      (err) => {
        const msg = err?.body?.remarks || Localization.strings.SUBSCRIPTION_LOCALE_FAILED;
        this.setState((prevState) => ({
          subscriptionLocalesDialog: {
            ...prevState.subscriptionLocalesDialog,
            assigning: false,
          },
        }));
        this.props.root.addNotification(msg, false);
      }
    );
  }

  handlePrivilegeReset = (row) => {
    const id = row.subscription_privilege_id || row.id;
    if (!id) return;
    const payload = {
      id,
      value: row.value_default ?? 0,
      value_2: row.value_2_default ?? 0,
    };
    this.props.root.refs.API.request(
      "subscription_privilege_update",
      payload,
      () => {
        this.refreshSubscriptionPrivilegeList();
      },
      (err) => {
        const msg = err?.body?.remarks || Localization.strings.SUBSCRIPTION_PRIVILEGE_FAILED;
        this.props.root.addNotification(msg, false);
      }
    );
  }

  // Whether a privilege's primary value is boolean (value_type: 0=int, 1=boolean)
  privilegeIsBoolean = (row) => String(row && row.value_type) === '1';

  privilegeName = (row) => String(row?.name || row?.privilege_name || '').toUpperCase();

  // Normalize a stored value (int 0/1 or "true"/"false") to a boolean
  privilegeTruthy = (v) => {
    const s = String(v).toLowerCase();
    return s === '1' || s === 'true';
  };

  // Apply a privilege field value to the dialog rows in local state
  setPrivilegeRowValue = (id, field, value) => {
    const currentRows = this.state.subscriptionPrivilegeDialog.rows || [];
    const changedRow = currentRows.find((r) => (r.subscription_privilege_id || r.id) === id);
    const rows = currentRows.map((r) => {
      const rid = r.subscription_privilege_id || r.id;
      return rid === id ? { ...r, [field]: value } : r;
    });
    const subscriptionId = this.state.subscriptionPrivilegeDialog.subscription?.id;
    const shouldUpdateExtraLocaleMaximum = field === 'value'
      && this.privilegeName(changedRow) === PLAN_EXTRA_LOCALE;
    const subscriptionList = shouldUpdateExtraLocaleMaximum
      ? this.state.subscriptionList.map((subscription) => (
        subscription.id === subscriptionId
          ? { ...subscription, max_extra_locale_number: Number(value) || 0 }
          : subscription
      ))
      : this.state.subscriptionList;
    this.setState({
      subscriptionList,
      subscriptionPrivilegeDialog: {
        ...this.state.subscriptionPrivilegeDialog,
        rows,
      }
    });
    this.state.subscriptionList = subscriptionList;
    this.state.subscriptionPrivilegeDialog.rows = rows;
  }

  // Commit a single privilege field (value or value_2) to the backend.
  // Values are stored as integers; booleans are sent as 1/0. The UI updates
  // optimistically for instant feedback and reverts if the request fails.
  commitPrivilegeField = (row, field, rawValue) => {
    const id = row.subscription_privilege_id || row.id;
    if (!id) {
      return;
    }
    let intVal = parseInt(rawValue, 10);
    if (isNaN(intVal)) {
      intVal = 0;
    }
    if (intVal < 0) {
      intVal = 0;
    }
    const prevVal = row[field];
    const currentInt = parseInt(prevVal, 10);
    if (!isNaN(currentInt) && currentInt === intVal) {
      return; // nothing changed
    }
    this.setPrivilegeRowValue(id, field, intVal);
    const payload = { id };
    payload[field] = intVal;
    this.props.root.refs.API.request(
      "subscription_privilege_update",
      payload,
      () => {
        // Optimistic value already applied; nothing further to do
      },
      (err) => {
        const msg = err?.body?.remarks || Localization.strings.SUBSCRIPTION_PRIVILEGE_FAILED;
        this.props.root.addNotification(msg, false);
        this.setPrivilegeRowValue(id, field, prevVal); // revert
      }
    );
  }

  // STORAGE_LIMIT.value is stored in MB everywhere in the app; this dialog
  // presents and edits it in GB. Convert MB -> GB for display (trimmed).
  mbToGbDisplay = (mb) => {
    const n = Number(mb);
    if (isNaN(n)) {
      return '';
    }
    return parseFloat((n / 1024).toFixed(2)).toString();
  }

  // Commit a GB-entered storage limit back to the backend as MB. Skips the
  // commit when the field is unchanged so a no-edit blur can't corrupt the
  // stored MB value through a lossy GB round-trip.
  commitStoragePrivilegeGb = (row, displayedGbStr, rawGbStr) => {
    if (String(rawGbStr) === String(displayedGbStr)) {
      return; // unchanged
    }
    let gb = parseFloat(rawGbStr);
    if (isNaN(gb) || gb < 0) {
      gb = 0;
    }
    const mb = Math.round(gb * 1024);
    this.commitPrivilegeField(row, 'value', mb);
  }

  handlePasswordChangeOpen = (userId, username) => {
    this.setState({
      passwordChangeDialog: {
        open: true,
        targetUserId: userId,
        targetUsername: username,
        adminPassword: "",
        newPassword: "",
        confirmPassword: "",
        isLoading: false,
      }
    });
  }

  handlePasswordChangeClose = () => {
    this.setState({
      passwordChangeDialog: {
        open: false,
        targetUserId: 0,
        targetUsername: "",
        adminPassword: "",
        newPassword: "",
        confirmPassword: "",
        isLoading: false,
      }
    });
  }

  handlePasswordChangeFieldChange = (field) => (event) => {
    let dialog = { ...this.state.passwordChangeDialog };
    dialog[field] = event.target.value;
    this.setState({ passwordChangeDialog: dialog });
  }

  handlePasswordChangeSubmit = () => {
    const dialog = this.state.passwordChangeDialog;
    
    // Basic validation
    if (!dialog.adminPassword || !dialog.newPassword || !dialog.confirmPassword) {
      this.props.root.addNotification("請填寫所有欄位", false);
      return;
    }

    if (dialog.newPassword !== dialog.confirmPassword) {
      this.props.root.addNotification("新密碼與確認密碼不相符", false);
      return;
    }

    if (dialog.newPassword.length < 6) {
      this.props.root.addNotification("密碼長度至少需要6個字符", false);
      return;
    }

    // Get admin ID automatically
    let adminId = 0;
    try {
      adminId = this.props.root.getMyUserId();
    } catch (e) {
      console.log("Could not get admin ID:", e);
    }

    if (!adminId || adminId <= 0) {
      this.props.root.addNotification("無法獲取管理員身份，請重新登入", false);
      return;
    }

    // Set loading state
    let loadingDialog = { ...dialog };
    loadingDialog.isLoading = true;
    this.setState({ passwordChangeDialog: loadingDialog });

    const requestObj = {
      admin_id: adminId,
      admin_password: dialog.adminPassword,
      target_user_id: dialog.targetUserId,
      new_password: dialog.newPassword
    };

    console.log("Sending password change request with admin_id:", adminId);

    this.props.root.refs.API.request(
      "admin_init_user_update_password", 
      requestObj, 
      this.passwordChangeOK, 
      this.passwordChangeFailed
    );
  }

  passwordChangeOK = (res) => {
    console.log("Password change response received", res);
    
    // Check if the operation was actually successful by examining the result field
    if (res && res.body && res.body.result && res.body.result == 1) {
      // True success
      this.props.root.addNotification(
        `用戶 ${this.state.passwordChangeDialog.targetUsername} 的密碼已成功更改`, 
        true
      );
      this.handlePasswordChangeClose();
    } else {
      // API call succeeded but operation failed (result: 0)
      let errorMessage = "密碼更改失敗";
      if (res && res.body && res.body.remarks) {
        errorMessage += ": " + res.body.remarks;
      }
      this.props.root.addNotification(errorMessage, false);
      
      // Remove loading state but keep dialog open
      let dialog = { ...this.state.passwordChangeDialog };
      dialog.isLoading = false;
      this.setState({ passwordChangeDialog: dialog });
    }
  }

  passwordChangeFailed = (err) => {
    console.log("Password change failed", err);
    let errorMessage = "密碼更改失敗";
    if (err && err.body && err.body.remarks) {
      errorMessage += ": " + err.body.remarks;
    } else if (err && err.remarks) {
      errorMessage += ": " + err.remarks;
    }
    this.props.root.addNotification(errorMessage, false);
    
    // Remove loading state
    let dialog = { ...this.state.passwordChangeDialog };
    dialog.isLoading = false;
    this.setState({ passwordChangeDialog: dialog });
  }

  handleDeleteUserOpen = (userId, username) => {
    this.setState({
      deleteUserDialog: {
        open: true,
        targetUserId: userId,
        targetUsername: username,
        adminPassword: "",
        isLoading: false,
      }
    });
  }

  handleDeleteUserClose = () => {
    this.setState({
      deleteUserDialog: {
        open: false,
        targetUserId: 0,
        targetUsername: "",
        adminPassword: "",
        isLoading: false,
      }
    });
  }

  handleDeleteUserPasswordChange = (event) => {
    let dialog = { ...this.state.deleteUserDialog };
    dialog.adminPassword = event.target.value;
    this.setState({ deleteUserDialog: dialog });
  }

  handleDeleteUserSubmit = () => {
    const dialog = this.state.deleteUserDialog;

    if (!dialog.adminPassword) {
      this.props.root.addNotification("請輸入管理員密碼", false);
      return;
    }

    let adminId = 0;
    try {
      adminId = this.props.root.getMyUserId();
    } catch (e) {
      console.log("Could not get admin ID:", e);
    }

    if (!adminId || adminId <= 0) {
      this.props.root.addNotification("無法獲取管理員身份，請重新登入", false);
      return;
    }

    let loadingDialog = { ...dialog };
    loadingDialog.isLoading = true;
    this.setState({ deleteUserDialog: loadingDialog });

    const requestObj = {
      admin_id: adminId,
      admin_password: dialog.adminPassword,
      target_user_id: dialog.targetUserId,
    };

    this.props.root.refs.API.request(
      "admin_delete_user",
      requestObj,
      this.deleteUserOK,
      this.deleteUserFailed
    );
  }

  deleteUserOK = (res) => {
    if (res && res.body && res.body.result && res.body.result == 1) {
      this.props.root.addNotification(
        `用戶 ${this.state.deleteUserDialog.targetUsername} 已成功刪除`,
        true
      );
      this.handleDeleteUserClose();
      this.handleSearchClicked();
    } else {
      let errorMessage = "刪除用戶失敗";
      if (res && res.body && res.body.remarks) {
        errorMessage += ": " + res.body.remarks;
      }
      this.props.root.addNotification(errorMessage, false);

      let dialog = { ...this.state.deleteUserDialog };
      dialog.isLoading = false;
      this.setState({ deleteUserDialog: dialog });
    }
  }

  deleteUserFailed = (err) => {
    let errorMessage = "刪除用戶失敗";
    if (err && err.body && err.body.remarks) {
      errorMessage += ": " + err.body.remarks;
    } else if (err && err.remarks) {
      errorMessage += ": " + err.remarks;
    }
    this.props.root.addNotification(errorMessage, false);

    let dialog = { ...this.state.deleteUserDialog };
    dialog.isLoading = false;
    this.setState({ deleteUserDialog: dialog });
  }

  handleEditUserOpen = (user) => {
    this.setState({
      editUserDialog: {
        open: true,
        targetUserId: user.id,
        targetUsername: user.username,
        displayName: user.display_name || "",
        email: user.email || "",
        role: parseInt(user.role) || 0,
        adminPassword: "",
        isLoading: false,
      }
    });
  }

  handleEditUserClose = () => {
    this.setState({
      editUserDialog: {
        open: false,
        targetUserId: 0,
        targetUsername: "",
        displayName: "",
        email: "",
        role: 0,
        adminPassword: "",
        isLoading: false,
      }
    });
  }

  handleEditUserFieldChange = (field) => (event) => {
    let dialog = { ...this.state.editUserDialog };
    dialog[field] = event.target.value;
    this.setState({ editUserDialog: dialog });
  }

  handleEditUserSubmit = () => {
    const dialog = this.state.editUserDialog;

    if (!dialog.adminPassword) {
      this.props.root.addNotification("請輸入管理員密碼", false);
      return;
    }

    let adminId = 0;
    try {
      adminId = this.props.root.getMyUserId();
    } catch (e) {
      console.log("Could not get admin ID:", e);
    }

    if (!adminId || adminId <= 0) {
      this.props.root.addNotification("無法獲取管理員身份，請重新登入", false);
      return;
    }

    let loadingDialog = { ...dialog };
    loadingDialog.isLoading = true;
    this.setState({ editUserDialog: loadingDialog });

    const requestObj = {
      admin_id: adminId,
      admin_password: dialog.adminPassword,
      target_user_id: dialog.targetUserId,
      display_name: dialog.displayName,
      email: dialog.email,
      role: dialog.role,
    };

    this.props.root.refs.API.request(
      "admin_edit_user",
      requestObj,
      this.editUserOK,
      this.editUserFailed
    );
  }

  editUserOK = (res) => {
    if (res && res.body && res.body.result && res.body.result == 1) {
      this.props.root.addNotification(
        `用戶 ${this.state.editUserDialog.targetUsername} 的資料已成功更新`,
        true
      );
      this.handleEditUserClose();
      this.handleSearchClicked();
    } else {
      let errorMessage = "更新用戶資料失敗";
      if (res && res.body && res.body.remarks) {
        errorMessage += ": " + res.body.remarks;
      }
      this.props.root.addNotification(errorMessage, false);

      let dialog = { ...this.state.editUserDialog };
      dialog.isLoading = false;
      this.setState({ editUserDialog: dialog });
    }
  }

  editUserFailed = (err) => {
    let errorMessage = "更新用戶資料失敗";
    if (err && err.body && err.body.remarks) {
      errorMessage += ": " + err.body.remarks;
    } else if (err && err.remarks) {
      errorMessage += ": " + err.remarks;
    }
    this.props.root.addNotification(errorMessage, false);

    let dialog = { ...this.state.editUserDialog };
    dialog.isLoading = false;
    this.setState({ editUserDialog: dialog });
  }

  handleEditSchoolOpen = (school) => {
    this.setState({
      schoolNavigationDirection: 'forward',
      editSchoolDialog: {
        open: true,
        targetSchoolId: school.id,
        name: school.name || "",
        nameEng: school.name_eng || "",
        key: school.key || "",
        locale: school.locale || "",
        schoolType: parseInt(school.school_type) || 0,
        schoolLevel: parseInt(school.school_level) || 0,
        adminPassword: "",
        isLoading: false,
      }
    });
    this.state.schoolNavigationDirection = 'forward';
  }

  handleEditSchoolClose = () => {
    this.setState({
      schoolNavigationDirection: 'back',
      editSchoolDialog: {
        open: false,
        targetSchoolId: 0,
        name: "",
        nameEng: "",
        key: "",
        locale: "",
        schoolType: 0,
        schoolLevel: 0,
        adminPassword: "",
        isLoading: false,
      }
    });
    this.state.schoolNavigationDirection = 'back';
  }

  handleEditSchoolFieldChange = (field) => (event) => {
    let dialog = { ...this.state.editSchoolDialog };
    dialog[field] = event.target.value;
    this.setState({ editSchoolDialog: dialog });
  }

  handleEditSchoolSubmit = () => {
    const dialog = this.state.editSchoolDialog;

    if (!dialog.adminPassword) {
      this.props.root.addNotification("請輸入管理員密碼", false);
      return;
    }

    if (!dialog.name || dialog.name.trim() === "") {
      this.props.root.addNotification("學校名稱不可為空", false);
      return;
    }

    if (!dialog.nameEng || dialog.nameEng.trim() === "") {
      this.props.root.addNotification("英文名稱不可為空", false);
      return;
    }

    if (!dialog.key || dialog.key.trim() === "") {
      this.props.root.addNotification("代碼不可為空", false);
      return;
    }

    const keyPattern = /^[a-zA-Z0-9_]+$/;
    if (!keyPattern.test(dialog.key.trim())) {
      this.props.root.addNotification("代碼只能包含英文字母、數字及底線", false);
      return;
    }

    let adminId = 0;
    try {
      adminId = this.props.root.getMyUserId();
    } catch (e) {
      console.log("Could not get admin ID:", e);
    }

    if (!adminId || adminId <= 0) {
      this.props.root.addNotification("無法獲取管理員身份，請重新登入", false);
      return;
    }

    let loadingDialog = { ...dialog };
    loadingDialog.isLoading = true;
    this.setState({ editSchoolDialog: loadingDialog });

    const requestObj = {
      admin_id: adminId,
      admin_password: dialog.adminPassword,
      target_school_id: dialog.targetSchoolId,
      name: dialog.name.trim(),
      name_eng: dialog.nameEng.trim(),
      key: dialog.key.trim(),
      locale: dialog.locale.trim(),
      school_type: dialog.schoolType,
      school_level: dialog.schoolLevel,
    };

    this.props.root.refs.API.request(
      "admin_edit_school",
      requestObj,
      this.editSchoolOK,
      this.editSchoolFailed
    );
  }

  editSchoolOK = (res) => {
    if (res && res.body && res.body.result && res.body.result == 1) {
      this.props.root.addNotification(
        `學校 (ID: ${this.state.editSchoolDialog.targetSchoolId}) 的資料已成功更新`,
        true
      );
      this.handleEditSchoolClose();
      this.handleSchoolSearchClicked();
    } else {
      let errorMessage = "更新學校資料失敗";
      if (res && res.body && res.body.remarks) {
        errorMessage += ": " + res.body.remarks;
      }
      this.props.root.addNotification(errorMessage, false);

      let dialog = { ...this.state.editSchoolDialog };
      dialog.isLoading = false;
      this.setState({ editSchoolDialog: dialog });
    }
  }

  editSchoolFailed = (err) => {
    let errorMessage = "更新學校資料失敗";
    if (err && err.body && err.body.remarks) {
      errorMessage += ": " + err.body.remarks;
    } else if (err && err.remarks) {
      errorMessage += ": " + err.remarks;
    }
    this.props.root.addNotification(errorMessage, false);

    let dialog = { ...this.state.editSchoolDialog };
    dialog.isLoading = false;
    this.setState({ editSchoolDialog: dialog });
  }

  renderBatchRowsTable(rows) {
    return (
      <ScrollArea.Autosize mah={280} type="auto">
        <Table striped highlightOnHover verticalSpacing="xs" stickyHeader>
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={70}>CSV 行</Table.Th>
              <Table.Th>用戶名稱</Table.Th>
              <Table.Th w={90}>學校編號</Table.Th>
              <Table.Th>狀態</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((row, i) => (
              <Table.Tr key={i}>
                <Table.Td>{row.line}</Table.Td>
                <Table.Td>{row.username || '-'}</Table.Td>
                <Table.Td>{row.school_id != null ? row.school_id : '-'}</Table.Td>
                <Table.Td>
                  {row.status === 'error' ? (
                    <Text c="red" fz="sm">{this.getBatchReasonText(row.reason)}</Text>
                  ) : (
                    <Text c="teal" fz="sm">{row.status === 'inserted' ? '已建立' : '可建立'}</Text>
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </ScrollArea.Autosize>
    );
  }

  renderBatchCreateUserDialog() {
    const dialog = this.state.batchCreateDialog;
    return (
      <Modal
        opened={dialog.open}
        onClose={this.handleBatchCreateClose}
        closeOnClickOutside={!dialog.isLoading}
        closeOnEscape={!dialog.isLoading}
        withCloseButton={!dialog.isLoading}
        title={<Text fw={600}>批量建立用戶 (CSV)</Text>}
        size="lg"
        centered
        zIndex={2000}
      >
        {dialog.step === 'upload' && (
          <Stack gap="md">
            <Box style={{ padding: 12, backgroundColor: '#f5f5f5', borderRadius: 6, fontSize: 14 }}>
              <div>上傳 CSV 檔案以一次過建立多名用戶。第一行必須是欄位標題：</div>
              <div style={{ marginTop: 6 }}>
                <strong>必要欄位:</strong> username (用戶名稱)、password (密碼, 至少6個字符)、school_id (學校編號或 School Key)
              </div>
              <div>
                <strong>可選欄位:</strong> display_name (顯示名稱)、email (電郵)、role (權限: 0 一般用戶 / 1 進階用戶 / 2 管理員, 留空為 0)
              </div>
              <div style={{ marginTop: 6, color: '#888' }}>
                每次最多 500 行，多餘的欄位會被忽略。上傳後會先驗證資料並顯示預覽，確認後才會寫入資料庫。密碼會在伺服器端加密儲存。
              </div>
            </Box>
            <Group>
              <Button variant="light" leftSection={<IconDownload size={16} />} onClick={this.handleBatchTemplateDownload}>
                下載 CSV 範本
              </Button>
              <FileButton key={dialog.fileInputKey} onChange={this.handleBatchFileSelected} accept=".csv,text/csv">
                {(props) => (
                  <Button {...props} leftSection={<IconUpload size={16} />} loading={dialog.isLoading}>
                    上傳 CSV 檔案
                  </Button>
                )}
              </FileButton>
            </Group>
          </Stack>
        )}

        {dialog.step === 'confirm' && (
          <Stack gap="md">
            <Box style={{ padding: 12, backgroundColor: '#f5f5f5', borderRadius: 6 }}>
              <div><strong>檔案:</strong> {dialog.fileName} (共 {dialog.reviewRows.length} 行資料)</div>
              <div style={{ marginTop: 4 }}>
                <Text component="span" c="teal" fw={600}>{dialog.validCount} 名用戶可建立</Text>
                {dialog.invalidCount > 0 && (
                  <Text component="span" c="red" fw={600} ml="md">{dialog.invalidCount} 行有問題 (將被略過)</Text>
                )}
              </div>
            </Box>
            {this.renderBatchRowsTable(dialog.reviewRows)}
            <TextInput
              type="password"
              label="管理員密碼"
              required
              description="輸入您的管理員密碼以確認建立以上用戶"
              value={dialog.adminPassword}
              onChange={this.handleBatchAdminPasswordChange}
              disabled={dialog.isLoading}
            />
            <Group justify="flex-end" mt="sm">
              <Button variant="default" onClick={this.handleBatchCreateBackToUpload} disabled={dialog.isLoading}>
                重新選擇檔案
              </Button>
              <Button
                onClick={this.handleBatchConfirmSubmit}
                loading={dialog.isLoading}
                disabled={dialog.validCount <= 0}
                leftSection={<IconUsersPlus size={16} />}
              >
                確認建立 {dialog.validCount} 名用戶
              </Button>
            </Group>
          </Stack>
        )}

        {dialog.step === 'result' && (
          <Stack gap="md">
            <Box style={{ padding: 12, backgroundColor: dialog.insertedCount > 0 ? '#e6f7ee' : '#fdecea', borderRadius: 6 }}>
              <Text fw={600}>已成功建立 {dialog.insertedCount} 名用戶</Text>
              {dialog.invalidCount > 0 && (
                <Text c="red" fz="sm" mt={4}>{dialog.invalidCount} 行未能建立，原因見下表</Text>
              )}
            </Box>
            {this.renderBatchRowsTable(dialog.resultRows)}
            <Group justify="flex-end" mt="sm">
              <Button onClick={this.handleBatchCreateClose}>完成</Button>
            </Group>
          </Stack>
        )}
      </Modal>
    );
  }

  renderActiveTab() {
    switch (this.state.activeTab) {
      case 0: return this.createUser();
      case 1: return this.userList();
      case 2: return this.createSchool();
      case 3: return this.schoolList();
      case 4: return this.subscriptionTab();
      default: return null;
    }
  }

  render() {
    const tasks = this.getTasks();
    const tabIcons = [
      <IconUserPlus size={18} />,
      <IconUsers size={18} />,
      <IconSchool size={18} />,
      <IconBuildings size={18} />,
      <IconLicense size={18} />,
    ];
    return (
      <div>
        <Modal
          opened={this.state.open}
          onClose={this.handleClose}
          withCloseButton={false}
          padding={0}
          size="85%"
          radius="md"
          centered
          zIndex={1500}
          styles={{
            content: { height: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
            body: { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', padding: 0 },
          }}
        >
          {/* Brand header */}
          <Group justify="space-between" px="lg" style={{ height: 56, flexShrink: 0, backgroundColor: BRAND_PRIMARY }}>
            <Text c="white" fw={600} fz="lg">{Localization.strings.ADMIN_TASK}</Text>
            <CloseButton onClick={this.handleClose} variant="transparent" c="white" size="lg" />
          </Group>

          {/* Body: left sidebar nav + right content */}
          <Flex style={{ flex: 1, minHeight: 0 }}>
            <Box
              style={{
                width: 220,
                flexShrink: 0,
                borderRight: '1px solid #e9ecef',
                backgroundColor: '#f8f9fa',
                overflowY: 'auto',
                paddingTop: 8,
                paddingBottom: 8,
              }}
            >
              {tasks.map((label, index) => (
                <NavLink
                  key={index}
                  active={this.state.activeTab === index}
                  label={label}
                  leftSection={tabIcons[index]}
                  onClick={() => this.changeTab(index)}
                />
              ))}
            </Box>
            <ScrollArea style={{ flex: 1, minHeight: 0 }}>
              <Box p="xl" style={{ minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
                {this.renderActiveTab()}
              </Box>
            </ScrollArea>
          </Flex>
        </Modal>

        {/* Batch Create User (CSV) Dialog */}
        {this.renderBatchCreateUserDialog()}

        {/* Password Change Dialog */}
        <Modal
          opened={this.state.passwordChangeDialog.open}
          onClose={this.handlePasswordChangeClose}
          closeOnClickOutside={!this.state.passwordChangeDialog.isLoading}
          closeOnEscape={!this.state.passwordChangeDialog.isLoading}
          withCloseButton={!this.state.passwordChangeDialog.isLoading}
          title={<Text fw={600}>更改用戶密碼</Text>}
          size="sm"
          centered
          zIndex={2000}
        >
          <Stack gap="md">
            <Box style={{ padding: 12, backgroundColor: '#f5f5f5', borderRadius: 6 }}>
              <strong>目標用戶:</strong> {this.state.passwordChangeDialog.targetUsername} (ID: {this.state.passwordChangeDialog.targetUserId})
            </Box>
            <TextInput
              type="password"
              label="管理員密碼"
              required
              description="輸入您的管理員密碼以確認身份"
              value={this.state.passwordChangeDialog.adminPassword}
              onChange={this.handlePasswordChangeFieldChange('adminPassword')}
              disabled={this.state.passwordChangeDialog.isLoading}
            />
            <TextInput
              type="password"
              label="目標用戶新密碼"
              required
              description="密碼長度至少6個字符"
              value={this.state.passwordChangeDialog.newPassword}
              onChange={this.handlePasswordChangeFieldChange('newPassword')}
              disabled={this.state.passwordChangeDialog.isLoading}
            />
            <TextInput
              type="password"
              label="目標用戶新密碼(再輸入一次)"
              required
              description="請再次輸入新密碼"
              value={this.state.passwordChangeDialog.confirmPassword}
              onChange={this.handlePasswordChangeFieldChange('confirmPassword')}
              disabled={this.state.passwordChangeDialog.isLoading}
              error={(this.state.passwordChangeDialog.newPassword !== this.state.passwordChangeDialog.confirmPassword && this.state.passwordChangeDialog.confirmPassword !== '') ? "密碼不相符" : undefined}
            />
            <Group justify="flex-end" mt="sm">
              <Button variant="default" onClick={this.handlePasswordChangeClose} disabled={this.state.passwordChangeDialog.isLoading}>
                取消
              </Button>
              <Button color="orange" onClick={this.handlePasswordChangeSubmit} loading={this.state.passwordChangeDialog.isLoading}>
                更改密碼
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Delete User Dialog */}
        <Modal
          opened={this.state.deleteUserDialog.open}
          onClose={this.handleDeleteUserClose}
          closeOnClickOutside={!this.state.deleteUserDialog.isLoading}
          closeOnEscape={!this.state.deleteUserDialog.isLoading}
          withCloseButton={!this.state.deleteUserDialog.isLoading}
          title={<Text fw={600} c="red">刪除用戶</Text>}
          size="sm"
          centered
          zIndex={2000}
        >
          <Stack gap="md">
            <Box style={{ padding: 12, backgroundColor: '#fdecea', borderRadius: 6, border: '1px solid #f5c6cb' }}>
              <strong>警告：</strong> 此操作會將用戶帳號名稱更改為 "Deleted-{this.state.deleteUserDialog.targetUsername}"，該用戶將無法再登入。
            </Box>
            <Box style={{ padding: 12, backgroundColor: '#f5f5f5', borderRadius: 6 }}>
              <strong>目標用戶：</strong> {this.state.deleteUserDialog.targetUsername} (ID: {this.state.deleteUserDialog.targetUserId})
            </Box>
            <TextInput
              type="password"
              label="管理員密碼"
              required
              description="輸入您的管理員密碼以確認身份"
              value={this.state.deleteUserDialog.adminPassword}
              onChange={this.handleDeleteUserPasswordChange}
              disabled={this.state.deleteUserDialog.isLoading}
            />
            <Group justify="flex-end" mt="sm">
              <Button variant="default" onClick={this.handleDeleteUserClose} disabled={this.state.deleteUserDialog.isLoading}>
                取消
              </Button>
              <Button color="red" onClick={this.handleDeleteUserSubmit} loading={this.state.deleteUserDialog.isLoading}>
                確認刪除
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Edit User Dialog */}
        <Modal
          opened={this.state.editUserDialog.open}
          onClose={this.handleEditUserClose}
          closeOnClickOutside={!this.state.editUserDialog.isLoading}
          closeOnEscape={!this.state.editUserDialog.isLoading}
          withCloseButton={!this.state.editUserDialog.isLoading}
          title={<Text fw={600}>編輯用戶資料</Text>}
          size="sm"
          centered
          zIndex={2000}
        >
          <Stack gap="md">
            <Box style={{ padding: 12, backgroundColor: '#f5f5f5', borderRadius: 6 }}>
              <strong>目標用戶：</strong> {this.state.editUserDialog.targetUsername} (ID: {this.state.editUserDialog.targetUserId})
            </Box>
            <TextInput
              label="顯示名稱"
              value={this.state.editUserDialog.displayName}
              onChange={this.handleEditUserFieldChange('displayName')}
              disabled={this.state.editUserDialog.isLoading}
            />
            <TextInput
              label="電郵"
              value={this.state.editUserDialog.email}
              onChange={this.handleEditUserFieldChange('email')}
              disabled={this.state.editUserDialog.isLoading}
            />
            <Select
              label="權限"
              allowDeselect={false}
              disabled={this.state.editUserDialog.isLoading}
              value={String(this.state.editUserDialog.role)}
              onChange={(v) => this.handleEditUserFieldChange('role')({ target: { value: Number(v) } })}
              data={[
                { value: '0', label: '一般用戶' },
                { value: '1', label: '進階用戶' },
                { value: '2', label: '管理員' },
              ]}
            />
            <Divider />
            <TextInput
              type="password"
              label="管理員密碼"
              required
              description="輸入您的管理員密碼以確認身份"
              value={this.state.editUserDialog.adminPassword}
              onChange={this.handleEditUserFieldChange('adminPassword')}
              disabled={this.state.editUserDialog.isLoading}
            />
            <Group justify="flex-end" mt="sm">
              <Button variant="default" onClick={this.handleEditUserClose} disabled={this.state.editUserDialog.isLoading}>
                取消
              </Button>
              <Button onClick={this.handleEditUserSubmit} loading={this.state.editUserDialog.isLoading}>
                儲存變更
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Subscription delete dialog */}
        <Modal
          opened={this.state.subscriptionDeleteDialog.open}
          onClose={this.closeSubscriptionDeleteDialog}
          closeOnClickOutside={!this.state.subscriptionDeleteDialog.loading}
          closeOnEscape={!this.state.subscriptionDeleteDialog.loading}
          withCloseButton={!this.state.subscriptionDeleteDialog.loading}
          title={<Text fw={600} c="red">{Localization.strings.SUBSCRIPTION_DELETE || Localization.strings.DELETE || 'Delete Subscription'}</Text>}
          size="md"
          centered
          zIndex={2000}
        >
          <Stack gap="md">
            <Box style={{ padding: 12, backgroundColor: '#f5f5f5', borderRadius: 6 }}>
              <strong>{Localization.strings.SUBSCRIPTION || 'Subscription'}:</strong>{' '}
              {this.state.subscriptionDeleteDialog.subscription
                ? `${this.state.subscriptionDeleteDialog.subscription.name || '-'} (${this.state.subscriptionDeleteDialog.subscription.name_en || '-'})`
                : '-'}
            </Box>
            <TextInput
              type="password"
              label="管理員密碼"
              required
              description="輸入您的管理員密碼以確認身份"
              value={this.state.subscriptionDeleteDialog.adminPassword}
              onChange={(e) => {
                const dialog = {
                  ...this.state.subscriptionDeleteDialog,
                  adminPassword: e.target.value,
                };
                this.setState({ subscriptionDeleteDialog: dialog });
                this.state.subscriptionDeleteDialog = dialog;
              }}
              disabled={this.state.subscriptionDeleteDialog.loading}
            />
            {this.state.subscriptionDeleteDialog.blocked ? (
              <Box>
                <Text c="red" fw={700} mb="xs">
                  {Localization.strings.SUBSCRIPTION_DELETE_BLOCKED || 'Delete the assigned school subscriptions first.'}
                </Text>
                <Text mb={4}>{Localization.strings.SUBSCRIPTION_ASSIGNED_SCHOOLS || 'Assigned schools'}</Text>
                <Table striped>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>ID</Table.Th>
                      <Table.Th>{Localization.strings.NAME || 'Name'}</Table.Th>
                      <Table.Th>{Localization.strings.SUBSCRIPTION_NAME_EN || 'English Name'}</Table.Th>
                      <Table.Th>{Localization.strings.SCHOOL_SUBSCRIPTION_START || 'Start Date'}</Table.Th>
                      <Table.Th>{Localization.strings.SCHOOL_SUBSCRIPTION_EXPIRE || 'Expire Date'}</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {this.state.subscriptionDeleteDialog.assignedSchools.map((row, idx) => (
                      <Table.Tr key={idx}>
                        <Table.Td>{row.school_id}</Table.Td>
                        <Table.Td>{row.name || '-'}</Table.Td>
                        <Table.Td>{row.name_eng || '-'}</Table.Td>
                        <Table.Td>{row.date_start || '-'}</Table.Td>
                        <Table.Td>{row.date_expire || '-'}</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Box>
            ) : (
              <Text c="dimmed">
                {Localization.strings.SUBSCRIPTION_DELETE_CONFIRM || 'Are you sure you want to delete this subscription?'}
              </Text>
            )}
            <Group justify="flex-end" mt="sm">
              <Button variant="default" onClick={this.closeSubscriptionDeleteDialog} disabled={this.state.subscriptionDeleteDialog.loading}>
                {this.state.subscriptionDeleteDialog.blocked
                  ? (Localization.strings.CLOSE || 'Close')
                  : (Localization.strings.CANCEL || 'Cancel')}
              </Button>
              {!this.state.subscriptionDeleteDialog.blocked && (
                <Button color="red" onClick={this.handleSubscriptionDeleteConfirm} loading={this.state.subscriptionDeleteDialog.loading}>
                  {Localization.strings.SUBSCRIPTION_DELETE || Localization.strings.DELETE || 'Delete'}
                </Button>
              )}
            </Group>
          </Stack>
        </Modal>

        {/* Legacy school subscription modal kept closed; content now renders in the school detail page. */}
        <Modal
          opened={false}
          onClose={this.closeSchoolSubscriptionDialog}
          title={<Text fw={600}>{Localization.strings.SCHOOL_SUBSCRIPTION_DIALOG_TITLE || '學校訂閱'}{this.state.schoolSubscriptionDialog.school ? ` - ${this.state.schoolSubscriptionDialog.school.name || this.state.schoolSubscriptionDialog.school.name_eng || ''}` : ''}</Text>}
          size="76rem"
          centered
          zIndex={2000}
          styles={{
            content: {
              height: '76vh',
              minHeight: '36rem',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
            },
            body: {
              flex: 1,
              minHeight: 0,
              overflow: 'hidden',
            },
          }}
        >
          <Stack gap="md" style={{ height: '100%', minHeight: 0 }}>
            <Box w="45%" miw={260} maw={440}>
              <SubscriptionPicker
                key={this.state.schoolSubscriptionDialog.pickerKey}
                root={this.props.root}
                placeholder={Localization.strings.SCHOOL_SUBSCRIPTION_SEARCH_PLACEHOLDER || '搜尋訂閱'}
                disabled={this.state.schoolSubscriptionDialog.addLoading || this.state.schoolSubscriptionDialog.loading}
                onSelect={(sub) => {
                  const dialog = { ...this.state.schoolSubscriptionDialog, addSelected: sub };
                  this.setState({ schoolSubscriptionDialog: dialog });
                  this.state.schoolSubscriptionDialog = dialog;
                }}
              />
            </Box>
            <Group justify="flex-start" align="flex-end">
              <TextInput
                type="date"
                label={Localization.strings.SCHOOL_SUBSCRIPTION_START || '開始日'}
                value={this.state.schoolSubscriptionDialog.addDateStart}
                disabled={this.state.schoolSubscriptionDialog.addLoading || this.state.schoolSubscriptionDialog.loading}
                onChange={(e) => {
                  const dialog = { ...this.state.schoolSubscriptionDialog, addDateStart: e.target.value };
                  this.setState({ schoolSubscriptionDialog: dialog });
                  this.state.schoolSubscriptionDialog = dialog;
                }}
                w={180}
              />
              <Select
                label={Localization.strings.SCHOOL_SUBSCRIPTION_DURATION || '期限'}
                value={this.state.schoolSubscriptionDialog.addDurationMonths}
                data={[
                  { value: '6', label: Localization.strings.SCHOOL_SUBSCRIPTION_HALF_YEAR || '半年' },
                  { value: '12', label: Localization.strings.SCHOOL_SUBSCRIPTION_ONE_YEAR || '1 年' },
                  { value: '24', label: Localization.strings.SCHOOL_SUBSCRIPTION_TWO_YEARS || '2 年' },
                  { value: '36', label: Localization.strings.SCHOOL_SUBSCRIPTION_THREE_YEARS || '3 年' },
                ]}
                allowDeselect={false}
                disabled={this.state.schoolSubscriptionDialog.addLoading || this.state.schoolSubscriptionDialog.loading}
                onChange={(value) => {
                  const dialog = {
                    ...this.state.schoolSubscriptionDialog,
                    addDurationMonths: value || DEFAULT_SUBSCRIPTION_DURATION_MONTHS,
                  };
                  this.setState({ schoolSubscriptionDialog: dialog });
                  this.state.schoolSubscriptionDialog = dialog;
                }}
                w={180}
              />
              <Button
                onClick={this.handleSchoolSubscriptionAdd}
                loading={this.state.schoolSubscriptionDialog.addLoading}
                disabled={this.state.schoolSubscriptionDialog.loading}
                leftSection={<IconPlus size={16} />}
              >
                {Localization.strings.SCHOOL_SUBSCRIPTION_ADD || '為學校加入訂閱'}
              </Button>
            </Group>
            <Box style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
              {this.state.schoolSubscriptionDialog.loading ? (
                <Group justify="center" p="md"><Loader size="sm" /><Text c="dimmed">{Localization.strings.LOADING_DATA}</Text></Group>
              ) : (
                <Table.ScrollContainer minWidth={900}>
                  <Table striped verticalSpacing="sm" style={{ tableLayout: 'fixed', minWidth: 900 }}>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th style={{ width: 56 }}>ID</Table.Th>
                        <Table.Th>{Localization.strings.SUBSCRIPTION_NAME_ZH}</Table.Th>
                        <Table.Th>{Localization.strings.SUBSCRIPTION_NAME_EN}</Table.Th>
                        <Table.Th style={{ width: 142 }}>{Localization.strings.SCHOOL_SUBSCRIPTION_START || '開始日'}</Table.Th>
                        <Table.Th style={{ width: 142 }}>{Localization.strings.SCHOOL_SUBSCRIPTION_EXPIRE || '到期日'}</Table.Th>
                        <Table.Th style={{ width: 92 }}>{Localization.strings.SCHOOL_SUBSCRIPTION_STATUS || '狀態'}</Table.Th>
                        <Table.Th>{Localization.strings.REMARK}</Table.Th>
                        <Table.Th style={{ width: 76 }}></Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {this.state.schoolSubscriptionDialog.rows.map((row, idx) => {
                        const dialog = this.state.schoolSubscriptionDialog;
                        const isEditing = dialog.editingRow?.school_subscription_id === row.school_subscription_id;
                        const editingRow = dialog.editingRow;
                        const isUpdateLoading = dialog.updateLoading;
                        const isDeleteLoading = dialog.deleteLoading;
                        const isBusy = isUpdateLoading || isDeleteLoading || dialog.loading;
                        const canStartEditing = !editingRow && !isBusy;
                        const displayedDates = isEditing ? editingRow : row;
                        const isActive = isSubscriptionActive(displayedDates.date_start, displayedDates.date_expire);
                        const editableCellStyle = {
                          width: '100%',
                          minHeight: 30,
                          padding: 0,
                          border: 0,
                          background: 'transparent',
                          color: 'inherit',
                          font: 'inherit',
                          textAlign: 'left',
                          cursor: canStartEditing ? 'text' : 'default',
                        };
                        return (
                          <Table.Tr
                            key={row.school_subscription_id || idx}
                            onBlur={isEditing ? this.handleSchoolSubscriptionEditBlur : undefined}
                          >
                            <Table.Td>{row.subscription_id}</Table.Td>
                            <Table.Td>{row.subscription_name || '-'}</Table.Td>
                            <Table.Td>{row.subscription_name_en || '-'}</Table.Td>
                            <Table.Td>
                              {isEditing ? (
                                <TextInput
                                  type="date"
                                  size="xs"
                                  value={editingRow.date_start}
                                  autoFocus={dialog.editingField === 'date_start'}
                                  disabled={isUpdateLoading}
                                  onChange={(e) => {
                                    const updated = { ...dialog, editingRow: { ...editingRow, date_start: e.target.value } };
                                    this.setState({ schoolSubscriptionDialog: updated });
                                    this.state.schoolSubscriptionDialog = updated;
                                  }}
                                />
                              ) : (
                                <Box
                                  component="button"
                                  type="button"
                                  disabled={!canStartEditing}
                                  style={editableCellStyle}
                                  onClick={() => this.handleSchoolSubscriptionEditStart(row, 'date_start')}
                                >
                                  {row.date_start || '-'}
                                </Box>
                              )}
                            </Table.Td>
                            <Table.Td>
                              {isEditing ? (
                                <TextInput
                                  type="date"
                                  size="xs"
                                  value={editingRow.date_expire}
                                  autoFocus={dialog.editingField === 'date_expire'}
                                  disabled={isUpdateLoading}
                                  onChange={(e) => {
                                    const updated = { ...dialog, editingRow: { ...editingRow, date_expire: e.target.value } };
                                    this.setState({ schoolSubscriptionDialog: updated });
                                    this.state.schoolSubscriptionDialog = updated;
                                  }}
                                />
                              ) : (
                                <Box
                                  component="button"
                                  type="button"
                                  disabled={!canStartEditing}
                                  style={editableCellStyle}
                                  onClick={() => this.handleSchoolSubscriptionEditStart(row, 'date_expire')}
                                >
                                  {row.date_expire || '-'}
                                </Box>
                              )}
                            </Table.Td>
                            <Table.Td>
                              <Badge color={isActive ? 'green' : 'red'} variant="light" radius="sm">
                                {isActive
                                  ? (Localization.strings.SCHOOL_SUBSCRIPTION_ACTIVE || 'Active')
                                  : (Localization.strings.SCHOOL_SUBSCRIPTION_EXPIRED || 'Expired')}
                              </Badge>
                            </Table.Td>
                            <Table.Td>
                              {isEditing ? (
                                <TextInput
                                  size="xs"
                                  value={editingRow.remark}
                                  autoFocus={dialog.editingField === 'remark'}
                                  disabled={isUpdateLoading}
                                  placeholder={Localization.strings.REMARK || '備註'}
                                  onChange={(e) => {
                                    const updated = { ...dialog, editingRow: { ...editingRow, remark: e.target.value } };
                                    this.setState({ schoolSubscriptionDialog: updated });
                                    this.state.schoolSubscriptionDialog = updated;
                                  }}
                                />
                              ) : (
                                <Box
                                  component="button"
                                  type="button"
                                  disabled={!canStartEditing}
                                  style={editableCellStyle}
                                  onClick={() => this.handleSchoolSubscriptionEditStart(row, 'remark')}
                                >
                                  {row.school_subscription_remark || '-'}
                                </Box>
                              )}
                            </Table.Td>
                            <Table.Td style={{ whiteSpace: 'nowrap' }}>
                              {isEditing ? (
                                <Group gap={4} wrap="nowrap">
                                  <Tooltip label={Localization.strings.SAVE || '儲存'}>
                                    <ActionIcon color="green" variant="subtle" onClick={this.handleSchoolSubscriptionUpdate} loading={isUpdateLoading}>
                                      <IconCheck size={18} />
                                    </ActionIcon>
                                  </Tooltip>
                                  <Tooltip label={Localization.strings.CANCEL || '取消'}>
                                    <ActionIcon color="red" variant="subtle" onClick={this.handleSchoolSubscriptionEditCancel} disabled={isUpdateLoading}>
                                      <IconX size={18} />
                                    </ActionIcon>
                                  </Tooltip>
                                </Group>
                              ) : (
                                <Tooltip label={Localization.strings.SCHOOL_SUBSCRIPTION_DELETE || '移除訂閱'}>
                                  <ActionIcon variant="subtle" color="red" onClick={() => this.handleSchoolSubscriptionDelete(row)} disabled={isBusy} loading={isDeleteLoading}>
                                    <IconTrash size={18} />
                                  </ActionIcon>
                                </Tooltip>
                              )}
                            </Table.Td>
                          </Table.Tr>
                        );
                      })}
                      {this.state.schoolSubscriptionDialog.rows.length === 0 && (
                        <Table.Tr>
                          <Table.Td colSpan={8} style={{ textAlign: 'center', padding: 12 }}>
                            {Localization.strings.SCHOOL_SUBSCRIPTION_NONE || '暫無訂閱'}
                          </Table.Td>
                        </Table.Tr>
                      )}
                    </Table.Tbody>
                  </Table>
                </Table.ScrollContainer>
              )}
            </Box>
          </Stack>
        </Modal>

        {/* Delete subscription confirmation dialog */}
        <Modal
          opened={!!this.state.schoolSubscriptionDialog.deleteConfirmRow}
          onClose={this.handleSchoolSubscriptionDeleteCancel}
          title={<Text fw={600} c="red">{Localization.strings.SCHOOL_SUBSCRIPTION_DELETE || '移除訂閱'}</Text>}
          size="sm"
          centered
          zIndex={2100}
        >
          <Stack gap="md">
            <Text>{Localization.strings.SCHOOL_SUBSCRIPTION_DELETE_CONFIRM || '確定要從學校移除此訂閱嗎？'}</Text>
            {this.state.schoolSubscriptionDialog.deleteConfirmRow && (
              <Box style={{ padding: 12, backgroundColor: '#f5f5f5', borderRadius: 6, fontSize: 14 }}>
                <div><strong>{Localization.strings.SUBSCRIPTION_NAME_ZH || '訂閱名稱'}:</strong> {this.state.schoolSubscriptionDialog.deleteConfirmRow.subscription_name || '-'}</div>
                <div><strong>{Localization.strings.SUBSCRIPTION_NAME_EN || 'Name (EN)'}:</strong> {this.state.schoolSubscriptionDialog.deleteConfirmRow.subscription_name_en || '-'}</div>
                <div><strong>{Localization.strings.SCHOOL_SUBSCRIPTION_START || '開始日'}:</strong> {this.state.schoolSubscriptionDialog.deleteConfirmRow.date_start || '-'}</div>
                <div><strong>{Localization.strings.SCHOOL_SUBSCRIPTION_EXPIRE || '到期日'}:</strong> {this.state.schoolSubscriptionDialog.deleteConfirmRow.date_expire || '-'}</div>
              </Box>
            )}
            <Group justify="flex-end" mt="sm">
              <Button variant="default" onClick={this.handleSchoolSubscriptionDeleteCancel} disabled={this.state.schoolSubscriptionDialog.deleteLoading}>
                {Localization.strings.CANCEL || '取消'}
              </Button>
              <Button color="red" onClick={this.handleSchoolSubscriptionDeleteConfirm} loading={this.state.schoolSubscriptionDialog.deleteLoading}>
                {Localization.strings.SCHOOL_SUBSCRIPTION_DELETE || '移除訂閱'}
              </Button>
            </Group>
          </Stack>
        </Modal>
      </div>
    )
  }
}

export default AdminTaskDialog;
